import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Home, CalendarDays, Handshake, Utensils, Truck,
  Wrench, DollarSign, CreditCard, Receipt, ShieldAlert, Scale, BadgeCheck,
  Activity, LogOut, ChevronRight, Search, RefreshCw, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, CheckCircle, XCircle, Ban, AlertTriangle,
  Eye, ChevronLeft, ChevronDown, Loader2, Zap, Building2, Menu, X,
  Bell, Settings, Database, Server, Globe, BarChart3, Star, MapPin,
  Terminal, Lock, UserCheck, Package, ChevronUp, Circle, Dot,
  PanelLeftClose, PanelLeftOpen, Hash, BookOpen, Layers, PieChart,
  Sliders, ExternalLink, ShoppingCart, Car, Briefcase, Clock, Send,
  ToggleLeft, ToggleRight, AlertOctagon, History, Download, StickyNote,
  FileWarning, Gavel, FlaskConical, CheckSquare, Snowflake, ScanLine,
  Telescope, Gift, Tag, UserPlus,
  Flame, AlertCircle, MessageSquare,
  BarChart2, ClipboardList, Shield, Percent, Link2, Award,
  PlusCircle, BellRing, Mail
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { adminAPI, adminReferralAPI, adminComplianceAPI, waitlistAPI, faqFeedbackAPI, contactAPI } from '../services/api';
import AmbassadorBadge from '../components/AmbassadorBadge';
import AdminEmailTools from '../components/AdminEmailTools';
import AdminAcquisitionSources from '../components/AdminAcquisitionSources';

/* ─────────────────────────────────────────────────────────────
   DESIGN TOKENS  —  admin console colour system
   distinct from the consumer app aesthetic on purpose
───────────────────────────────────────────────────────────── */
// sidebar: near-black navy
// shell:   dark topbar, cool-grey canvas
// accent:  brand navy for CTAs only
// danger:  brand red for destructive actions

const fmt  = (n) => n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const fmtN = (n) => n == null ? '—' : Number(n).toLocaleString('en-GB');
const fmtPct = (n) => n == null ? '—' : `${Number(n).toFixed(1)}%`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—';

/* ── status badge ── */
const Badge = ({ status, size = 'sm' }) => {
  const MAP = {
    confirmed:       'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
    active:          'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
    published:       'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
    approved:        'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
    resolved:        'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
    completed:       'bg-blue-500/15 text-blue-400 ring-blue-500/25',
    accepted:        'bg-blue-500/15 text-blue-400 ring-blue-500/25',
    pending:         'bg-amber-500/15 text-amber-400 ring-amber-500/25',
    pending_payment: 'bg-amber-500/15 text-amber-400 ring-amber-500/25',
    trialing:        'bg-amber-500/15 text-amber-400 ring-amber-500/25',
    open:            'bg-amber-500/15 text-amber-400 ring-amber-500/25',
    cancelled:             'bg-red-500/15 text-red-400 ring-red-500/25',
    suspended:             'bg-red-500/15 text-red-400 ring-red-500/25',
    rejected:              'bg-red-500/15 text-red-400 ring-red-500/25',
    escalated:             'bg-red-500/15 text-red-400 ring-red-500/25',
    off_platform_legal:    'bg-red-500/15 text-red-400 ring-red-500/25',
    draft:                 'bg-zinc-500/15 text-zinc-400 ring-zinc-500/25',
    closed:                'bg-zinc-500/15 text-zinc-400 ring-zinc-500/25',
    awaiting_host_consent: 'bg-amber-500/15 text-amber-400 ring-amber-500/25',
    held_in_escrow:        'bg-purple-500/15 text-purple-400 ring-purple-500/25',
  };
  const cls = MAP[status] || 'bg-zinc-500/15 text-zinc-400 ring-zinc-500/25';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase ring-1 ${cls}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

/* ── KPI card ── */
const Kpi = ({ label, value, sub, trend, icon: Icon, danger, onClick, drillLabel }) => (
  <div
    onClick={onClick}
    className={`bg-[#111827] border border-white/8 rounded-lg p-4 flex flex-col gap-3 group
      ${onClick ? 'cursor-pointer hover:border-blue-500/30 hover:bg-[#111827]/90 transition-all' : ''}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</span>
      <div className="flex items-center gap-1.5">
        {onClick && (
          <span className="text-[9px] text-blue-500/0 group-hover:text-blue-400 transition-colors font-semibold flex items-center gap-0.5">
            <ExternalLink size={9} /> {drillLabel || 'Drill down →'}
          </span>
        )}
        {Icon && (
          <div className={`w-7 h-7 rounded flex items-center justify-center ${danger ? 'bg-red-500/15' : 'bg-white/6'}`}>
            <Icon size={13} className={danger ? 'text-red-400' : 'text-zinc-400'} />
          </div>
        )}
      </div>
    </div>
    <div className={`text-2xl font-bold tabular-nums ${danger ? 'text-red-400' : 'text-white'}`}>
      {value ?? <Loader2 size={16} className="animate-spin text-zinc-600" />}
    </div>
    <div className="flex items-center justify-between">
      {sub && <span className="text-[10px] text-zinc-600">{sub}</span>}
      {trend != null && (
        <span className={`flex items-center gap-0.5 text-[10px] font-semibold ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
          {Math.abs(trend).toFixed(1)}%
        </span>
      )}
    </div>
  </div>
);

/* ── section header ── */
const SectionHead = ({ title, sub, action }) => (
  <div className="flex items-start justify-between mb-5">
    <div>
      <h2 className="text-base font-bold text-white">{title}</h2>
      {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

/* ── empty state ── */
const Empty = ({ icon: Icon, msg }) => (
  <div className="flex flex-col items-center justify-center py-14 text-zinc-600 gap-3">
    <Icon size={28} className="opacity-30" />
    <p className="text-sm">{msg}</p>
  </div>
);

/* ── spinner ── */
const Spin = () => (
  <div className="flex items-center justify-center py-14">
    <Loader2 size={24} className="animate-spin text-zinc-600" />
  </div>
);

/* ── table wrapper ── */
const Table = ({ heads, children, empty }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-white/6">
          {heads.map(h => (
            <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-zinc-600 whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/4">
        {children}
      </tbody>
    </table>
    {empty}
  </div>
);

const Tr = ({ children, onClick }) => (
  <tr
    onClick={onClick}
    className={`${onClick ? 'cursor-pointer' : ''} hover:bg-white/3 transition-colors`}
  >
    {children}
  </tr>
);

const Td = ({ children, mono, muted, right, danger }) => (
  <td className={`px-4 py-3 ${mono ? 'font-mono text-[10px]' : 'text-xs'} ${muted ? 'text-zinc-500' : danger ? 'text-red-400' : 'text-zinc-200'} ${right ? 'text-right' : ''}`}>
    {children}
  </td>
);

/* ── btn ── */
const Btn = ({ children, onClick, disabled, size = 'sm', variant = 'primary', className = '' }) => {
  const base = 'inline-flex items-center gap-1.5 font-semibold rounded transition-colors disabled:opacity-40 disabled:pointer-events-none';
  const sz = size === 'xs' ? 'text-[10px] px-2.5 py-1' : size === 'sm' ? 'text-xs px-3.5 py-1.5' : 'text-sm px-5 py-2.5';
  const v = {
    primary: 'bg-[#1F3A68] hover:bg-[#1F3A68]/80 text-white',
    danger:  'bg-red-600/90 hover:bg-red-600 text-white',
    ghost:   'bg-white/6 hover:bg-white/12 text-zinc-300',
    success: 'bg-emerald-600/90 hover:bg-emerald-600 text-white',
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${sz} ${v} ${className}`}>
      {children}
    </button>
  );
};

/* ── kill-switch toggle ── */
const KillSwitch = ({ label, sub, enabled, onChange, danger }) => (
  <div className={`flex items-center justify-between p-3 rounded-lg border transition-all
    ${enabled
      ? 'bg-emerald-500/5 border-emerald-500/20'
      : danger
        ? 'bg-red-500/10 border-red-500/30'
        : 'bg-amber-500/8 border-amber-500/20'
    }`}>
    <div className="min-w-0 flex-1 pr-3">
      <div className="text-xs font-semibold text-zinc-200">{label}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
    <button
      onClick={() => onChange(!enabled)}
      className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded transition-colors
        ${enabled
          ? 'text-emerald-400 hover:text-emerald-300'
          : 'text-red-400 hover:text-red-300'
        }`}
    >
      {enabled
        ? <><ToggleRight size={18} /> ON</>
        : <><ToggleLeft size={18} /> OFF</>
      }
    </button>
  </div>
);

/* ── timeline event row ── */
const TIMELINE_ICONS = {
  booking:    { icon: CalendarDays, color: 'text-blue-400',    bg: 'bg-blue-500/15'    },
  food_order: { icon: Utensils,     color: 'text-amber-400',   bg: 'bg-amber-500/15'   },
  ride:       { icon: Car,          color: 'text-purple-400',  bg: 'bg-purple-500/15'  },
  task:       { icon: Briefcase,    color: 'text-cyan-400',    bg: 'bg-cyan-500/15'    },
  ledger:     { icon: DollarSign,   color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  offer:      { icon: Handshake,    color: 'text-zinc-400',    bg: 'bg-zinc-500/15'    },
};

const TimelineEvent = ({ ev }) => {
  const meta = TIMELINE_ICONS[ev.type] || TIMELINE_ICONS['ledger'];
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-2.5 py-2.5 border-b border-white/4 last:border-0">
      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.bg}`}>
        <Icon size={11} className={meta.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-semibold text-zinc-200 truncate">{ev.title}</span>
          {ev.status && <Badge status={ev.status} />}
          {ev.role && (
            <span className="text-[9px] bg-[#1F3A68]/40 text-blue-300 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide">
              {ev.role}
            </span>
          )}
        </div>
        {ev.detail && <div className="text-[10px] text-zinc-600 mt-0.5 truncate">{ev.detail}</div>}
      </div>
      <div className="text-right flex-shrink-0">
        {ev.amount != null && (
          <div className="text-[11px] font-bold tabular-nums text-blue-400">{fmt(ev.amount)}</div>
        )}
        <div className="text-[9px] text-zinc-700 font-mono mt-0.5">{fmtDate(ev.timestamp)}</div>
      </div>
    </div>
  );
};

/* ── portal url helper (View As) ── */
const portalUrl = (roles = []) => {
  if (roles.includes('host'))              return '/host/dashboard';
  if (roles.includes('landlord'))          return '/landlord/dashboard';
  if (roles.includes('restaurant_partner')) return '/restaurant/dashboard';
  if (roles.includes('driver'))            return '/driver/dashboard';
  if (roles.includes('delivery_rider'))    return '/delivery/dashboard';
  if (roles.includes('service_provider'))  return '/provider/dashboard';
  return '/guest/bookings';
};

/* ─────────────────────────────────────────────────────────────
   SIDEBAR NAV CONFIG
───────────────────────────────────────────────────────────── */
const NAV = [
  {
    group: 'Platform',
    items: [
      { id: 'overview',         label: 'Overview',         icon: LayoutDashboard },
      { id: 'alerts',           label: 'Alerts',           icon: BellRing },
      { id: 'users',            label: 'Users',            icon: Users },
      { id: 'activity',         label: 'Activity Log',     icon: Activity },
      { id: 'system-controls',  label: 'System Controls',  icon: Sliders },
    ],
  },
  {
    group: 'Operations',
    items: [
      { id: 'properties',  label: 'Properties',         icon: Home },
      { id: 'bookings',    label: 'Bookings',            icon: CalendarDays },
      { id: 'marketplace', label: 'Marketplace',         icon: Handshake },
      { id: 'restaurants', label: 'Restaurants',         icon: Utensils },
      { id: 'drivers',        label: 'Taxi Drivers',        icon: Car },
      { id: 'delivery-riders', label: 'Delivery Riders',    icon: Truck },
      { id: 'providers',   label: 'Service Providers',   icon: Wrench },
    ],
  },
  {
    group: 'Finance',
    items: [
      { id: 'financial',      label: 'Financial',      icon: BarChart3 },
      { id: 'ledger',         label: 'Ledger',         icon: BookOpen },
      { id: 'payouts',        label: 'Payouts',        icon: CreditCard },
      { id: 'subscriptions',  label: 'Subscriptions',  icon: Receipt },
    ],
  },
  {
    group: 'Compliance',
    items: [
      { id: 'risk',                label: 'Risk & Safety',        icon: ShieldAlert },
      { id: 'fraud',               label: 'Fraud Signals',        icon: AlertOctagon },
      { id: 'referral-fraud',      label: 'Referral Fraud',       icon: Shield },
      { id: 'disputes',            label: 'Disputes',             icon: Scale },
      { id: 'verification',        label: 'Verification',         icon: BadgeCheck },
      { id: 'suspension-command',  label: 'Suspension Centre',    icon: Ban },
      { id: 'compliance-appeals',  label: 'Appeals Pipeline',     icon: Gavel },
      { id: 'compliance-expiring', label: 'Expiring Docs',        icon: FileWarning },
      { id: 'ocr-verification',    label: 'OCR Verification',     icon: ScanLine },
    ],
  },
  {
    group: 'Growth',
    items: [
      { id: 'soft-launch',  label: 'Soft Launch',       icon: Telescope },
      { id: 'growth',       label: 'Growth Metrics',    icon: BarChart2 },
      { id: 'retention',    label: 'Retention',         icon: UserPlus },
      { id: 'user-feedback', label: 'User Feedback',    icon: MessageSquare },
      { id: 'contact-messages', label: 'Contact Messages', icon: MessageSquare },
      { id: 'email-tools',  label: 'Email Tools',       icon: Mail },
      { id: 'acquisition',  label: 'Tracking Links',    icon: Link2 },
    ],
  },
  {
    group: 'Incentives',
    items: [
      { id: 'incentives',          label: 'Fee Waivers',        icon: Percent },
      { id: 'referrals',           label: 'Referral Codes',     icon: Tag },
      { id: 'referral-command',    label: 'Referral Campaign',  icon: Award },
      { id: 'audit-log',           label: 'Audit Log',          icon: ClipboardList },
    ],
  },
];

const ALL_ITEMS = NAV.flatMap(g => g.items);

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const AdminDashboard = () => {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { user, getAuthToken, logout, loading: authLoading } = useAuth();

  const [section, setSection]       = useState('overview');
  const [collapsed, setCollapsed]   = useState(false);
  const [loading, setLoading]       = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // ── data slices ──
  const [platformStats,    setPlatformStats]    = useState(null);
  const [waitlistStats,    setWaitlistStats]    = useState(null);  // pre-launch demand signal
  const [financialOverview,setFinancialOverview]= useState(null);
  const [monthlyTrend,     setMonthlyTrend]     = useState([]);
  const [recentActivity,   setRecentActivity]   = useState([]);
  const [adminAlerts,      setAdminAlerts]      = useState({ alerts: [], unresolvedCount: 0 });
  const [period,           setPeriod]           = useState('30days');

  const [usersData,   setUsersData]   = useState({ users: [], total: 0, pages: 1 });
  const [userSearch,  setUserSearch]  = useState('');
  const [userRole,    setUserRole]    = useState('');
  const [usersPage,   setUsersPage]   = useState(1);

  const [propertiesData, setPropertiesData] = useState({ properties: [], total: 0, pages: 1 });
  const [propSearch,     setPropSearch]     = useState('');
  const [propPage,       setPropPage]       = useState(1);

  const [bookingsData, setBookingsData] = useState({ bookings: [], total: 0, pages: 1 });
  const [bookSearch,   setBookSearch]   = useState('');
  const [bookStatus,   setBookStatus]   = useState('');
  const [bookPage,     setBookPage]     = useState(1);

  const [mlData,   setMlData]   = useState({ listings: [], total: 0 });
  const [moData,   setMoData]   = useState({ offers:   [], total: 0 });
  const [mlSearch, setMlSearch] = useState('');
  const [mlTab,    setMlTab]    = useState('listings');

  const [commissionAudit, setCommissionAudit] = useState(null);
  const [topEarners,      setTopEarners]      = useState(null);
  const [earnerRole,      setEarnerRole]      = useState('host');

  const [payoutRuns,       setPayoutRuns]       = useState([]);
  const [triggeringPayout, setTriggeringPayout] = useState(false);
  const [connectStatus,    setConnectStatus]    = useState(null);

  const [subscriptions, setSubscriptions] = useState(null);
  const [subsList,      setSubsList]      = useState([]);
  const [subStatus,     setSubStatus]     = useState('active');

  const [riskData,       setRiskData]       = useState(null);
  const [flaggedRiders,  setFlaggedRiders]  = useState([]);
  const [deliveryRosterRegistered, setDeliveryRosterRegistered] = useState([]);
  const [deliveryRosterLimbo,      setDeliveryRosterLimbo]      = useState([]);
  const [driverRosterRegistered,     setDriverRosterRegistered]     = useState([]);
  const [driverRosterLimbo,          setDriverRosterLimbo]          = useState([]);
  const [restaurantRosterRegistered, setRestaurantRosterRegistered] = useState([]);
  const [restaurantRosterLimbo,      setRestaurantRosterLimbo]      = useState([]);
  const [providerRosterRegistered,   setProviderRosterRegistered]   = useState([]);
  const [providerRosterLimbo,        setProviderRosterLimbo]        = useState([]);
  const [incidents,      setIncidents]      = useState([]);
  const [incidentStatus, setIncidentStatus] = useState('');

  const [disputes,        setDisputes]        = useState([]);
  const [disputeFilter,   setDisputeFilter]   = useState('');
  const [resolvingDispute,setResolvingDispute]= useState(null);

  const [pipeline,          setPipeline]          = useState([]);
  const [approvingPipeline, setApprovingPipeline] = useState(null);

  // ── ledger
  const [ledgerAnalytics, setLedgerAnalytics] = useState(null);

  // ── user timeline (drill-down)
  const [selectedUser,    setSelectedUser]    = useState(null);
  const [userTimeline,    setUserTimeline]    = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // ── balance check / preflight / dispute actions
  const [balanceCheck,       setBalanceCheck]       = useState(null);
  const [preflightData,      setPreflightData]      = useState(null);
  const [showPreflightModal, setShowPreflightModal] = useState(false);
  const [preflightLoading,   setPreflightLoading]   = useState(false);
  const [actioningDispute,   setActioningDispute]   = useState(null); // disputeId → action key

  // ── soft launch mode
  const [softLaunchMode,   setSoftLaunchMode]   = useState(false);
  const [betaUsers,        setBetaUsers]        = useState([]);
  const [frictionFeed,     setFrictionFeed]     = useState([]);
  const [frictionHours,    setFrictionHours]    = useState(24);

  // ── growth
  const [funnelData,       setFunnelData]       = useState([]);
  const [verticalPerf,     setVerticalPerf]     = useState([]);

  // ── fraud signals
  const [fraudSignals,     setFraudSignals]     = useState([]);

  // ── retention
  const [lowRatings,       setLowRatings]       = useState([]);
  const [stalledUsers,     setStalledUsers]     = useState([]);
  const [ratingThreshold,  setRatingThreshold]  = useState(3);

  // ── incentives
  const [incentives,       setIncentives]       = useState([]);
  const [showIncentiveModal, setShowIncentiveModal] = useState(false);
  const [newIncentive,     setNewIncentive]     = useState({ userId:'', incentiveType:'commission_waiver', roleScope:'all', discountPct:100, maxUses:'', notes:'' });
  const [savingIncentive,  setSavingIncentive]  = useState(false);

  // ── referral command centre
  const [refCommandCentre,    setRefCommandCentre]    = useState(null);
  const [refFraudFlags,       setRefFraudFlags]       = useState([]);
  const [refAllPayouts,       setRefAllPayouts]       = useState([]);
  const [refPayoutFilter,     setRefPayoutFilter]     = useState('');
  const [voidingPayout,       setVoidingPayout]       = useState(null);
  const [showVoidModal,       setShowVoidModal]       = useState(false);
  const [voidTarget,          setVoidTarget]          = useState(null);
  const [voidReason,          setVoidReason]          = useState('');
  const [voidNotes,           setVoidNotes]           = useState('');

  // ── suspension command centre
  const [suspendedSummary,    setSuspendedSummary]    = useState(null);
  const [suspendEntities,     setSuspendEntities]     = useState({ drivers: [], restaurants: [] });
  const [showSuspendModal,    setShowSuspendModal]    = useState(false);
  const [suspendTarget,       setSuspendTarget]       = useState(null);
  const [suspendReason,       setSuspendReason]       = useState('');
  const [suspendNote,         setSuspendNote]         = useState('');
  const [executingSuspend,    setExecutingSuspend]    = useState(false);

  // ── compliance appeals
  const [complianceAppeals,   setComplianceAppeals]   = useState([]);
  const [appealsTotal,        setAppealsTotal]        = useState(0);
  const [decidingAppeal,      setDecidingAppeal]      = useState(null);
  const [showDecisionModal,   setShowDecisionModal]   = useState(false);
  const [decisionTarget,      setDecisionTarget]      = useState(null);
  const [decisionType,        setDecisionType]        = useState('');
  const [decisionExtra,       setDecisionExtra]       = useState('');

  // ── compliance expiring docs
  const [expiringDocs,        setExpiringDocs]        = useState([]);
  const [nudgingAll,          setNudgingAll]          = useState(false);

  // ── OCR verification queue
  const [ocrQueue,            setOcrQueue]            = useState([]);
  const [ocrRouteFilter,      setOcrRouteFilter]      = useState('');
  const [ocrScoreFilter,      setOcrScoreFilter]      = useState('');
  const [ocrVerifying,        setOcrVerifying]        = useState(null);    // log._id currently being actioned
  const [ocrEditExpiry,       setOcrEditExpiry]       = useState({});      // { [logId]: 'YYYY-MM-DD' }
  const [ocrEditNotes,        setOcrEditNotes]        = useState({});      // { [logId]: 'note text' }
  const [ocrRejectId,         setOcrRejectId]         = useState(null);
  const [ocrRejectReason,     setOcrRejectReason]     = useState('');

  // ── referral codes
  const [referralCodes,    setReferralCodes]    = useState([]);
  const [showMintModal,    setShowMintModal]    = useState(false);
  const [newCode,          setNewCode]          = useState({ code:'', referrerRewardGbp:20, newUserRewardGbp:10, maxUses:'', globalCapGbp:'', notes:'' });
  const [mintingCode,      setMintingCode]      = useState(false);

  // ── audit log
  const [auditLog,         setAuditLog]         = useState([]);
  const [auditFilter,      setAuditFilter]      = useState('');

  // ── user feedback
  const [feedbackItems,    setFeedbackItems]    = useState([]);
  const [feedbackUnread,   setFeedbackUnread]   = useState(0);
  const [feedbackFilterCat, setFeedbackFilterCat] = useState('all');
  const [feedbackFilterPortal, setFeedbackFilterPortal] = useState('all');
  const [feedbackDateTab, setFeedbackDateTab] = useState('all');

  // ── contact-form messages
  const [contactItems,  setContactItems]  = useState([]);
  const [contactUnread, setContactUnread] = useState(0);

  // ── kill-switch system controls (synced with backend platform_settings)
  const [controls, setControls] = useState({
    bookingsEnabled:       true,
    foodOrdersEnabled:     true,
    deliveryEnabled:       true,    // Food-delivery rider dispatch
    ridesEnabled:          false,   // Taxi / PHV — OFF until TfL Operator Licence is live
    marketplaceEnabled:    true,
    guestSignupsEnabled:   true,    // Guest gate — OFF during Supply-First phase
    partnerSignupsEnabled: true,    // Partners always open during build-out
    payoutsEnabled:        true,
    maintenanceMode:       false,
  });

  /* ── auth guard ── */
  useEffect(() => {
    if (!authLoading && user && !user.roles?.includes('admin')) navigate('/');
  }, [authLoading, user, navigate]);

  /* ── load platform settings from backend ── */
  useEffect(() => {
    const token = getAuthToken() || sessionStorage.getItem('authToken');
    if (!token) return;
    adminAPI.getSettings(token)
      .then(data => setControls(prev => ({ ...prev, ...data })))
      .catch(e => console.warn('Could not load platform settings:', e));
  }, [user]);

  /* ── loader helper ── */
  const load = useCallback(async (key, fn) => {
    setLoading(p => ({ ...p, [key]: true }));
    setSectionErrors(p => ({ ...p, [key]: null }));
    try { await fn(); }
    catch (e) {
      console.error(key, e);
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Failed to load data. Please retry.';
      setSectionErrors(p => ({ ...p, [key]: msg }));
    }
    finally { setLoading(p => ({ ...p, [key]: false })); }
  }, []);

  /* ── overview always loads ── */
  useEffect(() => {
    if (authLoading || !user) return;
    const token = getAuthToken();
    load('overview', async () => {
      const [stats, fin, trend, act] = await Promise.all([
        adminAPI.getPlatformStats(token),
        adminAPI.getFinancialOverview(token, period),
        adminAPI.getMonthlyTrend(token, 6),
        adminAPI.getRecentActivity(token, 30),
      ]);
      setPlatformStats(stats);
      setFinancialOverview(fin);
      setMonthlyTrend(trend?.trend || []);
      setRecentActivity(act?.activity || []);
      setLastRefresh(new Date());
    });
    // Refresh the unresolved-alert count in parallel so the sidebar badge
    // is current the moment the admin lands on any section. Cheap query
    // (single count + tiny list); failing silently is fine.
    adminAPI.getAlerts(token, false)
      .then(d => setAdminAlerts(d || { alerts: [], unresolvedCount: 0 }))
      .catch(() => {});

    // Load waitlist stats in parallel (non-blocking — won't break overview if it fails)
    waitlistAPI.getStats(token)
      .then(data => setWaitlistStats(data))
      .catch(() => {});
  }, [authLoading, user, period, refreshKey]);

  /* ── section-specific loads ── */
  useEffect(() => {
    if (authLoading || !user) return;
    const token = getAuthToken();

    if (section === 'users')
      load('users', async () => {
        const d = await adminAPI.getUsers(token, { search: userSearch, role: userRole, page: usersPage });
        setUsersData(d || { users: [], total: 0, pages: 1 });
      });

    if (section === 'properties')
      load('properties', async () => {
        const d = await adminAPI.getAdminProperties(token, { search: propSearch, page: propPage });
        setPropertiesData(d || { properties: [], total: 0, pages: 1 });
      });

    if (section === 'bookings')
      load('bookings', async () => {
        const d = await adminAPI.getAdminBookings(token, { status: bookStatus, search: bookSearch, page: bookPage });
        setBookingsData(d || { bookings: [], total: 0, pages: 1 });
      });

    if (section === 'marketplace')
      load('marketplace', async () => {
        const [ml, mo] = await Promise.all([
          adminAPI.getAdminMarketplaceListings(token, { search: mlSearch }),
          adminAPI.getAdminMarketplaceOffers(token, {}),
        ]);
        setMlData(ml || { listings: [], total: 0 });
        setMoData(mo || { offers: [], total: 0 });
      });

    if (section === 'financial')
      load('financial', async () => {
        const [ca, te] = await Promise.all([
          adminAPI.getCommissionAudit(token, period),
          adminAPI.getTopEarners(token, period, earnerRole),
        ]);
        setCommissionAudit(ca);
        setTopEarners(te);
      });

    if (section === 'payouts')
      load('payouts', async () => {
        const [runs, cs] = await Promise.all([
          adminAPI.getWeeklyPayoutRuns(token),
          adminAPI.getConnectStatus(token),
        ]);
        setPayoutRuns(runs?.runs || []);
        setConnectStatus(cs);
      });

    if (section === 'subscriptions')
      load('subscriptions', async () => {
        const [ov, list] = await Promise.all([
          adminAPI.getSubscriptionsOverview(token),
          adminAPI.getSubscriptionsList(token, subStatus),
        ]);
        setSubscriptions(ov);
        setSubsList(list?.subscriptions || []);
      });

    if (section === 'risk')
      load('risk', async () => {
        const [rd, fr, inc] = await Promise.all([
          adminAPI.getRiskMonitoring(token, period),
          adminAPI.getFlaggedRiders(token),
          adminAPI.getIncidents(null, incidentStatus, token),
        ]);
        setRiskData(rd);
        setFlaggedRiders(fr?.riders || []);
        setIncidents(inc?.incidents || []);
      });

    if (section === 'verification')
      load('verification', async () => {
        const d = await adminAPI.getVerificationPipeline(token);
        setPipeline(d?.pipeline || d || []);
      });

    if (section === 'activity')
      load('activity', async () => {
        const d = await adminAPI.getRecentActivity(token, 100);
        setRecentActivity(d?.activity || []);
      });

    if (section === 'alerts')
      load('alerts', async () => {
        const d = await adminAPI.getAlerts(token, false);
        setAdminAlerts(d || { alerts: [], unresolvedCount: 0 });
      });

    if (section === 'ledger')
      load('ledger', async () => {
        const [d, bc] = await Promise.all([
          adminAPI.getLedgerPlatformAnalytics(token),
          adminAPI.getBalanceCheck(token, 'all'),
        ]);
        setLedgerAnalytics(d);
        setBalanceCheck(bc);
      });

    if (section === 'financial')
      load('balancecheck', async () => {
        const bc = await adminAPI.getBalanceCheck(token, period);
        setBalanceCheck(bc);
      });

    if (section === 'disputes')
      load('disputes', async () => {
        const d = await adminAPI.getAllDisputes(token, disputeFilter);
        setDisputes(d?.disputes || d || []);
      });

    if (section === 'drivers' || section === 'delivery-riders')
      load('risk', async () => {
        const calls = [
          adminAPI.getFlaggedRiders(token),
          adminAPI.getIncidents(null, '', token),
        ];
        if (section === 'delivery-riders') calls.push(adminAPI.getDeliveryRiders(token));
        if (section === 'drivers')         calls.push(adminAPI.getDriversList(token));
        const [fr, inc, roster] = await Promise.all(calls);
        setFlaggedRiders(fr?.riders || []);
        setIncidents(inc?.incidents || []);
        if (roster && section === 'delivery-riders') {
          setDeliveryRosterRegistered(roster.registered || []);
          setDeliveryRosterLimbo(roster.limbo || []);
        }
        if (roster && section === 'drivers') {
          setDriverRosterRegistered(roster.registered || []);
          setDriverRosterLimbo(roster.limbo || []);
        }
      });

    if (section === 'restaurants')
      load('restaurants-roster', async () => {
        const roster = await adminAPI.getRestaurantsList(token);
        setRestaurantRosterRegistered(roster?.registered || []);
        setRestaurantRosterLimbo(roster?.limbo || []);
      });

    if (section === 'providers')
      load('providers-roster', async () => {
        const roster = await adminAPI.getProvidersList(token);
        setProviderRosterRegistered(roster?.registered || []);
        setProviderRosterLimbo(roster?.limbo || []);
      });

    if (section === 'soft-launch')
      load('soft-launch', async () => {
        const [bu, ff] = await Promise.all([
          adminAPI.getBetaUsers(token),
          adminAPI.getFrictionFeed(token, frictionHours),
        ]);
        setBetaUsers(bu?.betaUsers || []);
        setFrictionFeed(ff?.feed || []);
      });

    if (section === 'growth')
      load('growth', async () => {
        const [fn, vp] = await Promise.all([
          adminAPI.getConversionFunnel(token, period),
          adminAPI.getVerticalPerformance(token, period),
        ]);
        setFunnelData(fn?.funnel || []);
        setVerticalPerf(vp?.verticals || []);
      });

    if (section === 'fraud')
      load('fraud', async () => {
        const d = await adminAPI.getFraudSignals(token);
        setFraudSignals(d?.signals || []);
      });

    if (section === 'retention')
      load('retention', async () => {
        const [lr, su] = await Promise.all([
          adminAPI.getLowRatings(token, ratingThreshold),
          adminAPI.getStalledUsers(token),
        ]);
        setLowRatings(lr?.reviews || []);
        setStalledUsers(su?.stalledUsers || []);
      });

    if (section === 'incentives')
      load('incentives', async () => {
        const d = await adminAPI.getIncentives(token);
        setIncentives(d?.incentives || []);
      });

    if (section === 'referrals')
      load('referrals', async () => {
        const d = await adminAPI.getReferralCodes(token);
        setReferralCodes(d?.codes || []);
      });

    if (section === 'audit-log')
      load('audit-log', async () => {
        const d = await adminAPI.getAuditLog(token, { search: auditFilter || undefined, limit: 200 });
        setAuditLog(d?.log || []);
      });

    if (section === 'referral-command')
      load('referral-command', async () => {
        const d = await adminReferralAPI.getCommandCentre(token);
        setRefCommandCentre(d || null);
      });

    if (section === 'referral-fraud')
      load('referral-fraud', async () => {
        const [flags, payouts] = await Promise.all([
          adminReferralAPI.getFraudRadar(token),
          adminReferralAPI.getAllPayouts(token, refPayoutFilter),
        ]);
        setRefFraudFlags(flags?.flags || []);
        setRefAllPayouts(payouts?.payouts || []);
      });

    if (section === 'suspension-command')
      load('suspension-command', async () => {
        const [summary, drivers, restaurants] = await Promise.all([
          adminComplianceAPI.getSuspendedSummary(token),
          adminAPI.getDrivers ? adminAPI.getDrivers(token) : Promise.resolve({ drivers: [] }),
          adminAPI.getRestaurants ? adminAPI.getRestaurants(token, 1, 200, '') : Promise.resolve({ restaurants: [] }),
        ]);
        setSuspendedSummary(summary);
        setSuspendEntities({
          drivers:     (drivers?.drivers     || []).slice(0, 100),
          restaurants: (restaurants?.restaurants || []).slice(0, 100),
        });
      });

    if (section === 'compliance-appeals')
      load('compliance-appeals', async () => {
        const d = await adminComplianceAPI.getAppeals(token);
        setComplianceAppeals(d?.appeals || []);
        setAppealsTotal(d?.total || 0);
      });

    if (section === 'compliance-expiring')
      load('compliance-expiring', async () => {
        const d = await adminComplianceAPI.getExpiring(token, 14);
        setExpiringDocs(d?.expiring || []);
      });

    if (section === 'ocr-verification')
      load('ocr-verification', async () => {
        const d = await adminComplianceAPI.getVerificationQueue(token, {
          route:    ocrRouteFilter || undefined,
          preScore: ocrScoreFilter || undefined,
        });
        setOcrQueue(d?.logs || []);
      });

    if (section === 'user-feedback')
      load('user-feedback', async () => {
        const data = await faqFeedbackAPI.getAll(token);
        setFeedbackItems(data.feedback || []);
        setFeedbackUnread(data.unread_count || 0);
      });

    if (section === 'contact-messages')
      load('contact-messages', async () => {
        const data = await contactAPI.getAll(token);
        setContactItems(data.messages || []);
        setContactUnread(data.unread_count || 0);
      });

  }, [section, authLoading, user, refreshKey,
      ocrRouteFilter, ocrScoreFilter,
      userSearch, userRole, usersPage,
      propSearch, propPage,
      bookSearch, bookStatus, bookPage,
      mlSearch, period, earnerRole, subStatus, incidentStatus, disputeFilter,
      frictionHours, ratingThreshold, auditFilter, refPayoutFilter]);

  /* ── fetch feedback ── */
  const fetchFeedback = async () => {
    try {
      const token = getAuthToken();
      const data = await faqFeedbackAPI.getAll(token);
      setFeedbackItems(data.feedback || []);
      setFeedbackUnread(data.unread_count || 0);
    } catch (err) { console.error('Failed to fetch feedback', err); }
  };

  /* ── fetch contact messages ── */
  const fetchContact = async () => {
    try {
      const token = getAuthToken();
      const data = await contactAPI.getAll(token);
      setContactItems(data.messages || []);
      setContactUnread(data.unread_count || 0);
    } catch (err) { console.error('Failed to fetch contact messages', err); }
  };

  /* ── actions ── */
  const handleSuspend = async (userId, suspended) => {
    try {
      await adminAPI.suspendUser(userId, !suspended, getAuthToken());
      toast({ title: suspended ? 'User unsuspended' : 'User suspended' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleGrantAdmin = async (userId) => {
    try {
      await adminAPI.updateUserRoles(userId, 'add', 'admin', getAuthToken());
      toast({ title: 'Admin role granted' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(
      `Permanently delete the user ${email}? This removes the account and its auth tokens. Bookings / properties / orders are NOT cascade-deleted — they will remain as orphaned records. Continue?`
    )) return;
    try {
      const res = await adminAPI.deleteUser(userId, getAuthToken());
      toast({ title: 'User deleted', description: `Removed ${res?.deletedEmail || email}.` });
      setSelectedUser(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not delete user';
      toast({ title: 'Delete failed', description: detail, variant: 'destructive' });
    }
  };

  const handleTriggerPayout = async () => {
    setTriggeringPayout(true);
    try {
      await adminAPI.triggerWeeklyPayout(getAuthToken());
      toast({ title: 'Weekly payout triggered' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error triggering payout', variant: 'destructive' }); }
    finally { setTriggeringPayout(false); }
  };

  const handleResolveDispute = async (orderId, decision) => {
    setResolvingDispute(orderId);
    try {
      await adminAPI.resolveDispute(orderId, decision, '', getAuthToken());
      toast({ title: `Dispute ${decision}d` });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setResolvingDispute(null); }
  };

  /* ── New light-touch arbitration actions ── */
  const handleDisputeAction = async (disputeId, action) => {
    setActioningDispute(`${disputeId}:${action}`);
    try {
      const token = getAuthToken();
      if (action === 'initiate-refund') {
        await adminAPI.initiateDisputeRefund(disputeId, null, token);
        toast({ title: 'Refund request initiated', description: 'Host notified. Funds held in escrow.' });
      } else if (action === 'legal') {
        await adminAPI.markDisputeLegal(disputeId, null, token);
        toast({ title: 'Marked as off-platform legal', description: 'Admin is no longer managing this case.' });
      } else if (action === 'close') {
        await adminAPI.closeDispute(disputeId, null, token);
        toast({ title: 'Dispute closed', description: 'Escrow released.' });
      }
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setActioningDispute(null); }
  };

  /* ── Payout preflight ── */
  const handlePreflightCheck = async () => {
    setPreflightLoading(true);
    try {
      const data = await adminAPI.getPayoutPreflight(getAuthToken());
      setPreflightData(data);
      setShowPreflightModal(true);
    } catch { toast({ title: 'Could not run preflight check', variant: 'destructive' }); }
    finally { setPreflightLoading(false); }
  };

  const handleConfirmPayout = async () => {
    setShowPreflightModal(false);
    await handleTriggerPayout();
  };

  /* ── Incentive actions ── */
  const handleCreateIncentive = async () => {
    setSavingIncentive(true);
    try {
      await adminAPI.createIncentive({
        userId:        newIncentive.userId,
        incentiveType: newIncentive.incentiveType,
        roleScope:     newIncentive.roleScope,
        discountPct:   parseInt(newIncentive.discountPct, 10),
        maxUses:       newIncentive.maxUses ? parseInt(newIncentive.maxUses, 10) : null,
        notes:         newIncentive.notes || null,
      }, getAuthToken());
      toast({ title: 'Incentive granted' });
      setShowIncentiveModal(false);
      setNewIncentive({ userId:'', incentiveType:'commission_waiver', roleScope:'all', discountPct:100, maxUses:'', notes:'' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error creating incentive', variant: 'destructive' }); }
    finally { setSavingIncentive(false); }
  };

  const handleRevokeIncentive = async (id) => {
    try {
      await adminAPI.revokeIncentive(id, getAuthToken());
      toast({ title: 'Incentive revoked' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleMintCode = async () => {
    setMintingCode(true);
    try {
      const res = await adminAPI.mintReferralCode({
        code:              newCode.code || undefined,
        referrerRewardGbp: parseFloat(newCode.referrerRewardGbp),
        newUserRewardGbp:  parseFloat(newCode.newUserRewardGbp),
        maxUses:           newCode.maxUses ? parseInt(newCode.maxUses, 10) : null,
        globalCapGbp:      newCode.globalCapGbp ? parseFloat(newCode.globalCapGbp) : null,
        notes:             newCode.notes || null,
      }, getAuthToken());
      toast({ title: `Code "${res.code}" minted` });
      setShowMintModal(false);
      setNewCode({ code:'', referrerRewardGbp:20, newUserRewardGbp:10, maxUses:'', globalCapGbp:'', notes:'' });
      setRefreshKey(k => k + 1);
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Error minting code', variant: 'destructive' }); }
    finally { setMintingCode(false); }
  };

  const handleToggleCode = async (code) => {
    try {
      const res = await adminAPI.toggleReferralCode(code, getAuthToken());
      toast({ title: res.message });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleSetAmbassador = async (userId, flag) => {
    try {
      await adminAPI.setAmbassador(userId, flag, getAuthToken());
      toast({ title: flag ? 'Ambassador status granted' : 'Ambassador status removed' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleSetBeta = async (userId, flag) => {
    try {
      await adminAPI.setBetaFlag(userId, flag, null, getAuthToken());
      toast({ title: flag ? 'Beta flag set' : 'Beta flag cleared' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  /* ── Referral payout actions ── */
  const handleVoidPayout = async () => {
    if (!voidTarget) return;
    const payoutId = typeof voidTarget === 'string' ? voidTarget : voidTarget._id;
    setVoidingPayout(payoutId);
    try {
      await adminReferralAPI.voidPayout(payoutId, voidReason, voidNotes, getAuthToken());
      toast({ title: 'Payout voided — internal note auto-created' });
      setShowVoidModal(false);
      setVoidTarget(null);
      setVoidReason('');
      setVoidNotes('');
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error voiding payout', variant: 'destructive' }); }
    finally { setVoidingPayout(null); }
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      await adminReferralAPI.approvePayout(payoutId, getAuthToken());
      toast({ title: 'Payout approved — queued for next weekly run' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleToggleReferralKillSwitch = async (active) => {
    try {
      await adminReferralAPI.updateCampaign({ newReferralsActive: active }, getAuthToken());
      toast({ title: active
        ? 'New referrals earn rewards again'
        : "Paused: new referrals are still recorded but won't earn. Existing referrals keep earning." });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  /* ── Suspension Command Centre ── */
  const handleSuspendEntity = async () => {
    if (!suspendTarget || !suspendReason) return;
    setExecutingSuspend(true);
    try {
      const res = await adminComplianceAPI.suspendEntity(
        suspendTarget._id || suspendTarget.id,
        suspendTarget.entityType,
        suspendReason,
        suspendNote,
        !suspendTarget.suspended,
        getAuthToken()
      );
      const wasSuspending = !suspendTarget.suspended;
      toast({
        title: wasSuspending ? '🚫 Entity suspended' : '✅ Entity reactivated',
        description: res.pendingLockdown
          ? 'Active trip in progress — lockdown pending completion.'
          : res.message,
      });
      setShowSuspendModal(false);
      setSuspendTarget(null);
      setSuspendReason('');
      setSuspendNote('');
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setExecutingSuspend(false); }
  };

  /* ── Appeal decisions ── */
  const handleDecideAppeal = async () => {
    if (!decisionTarget || !decisionType) return;
    setDecidingAppeal(decisionTarget._id);
    try {
      const extra = decisionType === 'request_info'
        ? { infoRequest: decisionExtra }
        : decisionType === 'permanent_ban'
        ? { banReason: decisionExtra }
        : {};
      await adminComplianceAPI.decideAppeal(decisionTarget._id, decisionType, extra, getAuthToken());
      const labels = { approve: 'Appeal approved ✅', request_info: 'More info requested 📋', permanent_ban: 'Permanently banned 🔒' };
      toast({ title: labels[decisionType] || 'Decision recorded' });
      setShowDecisionModal(false);
      setDecisionTarget(null);
      setDecisionType('');
      setDecisionExtra('');
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setDecidingAppeal(null); }
  };

  const handleNudgeAll = async () => {
    setNudgingAll(true);
    try {
      const res = await adminComplianceAPI.nudgeAll(getAuthToken());
      toast({ title: `Nudge sent to ${res.nudged || 0} entities` });
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setNudgingAll(false); }
  };

  const handleDownloadReport = () => {
    adminComplianceAPI.downloadReport(getAuthToken());
  };

  const handleOcrVerify = async (log) => {
    const expiry = ocrEditExpiry[log._id] || log.extractedData?.expiryDate || '';
    if (!expiry) {
      toast({ title: 'Please enter an expiry date', variant: 'destructive' });
      return;
    }
    setOcrVerifying(log._id);
    try {
      const res = await adminComplianceAPI.verifyDocument(
        log._id, expiry,
        log.extractedData?.certNumber || '',
        ocrEditNotes[log._id] || '',
        getAuthToken()
      );
      toast({ title: res.reactivated ? 'Document verified — account reactivated ✅' : 'Document verified ✅' });
      setOcrQueue(prev => prev.filter(l => l._id !== log._id));
    } catch {
      toast({ title: 'Error verifying document', variant: 'destructive' });
    } finally { setOcrVerifying(null); }
  };

  const handleOcrReject = async () => {
    if (!ocrRejectId || !ocrRejectReason.trim()) return;
    setOcrVerifying(ocrRejectId);
    try {
      await adminComplianceAPI.rejectDocument(ocrRejectId, ocrRejectReason.trim(), getAuthToken());
      toast({ title: 'Document rejected — partner notified' });
      setOcrQueue(prev => prev.filter(l => l._id !== ocrRejectId));
      setOcrRejectId(null);
      setOcrRejectReason('');
    } catch {
      toast({ title: 'Error rejecting document', variant: 'destructive' });
    } finally { setOcrVerifying(null); }
  };

  const handleApprovePipeline = async (restaurantId) => {
    setApprovingPipeline(restaurantId);
    try {
      await adminAPI.pipelineApprove(restaurantId, 'Approved by admin', getAuthToken());
      toast({ title: 'Restaurant approved' });
      setRefreshKey(k => k + 1);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
    finally { setApprovingPipeline(null); }
  };

  const fetchUserTimeline = async (userId) => {
    setTimelineLoading(true);
    setUserTimeline(null);
    try {
      const data = await adminAPI.getUserTimeline(userId, getAuthToken());
      setUserTimeline(data);
    } catch (e) {
      console.error('timeline', e);
      toast({ title: 'Could not load timeline', variant: 'destructive' });
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleSelectUser = (u) => {
    if (selectedUser?._id === u._id) {
      setSelectedUser(null);
      setUserTimeline(null);
    } else {
      setSelectedUser(u);
      fetchUserTimeline(u._id);
    }
  };

  const handleToggleControl = async (key, value) => {
    // Per-vertical legal/insurance prerequisites. These verticals stay OFF until
    // their compliance is in place; warn before an admin switches one ON.
    const VERTICAL_PREREQS = {
      ridesEnabled: "Taxi / PHV requires a TfL Operator Licence, driver PHV licences, enhanced DBS and hire-and-reward insurance before going live.",
      foodOrdersEnabled: "Food requires FSA registration and per-item allergen data (Natasha's Law) before going live.",
      deliveryEnabled: "Delivery requires rider right-to-work checks, NMW compliance and rider insurance before going live.",
    };
    const patch = { [key]: value };
    if (value && VERTICAL_PREREQS[key]) {
      const proceed = window.confirm(`${VERTICAL_PREREQS[key]}\n\nI confirm these prerequisites are in place. Enable?`);
      if (!proceed) return;
      // The backend refuses to enable a gated vertical without this flag
      // (HTTP 428) — the confirm above is the acknowledgement.
      patch.acknowledgePrerequisites = true;
      if (key === 'ridesEnabled' && !controls.tflOperatorLicenceNumber) {
        const licence = window.prompt('Enter the TfL operator licence number (required before rides can go live):', '');
        if (!licence || !licence.trim()) {
          toast({ title: 'Rides not enabled', description: 'A TfL operator licence number is required first.', variant: 'destructive' });
          return;
        }
        patch.tflOperatorLicenceNumber = licence.trim();
      }
    }
    setControls(prev => ({ ...prev, [key]: value }));
    const label = key.replace(/([A-Z])/g, ' $1').trim();
    try {
      const token = getAuthToken() || sessionStorage.getItem('authToken');
      if (!token) throw new Error('No auth token — please log in again.');
      console.log('[kill-switch] PATCH', patch, 'token?', !!token);
      const resp = await adminAPI.updateSettings(patch, token);
      console.log('[kill-switch] response', resp);
      // Server is source of truth — apply all returned settings to state
      if (resp && typeof resp === 'object') {
        const { ok, ...serverSettings } = resp;
        if (Object.keys(serverSettings).length) {
          setControls(prev => ({ ...prev, ...serverSettings }));
        }
      }
      toast({
        title: value ? `${label} enabled` : `${label} disabled`,
        description: value ? 'Setting saved — takes effect immediately.' : 'Kill-switch active — setting saved.',
      });
    } catch (e) {
      // Revert optimistic update on failure
      setControls(prev => ({ ...prev, [key]: !value }));
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      console.error('[kill-switch] failed', status, detail, e);
      toast({
        title: `Failed to save setting${status ? ` (${status})` : ''}`,
        description: (typeof detail === 'string' ? detail : detail?.message) || e?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Only boolean OFF entries count as kill switches. The controls object
  // is hydrated from /api/admin/settings which also returns numeric
  // counters like foundingCohortHostCount (currently 0). The previous
  // !v test caught those zeros and labelled them as active kill
  // switches in the alert banner. Restricting to explicit `false`
  // booleans fixes that without listing each switch by hand.
  const activeKillSwitches = Object.entries(controls).filter(([k, v]) =>
    typeof v === 'boolean' && v === false && k !== 'maintenanceMode'
  );
  const maintenanceActive = controls.maintenanceMode;

  const activeItem = ALL_ITEMS.find(n => n.id === section);
  const isLoading  = Object.values(loading).some(Boolean);

  if (authLoading) return (
    <div className="min-h-screen bg-[#0A0F1E] flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-zinc-600" />
    </div>
  );

  /* ══════════════════════════════════════════════════
     CHART THEME  — matches dark console
  ══════════════════════════════════════════════════ */
  const chartStyle = {
    tick: { fill: '#52525b', fontSize: 10 },
    cartesian: { stroke: '#27272a', strokeDasharray: '4 4' },
    tooltip: {
      contentStyle: { background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, fontSize: 11, color: '#e4e4e7' },
      cursor: { fill: 'rgba(255,255,255,0.04)' },
    },
  };

  /* ══════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════ */
  return (
    <div className="flex h-screen bg-[#0A0F1E] text-zinc-200 overflow-hidden font-sans">

      {/* ╔══════════════════════════════╗
          ║  S I D E B A R              ║
          ╚══════════════════════════════╝ */}
      <aside className={`flex-shrink-0 flex flex-col border-r border-white/6 transition-all duration-200 bg-[#0D1323]
        ${collapsed ? 'w-14' : 'w-52'}`}>

        {/* Logo + collapse toggle */}
        <div className="flex items-center justify-between px-3 py-3.5 border-b border-white/6">
          {!collapsed && (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-6 w-6 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-5 w-auto object-contain brightness-0 invert" /></div>
              <div className="min-w-0">
                <div className="text-white text-xs font-bold leading-tight truncate">Ariv</div>
                <div className="text-[9px] text-zinc-600 font-bold uppercase tracking-widest mt-0.5">Admin Console</div>
              </div>
            </div>
          )}
          {collapsed && <div className="h-6 w-6 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 mx-auto"><img src="/logo.png" alt="Ariv" className="h-5 w-auto object-contain brightness-0 invert" /></div>}
          {!collapsed && (
            <button onClick={() => setCollapsed(true)} className="text-zinc-600 hover:text-zinc-400 flex-shrink-0 ml-1">
              <PanelLeftClose size={14} />
            </button>
          )}
        </div>

        {/* PRODUCTION badge + kill-switch alert */}
        {!collapsed && (
          <div className="px-3 py-2 border-b border-white/4 space-y-1.5">
            {/* Soft launch mode toggle */}
            <button
              onClick={() => setSoftLaunchMode(m => !m)}
              className={`w-full flex items-center gap-1.5 rounded px-2 py-1 border transition-colors
                ${softLaunchMode
                  ? 'bg-violet-500/20 border-violet-500/40'
                  : 'bg-white/3 border-white/8 hover:bg-white/6'
                }`}
            >
              <Telescope size={10} className={softLaunchMode ? 'text-violet-400' : 'text-zinc-600'} />
              <span className={`text-[9px] font-bold uppercase tracking-widest ${softLaunchMode ? 'text-violet-400' : 'text-zinc-600'}`}>
                {softLaunchMode ? 'Soft Launch Mode' : 'Soft Launch'}
              </span>
              {softLaunchMode && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />}
            </button>
            <div className={`flex items-center gap-1.5 rounded px-2 py-1 border transition-colors
              ${maintenanceActive
                ? 'bg-amber-500/15 border-amber-500/30'
                : 'bg-emerald-500/10 border-emerald-500/20'
              }`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse
                ${maintenanceActive ? 'bg-amber-400' : 'bg-emerald-400'}`} />
              <span className={`text-[9px] font-bold uppercase tracking-widest
                ${maintenanceActive ? 'text-amber-400' : 'text-emerald-400'}`}>
                {maintenanceActive ? 'Maintenance Mode' : 'Production'}
              </span>
            </div>
            {(activeKillSwitches.length > 0 || maintenanceActive) && (
              <button
                onClick={() => setSection('system-controls')}
                className="w-full flex items-center gap-1.5 bg-red-500/15 border border-red-500/30 rounded px-2 py-1 hover:bg-red-500/20 transition-colors"
              >
                <AlertOctagon size={10} className="text-red-400 flex-shrink-0" />
                <span className="text-[9px] font-bold text-red-400 truncate">
                  {activeKillSwitches.length} switch{activeKillSwitches.length !== 1 ? 'es' : ''} OFF
                </span>
              </button>
            )}
          </div>
        )}

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto py-3 space-y-4 scrollbar-thin">
          {NAV.map(group => (
            <div key={group.group}>
              {!collapsed && (
                <div className="px-3 mb-1 text-[9px] font-bold uppercase tracking-widest text-zinc-700">
                  {group.group}
                </div>
              )}
              {group.items.map(item => {
                const Icon   = item.icon;
                const active = section === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSection(item.id)}
                    title={collapsed ? item.label : undefined}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-xs
                      ${active
                        ? 'bg-[#1F3A68]/40 text-white border-r-2 border-[#1F3A68]'
                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/4'
                      }`}
                  >
                    <Icon size={14} className={`flex-shrink-0 ${active ? 'text-blue-400' : ''}`} />
                    {!collapsed && <span className="truncate font-medium flex-1">{item.label}</span>}
                    {!collapsed && item.id === 'system-controls' && activeKillSwitches.length > 0 && (
                      <span className="w-4 h-4 rounded-full bg-red-500/80 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                        {activeKillSwitches.length}
                      </span>
                    )}
                    {collapsed && item.id === 'system-controls' && activeKillSwitches.length > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
                    )}
                    {!collapsed && item.id === 'alerts' && adminAlerts.unresolvedCount > 0 && (
                      <span className="w-4 h-4 rounded-full bg-amber-500/90 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                        {adminAlerts.unresolvedCount}
                      </span>
                    )}
                    {collapsed && item.id === 'alerts' && adminAlerts.unresolvedCount > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
                    )}
                    {!collapsed && item.id === 'user-feedback' && feedbackUnread > 0 && (
                      <span className="w-4 h-4 rounded-full bg-blue-500/80 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                        {feedbackUnread}
                      </span>
                    )}
                    {collapsed && item.id === 'user-feedback' && feedbackUnread > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-blue-500" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom: expand + user + logout */}
        <div className="border-t border-white/6 p-2 space-y-1">
          {collapsed && (
            <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center py-2 text-zinc-600 hover:text-zinc-400">
              <PanelLeftOpen size={14} />
            </button>
          )}
          {!collapsed && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/4">
              <div className="w-6 h-6 rounded-full bg-[#1F3A68] flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-bold text-white">{(user?.name || user?.email || 'A')[0].toUpperCase()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-semibold text-zinc-300 truncate">{user?.name || user?.email}</div>
                <div className="text-[9px] text-zinc-600">Administrator</div>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/admin/maintenance')}
            title="One-off maintenance jobs (encryption backfill etc.)"
            className="w-full flex items-center gap-2 px-2 py-2 text-zinc-600 hover:text-zinc-200 text-[10px] transition-colors rounded hover:bg-white/4"
          >
            <Wrench size={13} />
            {!collapsed && 'Maintenance'}
          </button>
          <button
            onClick={() => navigate('/settings')}
            title="Account settings, password, 2FA"
            className="w-full flex items-center gap-2 px-2 py-2 text-zinc-600 hover:text-zinc-200 text-[10px] transition-colors rounded hover:bg-white/4"
          >
            <Settings size={13} />
            {!collapsed && 'Account settings'}
          </button>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            title="Sign out"
            className="w-full flex items-center gap-2 px-2 py-2 text-zinc-600 hover:text-red-400 text-[10px] transition-colors rounded hover:bg-white/4"
          >
            <LogOut size={13} />
            {!collapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      {/* ╔══════════════════════════════════════════╗
          ║  M A I N   A R E A                      ║
          ╚══════════════════════════════════════════╝ */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* ── TOP BAR ── */}
        <header className="flex-shrink-0 h-12 bg-[#0D1323] border-b border-white/6 flex items-center px-5 gap-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-zinc-600 font-medium">Admin</span>
            <ChevronRight size={12} className="text-zinc-700" />
            <span className="text-zinc-300 font-semibold">{activeItem?.label}</span>
          </div>

          <div className="flex-1" />

          {/* Kill-switch warning pill */}
          {(activeKillSwitches.length > 0 || maintenanceActive) && (
            <button
              onClick={() => setSection('system-controls')}
              className="flex items-center gap-1.5 bg-red-500/20 border border-red-500/30 rounded px-2.5 py-1 text-[9px] font-bold text-red-400 uppercase tracking-wider hover:bg-red-500/30 transition-colors"
            >
              <AlertOctagon size={10} />
              {maintenanceActive ? 'MAINTENANCE' : `${activeKillSwitches.length} KILL-SWITCH${activeKillSwitches.length !== 1 ? 'ES' : ''} ACTIVE`}
            </button>
          )}

          {/* System status pills */}
          <div className="hidden md:flex items-center gap-2">
            {[
              { label: 'API', ok: true },
              { label: 'DB',  ok: true },
              { label: 'Pay', ok: controls.payoutsEnabled },
            ].map(s => (
              <div key={s.label} className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider
                ${s.ok ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-400'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s.ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {s.label}
              </div>
            ))}
          </div>

          {/* Period selector (financial + overview + risk) */}
          {['overview', 'financial', 'risk'].includes(section) && (
            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              className="bg-white/6 border border-white/10 text-zinc-300 rounded text-[10px] px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
            >
              <option value="7days">7 days</option>
              <option value="30days">30 days</option>
              <option value="90days">90 days</option>
            </select>
          )}

          {/* Last refresh */}
          <span className="text-[9px] text-zinc-700 hidden lg:block">
            Refreshed {fmtTime(lastRefresh)}
          </span>

          {/* Refresh */}
          <button
            onClick={() => { setRefreshKey(k => k + 1); setLastRefresh(new Date()); }}
            className="text-zinc-600 hover:text-zinc-300 transition-colors"
            title="Refresh data"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </header>

        {/* ── CONTENT ── */}
        <main className="flex-1 overflow-y-auto p-5 bg-[#0A0F1E]">

          {/* Soft Launch Mode Banner */}
          {softLaunchMode && (
            <div className="mb-4 flex items-center gap-3 bg-violet-500/15 border border-violet-500/30 rounded-xl px-4 py-2.5">
              <Telescope size={14} className="text-violet-400 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-violet-300 text-xs font-bold">Soft Launch Mode Active</span>
                <span className="text-violet-400/70 text-[11px] ml-2">High-intensity monitoring lens — focusing on beta cohort UX & friction signals</span>
              </div>
              <button
                onClick={() => setSection('soft-launch')}
                className="text-[10px] font-bold text-violet-400 bg-violet-500/20 px-2.5 py-1 rounded hover:bg-violet-500/30 transition-colors"
              >
                View Dashboard →
              </button>
              <button onClick={() => setSoftLaunchMode(false)} className="text-violet-500 hover:text-violet-300 ml-1">
                <X size={12} />
              </button>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              OVERVIEW
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'overview' && (
            <div className="space-y-5">
              <SectionHead
                title="Platform Overview"
                sub={`All verticals · ${period}`}
              />

              {/* KPI row 1 — financial */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="GMV (Period)"        value={fmt(financialOverview?.gmv)}                   icon={TrendingUp}  onClick={() => setSection('financial')} drillLabel="View Financial →" />
                <Kpi label="Platform Revenue"    value={fmt(financialOverview?.platformRevenue)}        icon={DollarSign}  onClick={() => setSection('ledger')}    drillLabel="View Ledger →"    />
                <Kpi label="Transactions"        value={fmtN(financialOverview?.transactionCount)}      icon={Receipt}     onClick={() => setSection('ledger')}    drillLabel="View Ledger →"    />
                <Kpi label="Avg. Booking Value"  value={fmt(financialOverview?.avgBookingValue)}        icon={BarChart3}   onClick={() => setSection('bookings')}  drillLabel="View Bookings →"  />
              </div>

              {/* KPI row 2 — entities */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Total Users"         value={fmtN(platformStats?.users?.total)}              icon={Users}        onClick={() => setSection('users')}      drillLabel="View Users →"      />
                <Kpi label="New Users (7d)"      value={fmtN(platformStats?.recentActivity?.newUsersLast7Days)} icon={ArrowUpRight} onClick={() => setSection('users')} drillLabel="View Users →" />
                <Kpi label="Active Properties"   value={fmtN(platformStats?.entities?.publishedProperties)} icon={Home}     onClick={() => setSection('properties')} drillLabel="View Properties →" />
                <Kpi label="Active Restaurants"  value={fmtN(platformStats?.entities?.activeRestaurants)} icon={Utensils}  onClick={() => setSection('restaurants')} drillLabel="Restaurants →"    />
              </div>

              {/* User role breakdown + bookings 7d */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Hosts"           value={fmtN(platformStats?.users?.byRole?.hosts)}         icon={Building2}    onClick={() => { setUserRole('host');       setSection('users'); }} drillLabel="Filter Hosts →"    />
                <Kpi label="Guests"          value={fmtN(platformStats?.users?.byRole?.guests)}        icon={Users}        onClick={() => { setUserRole('guest');      setSection('users'); }} drillLabel="Filter Guests →"   />
                <Kpi label="Bookings (7d)"   value={fmtN(platformStats?.recentActivity?.bookingsLast7Days)} icon={CalendarDays} onClick={() => setSection('bookings')} drillLabel="View Bookings →" />
                <Kpi label="Total Bookings"  value={fmtN(platformStats?.entities?.bookings)}           icon={CalendarDays} onClick={() => setSection('bookings')} drillLabel="View Bookings →"   />
              </div>

              {/* Waitlist Demand Signal — shown when guest gate is closed or supply is building */}
              {(waitlistStats || platformStats?.waitlist?.total > 0) && (
                <div className={`flex items-center gap-4 p-4 rounded-xl border transition-all
                  ${(waitlistStats?.launchSignal || platformStats?.waitlist?.launchSignal)
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-amber-500/8 border-amber-500/20'
                  }`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${(waitlistStats?.launchSignal || platformStats?.waitlist?.launchSignal) ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400 animate-pulse'}`} />
                      <span className="text-xs font-bold text-zinc-300">
                        Guest Waitlist — {(waitlistStats?.total ?? platformStats?.waitlist?.total ?? 0).toLocaleString()} waiting
                      </span>
                      {(waitlistStats?.today ?? platformStats?.waitlist?.todayGrowth ?? 0) > 0 && (
                        <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full">
                          +{waitlistStats?.today ?? platformStats?.waitlist?.todayGrowth} today
                        </span>
                      )}
                    </div>
                    {/* Progress bar toward 500 threshold */}
                    <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden mt-1">
                      <div
                        className={`h-1.5 rounded-full transition-all ${(waitlistStats?.launchSignal || platformStats?.waitlist?.launchSignal) ? 'bg-emerald-400' : 'bg-amber-400'}`}
                        style={{ width: `${Math.min(100, Math.round(((waitlistStats?.total ?? platformStats?.waitlist?.total ?? 0) / 500) * 100))}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-zinc-600 mt-1">
                      {(waitlistStats?.launchSignal || platformStats?.waitlist?.launchSignal)
                        ? '🚀 500-guest threshold reached — evaluate supply before opening the Guest Gate.'
                        : `${Math.max(0, 500 - (waitlistStats?.total ?? platformStats?.waitlist?.total ?? 0))} more guests needed to hit launch threshold.`
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => setSection('system-controls')}
                    className="text-[10px] bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 px-3 py-1.5 rounded-lg flex-shrink-0 transition-colors"
                  >
                    System Controls →
                  </button>
                </div>
              )}

              {/* Chart + verticals */}
              <div className="grid lg:grid-cols-3 gap-4">
                {/* Revenue area chart */}
                <div className="lg:col-span-2 bg-[#111827] border border-white/6 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold text-zinc-300">Revenue Trend</span>
                    <span className="text-[10px] text-zinc-600">6 months · GMV + Platform Revenue</span>
                  </div>
                  {monthlyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={monthlyTrend}>
                        <defs>
                          <linearGradient id="gGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#1F3A68" stopOpacity={0.5}/>
                            <stop offset="95%" stopColor="#1F3A68" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="rGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#10b981" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="4 4" stroke="#27272a" />
                        <XAxis dataKey="month" tick={chartStyle.tick} axisLine={false} tickLine={false} />
                        <YAxis tick={chartStyle.tick} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                        <Tooltip {...chartStyle.tooltip} formatter={v => fmt(v)} />
                        <Area type="monotone" dataKey="gmv"     stroke="#3b82f6" fill="url(#gGrad)" strokeWidth={1.5} name="GMV"     dot={false}/>
                        <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#rGrad)" strokeWidth={1.5} name="Revenue" dot={false}/>
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-zinc-700 text-xs">No trend data for period</div>
                  )}
                </div>

                {/* Verticals at a glance */}
                <div className="bg-[#111827] border border-white/6 rounded-lg p-4">
                  <span className="text-xs font-bold text-zinc-300 block mb-4">Verticals</span>
                  <div className="space-y-3">
                    {[
                      { label: 'Properties',   icon: Home,        value: platformStats?.entities?.publishedProperties, color: '#3b82f6' },
                      { label: 'Hosts',        icon: Building2,   value: platformStats?.users?.byRole?.hosts,          color: '#10b981' },
                      { label: 'Bookings',     icon: CalendarDays,value: platformStats?.entities?.bookings,             color: '#f59e0b' },
                      { label: 'Food Orders',  icon: Utensils,    value: platformStats?.entities?.foodOrders,           color: '#ef4444' },
                      { label: 'Landlords',    icon: Building2,   value: platformStats?.users?.byRole?.landlords,       color: '#8b5cf6' },
                      { label: 'Providers',    icon: Wrench,      value: platformStats?.users?.byRole?.providers,       color: '#06b6d4' },
                    ].map((v, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <v.icon size={12} style={{ color: v.color }} className="flex-shrink-0" />
                        <span className="text-xs text-zinc-500 flex-1">{v.label}</span>
                        <span className="text-xs font-bold tabular-nums text-zinc-200">{fmtN(v.value) || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Activity feed */}
              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                  <span className="text-xs font-bold text-zinc-300">Live Activity</span>
                  <button onClick={() => setSection('activity')} className="text-[10px] text-[#3b82f6] hover:text-blue-300 transition-colors">
                    View full log →
                  </button>
                </div>
                <div className="divide-y divide-white/4 max-h-64 overflow-y-auto">
                  {loading['overview'] && <Spin />}
                  {!loading['overview'] && recentActivity.slice(0, 15).map((ev, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/3">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        ev.type === 'user_signup' ? 'bg-blue-400' :
                        ev.type === 'booking'     ? 'bg-emerald-400' : 'bg-amber-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-300 truncate">{ev.title}</p>
                        <p className="text-[10px] text-zinc-600 truncate">{ev.detail}</p>
                      </div>
                      <div className="text-[10px] text-zinc-700 flex-shrink-0">
                        {ev.timestamp ? fmtDate(ev.timestamp) : ''}
                      </div>
                    </div>
                  ))}
                  {!loading['overview'] && recentActivity.length === 0 && (
                    <div className="px-4 py-8 text-center text-zinc-700 text-xs">No recent activity</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              USERS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'users' && (
            <div className="space-y-4">
              <SectionHead title="User Management" sub={`${fmtN(usersData.total)} registered accounts`} />

              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input
                    value={userSearch}
                    onChange={e => { setUserSearch(e.target.value); setUsersPage(1); }}
                    placeholder="Search name or email…"
                    className="pl-7 pr-3 py-1.5 bg-[#111827] border border-white/10 rounded text-xs text-zinc-300 placeholder-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#1F3A68] w-56"
                  />
                </div>
                <select
                  value={userRole}
                  onChange={e => { setUserRole(e.target.value); setUsersPage(1); }}
                  className="bg-[#111827] border border-white/10 rounded text-xs text-zinc-400 px-2.5 py-1.5 focus:outline-none"
                >
                  <option value="">All roles</option>
                  {['guest','host','landlord','service_provider','restaurant_partner','driver','delivery_rider','admin'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                {loading['users'] ? <Spin /> : (
                  <Table heads={['Name / Email', 'Ref ID', 'Roles', 'Joined', 'Status', 'View As', 'Actions']}
                    empty={usersData.users.length === 0 && <Empty icon={Users} msg="No users found" />}>
                    {usersData.users.map(u => (
                      <React.Fragment key={u._id}>
                        <Tr onClick={() => handleSelectUser(u)}>
                          <Td>
                            <div className="flex items-center gap-1.5">
                              {selectedUser?._id === u._id
                                ? <ChevronDown size={11} className="text-blue-400 flex-shrink-0" />
                                : <ChevronRight size={11} className="text-zinc-700 flex-shrink-0" />
                              }
                              <div>
                                <div className="font-semibold text-zinc-200 flex items-center gap-1.5 flex-wrap">
                                  {u.name || '—'}
                                  {u.merchantAgreementSignedAt && (
                                    <span title={`Merchant agreement signed ${fmtDate(u.merchantAgreementSignedAt)}`}
                                      className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wide bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded ring-1 ring-emerald-500/25">
                                      <CheckSquare size={8} /> Signed
                                    </span>
                                  )}
                                  {u.isAmbassador && (
                                    <span className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded ring-1 ring-amber-500/25">
                                      <Award size={8} /> Ambassador
                                    </span>
                                  )}
                                  {u.isBetaUser && (
                                    <span className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wide bg-violet-500/15 text-violet-400 px-1.5 py-0.5 rounded ring-1 ring-violet-500/25">
                                      <FlaskConical size={8} /> Beta
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] text-zinc-600 mt-0.5 font-mono">{u.email}</div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span className="font-mono text-[10px] text-amber-400/80 bg-amber-500/8 border border-amber-500/15 px-2 py-1 rounded-md tracking-wider select-all" title="Human-readable user reference — share with admin for lookups">
                              {u.userRef || `BC-${(u._id || '').slice(-5).toUpperCase()}`}
                            </span>
                          </Td>
                          <Td>
                            <div className="flex flex-wrap gap-1">
                              {(u.roles || []).map(r => (
                                <span key={r} className="text-[9px] bg-[#1F3A68]/40 text-blue-300 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide">{r}</span>
                              ))}
                            </div>
                          </Td>
                          <Td muted mono>{fmtDate(u.createdAt)}</Td>
                          <Td><Badge status={u.suspended ? 'suspended' : 'active'} /></Td>
                          <Td>
                            <a
                              href={portalUrl(u.roles || [])}
                              target="_blank"
                              rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              <Eye size={10} /> View As
                            </a>
                          </Td>
                          <Td>
                            <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                              <Btn
                                size="xs"
                                variant={u.suspended ? 'success' : 'danger'}
                                onClick={() => handleSuspend(u._id, u.suspended)}
                              >
                                {u.suspended ? 'Unsuspend' : 'Suspend'}
                              </Btn>
                              {!u.roles?.includes('admin') && (
                                <Btn size="xs" variant="ghost" onClick={() => handleGrantAdmin(u._id)}>
                                  <Lock size={9} /> Admin
                                </Btn>
                              )}
                              <Btn size="xs" variant="ghost" onClick={() => handleSetAmbassador(u._id, !u.isAmbassador)}
                                className={u.isAmbassador ? 'text-amber-400' : ''}>
                                <Award size={9} />
                              </Btn>
                              <Btn size="xs" variant="ghost" onClick={() => handleSetBeta(u._id, !u.isBetaUser)}
                                className={u.isBetaUser ? 'text-violet-400' : ''}>
                                <FlaskConical size={9} />
                              </Btn>
                              {u._id !== (user?._id || user?.id) && (
                                <Btn
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => handleDeleteUser(u._id, u.email)}
                                  className="text-red-400 hover:text-red-300"
                                  title={`Permanently delete ${u.email}`}
                                >
                                  <X size={9} />
                                </Btn>
                              )}
                            </div>
                          </Td>
                        </Tr>

                        {/* ── Expanded timeline panel ── */}
                        {selectedUser?._id === u._id && (
                          <tr>
                            <td colSpan={6} className="bg-[#0D1323] border-b border-blue-500/20 px-0 py-0">
                              <div className="p-4">
                                {/* User summary header */}
                                <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
                                  <div>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                      <History size={14} className="text-blue-400" />
                                      User Timeline — {u.name || u.email}
                                    </h3>
                                    <p className="text-[10px] text-zinc-600 mt-0.5">
                                      Unified activity across all verticals
                                    </p>
                                  </div>
                                  {userTimeline && (
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {/* Risk score */}
                                      <div className={`px-3 py-1.5 rounded-lg border text-center
                                        ${(userTimeline.riskScore || 0) >= 60 ? 'bg-red-500/15 border-red-500/30'
                                          : (userTimeline.riskScore || 0) >= 30 ? 'bg-amber-500/15 border-amber-500/30'
                                          : 'bg-emerald-500/10 border-emerald-500/20'
                                        }`}>
                                        <div className={`text-lg font-bold tabular-nums
                                          ${(userTimeline.riskScore || 0) >= 60 ? 'text-red-400'
                                            : (userTimeline.riskScore || 0) >= 30 ? 'text-amber-400'
                                            : 'text-emerald-400'
                                          }`}>
                                          {userTimeline.riskScore ?? 0}
                                        </div>
                                        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-600">
                                          Risk Score
                                        </div>
                                      </div>
                                      <div className="text-center">
                                        <div className="text-base font-bold text-blue-400 tabular-nums">{fmtN(userTimeline.totalTransactions)}</div>
                                        <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold">Transactions</div>
                                      </div>
                                      <div className="text-center">
                                        <div className={`text-base font-bold tabular-nums ${(userTimeline.refundCount || 0) > 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                                          {fmtN(userTimeline.refundCount || 0)}
                                        </div>
                                        <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold">Refunds</div>
                                      </div>
                                      <div className="text-center">
                                        <div className={`text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded
                                          ${(userTimeline.riskScore || 0) >= 60 ? 'bg-red-500/20 text-red-400'
                                            : (userTimeline.riskScore || 0) >= 30 ? 'bg-amber-500/20 text-amber-400'
                                            : 'bg-emerald-500/10 text-emerald-400'
                                          }`}>
                                          {(userTimeline.riskScore || 0) >= 60 ? 'HIGH RISK'
                                            : (userTimeline.riskScore || 0) >= 30 ? 'MED RISK'
                                            : 'LOW RISK'}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Timeline events */}
                                {timelineLoading ? (
                                  <div className="flex items-center justify-center py-8">
                                    <Loader2 size={18} className="animate-spin text-zinc-600" />
                                    <span className="ml-2 text-xs text-zinc-600">Loading timeline…</span>
                                  </div>
                                ) : userTimeline?.timeline?.length > 0 ? (
                                  <div className="max-h-72 overflow-y-auto">
                                    {userTimeline.timeline.map((ev, i) => (
                                      <TimelineEvent key={i} ev={ev} />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center py-8 text-zinc-600">
                                    <Clock size={24} className="opacity-30 mb-2" />
                                    <p className="text-xs">No activity recorded for this user</p>
                                  </div>
                                )}

                                {/* Internal Notes button — admin-private */}
                                <div className="mt-3 pt-3 border-t border-white/6 flex items-center justify-between">
                                  <span className="text-[10px] text-zinc-600 flex items-center gap-1">
                                    <StickyNote size={11} /> Admin-private notes are stored separately
                                  </span>
                                  <button
                                    onClick={() => {
                                      setSection('users');
                                      toast({ title: 'Internal Notes', description: 'Open the Internal Notes panel for this user from the dispute or user detail section.' });
                                    }}
                                    className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                                  >
                                    <StickyNote size={10} /> View Internal Notes →
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </Table>
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] text-zinc-600">
                <span>{usersData.total} users total</span>
                <div className="flex items-center gap-1.5">
                  <Btn size="xs" variant="ghost" disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)}>
                    <ChevronLeft size={11} />
                  </Btn>
                  <span>Page {usersPage} / {usersData.pages || 1}</span>
                  <Btn size="xs" variant="ghost" disabled={usersPage >= (usersData.pages || 1)} onClick={() => setUsersPage(p => p + 1)}>
                    <ChevronRight size={11} />
                  </Btn>
                </div>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              PROPERTIES
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'properties' && (
            <div className="space-y-4">
              <SectionHead title="Properties" sub={`${fmtN(propertiesData.total)} listings on platform`} />

              <div className="relative w-56">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  value={propSearch}
                  onChange={e => { setPropSearch(e.target.value); setPropPage(1); }}
                  placeholder="Search properties…"
                  className="pl-7 pr-3 py-1.5 w-full bg-[#111827] border border-white/10 rounded text-xs text-zinc-300 placeholder-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                />
              </div>

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                {loading['properties'] ? <Spin /> : (
                  <Table heads={['Property', 'Host', 'Location', 'Nightly Rate', 'Status', 'Insurance', '']}
                    empty={propertiesData.properties.length === 0 && <Empty icon={Home} msg="No properties found" />}>
                    {propertiesData.properties.map(p => (
                      <Tr key={p._id}>
                        <Td><div className="font-semibold text-zinc-200 max-w-[200px] truncate">{p.title || '—'}</div></Td>
                        <Td muted>{p.hostName || p.hostEmail || '—'}</Td>
                        <Td muted>{p.location?.city || p.location?.address || '—'}</Td>
                        <Td><span className="font-bold tabular-nums text-blue-400">{p.pricing?.nightlyRate ? `£${p.pricing.nightlyRate}` : '—'}</span></Td>
                        <Td><Badge status={p.status} /></Td>
                        <Td><span className="text-[10px] text-zinc-400">{p.insurance?.hasInsurance ? 'On file' : 'Not recorded'}</span></Td>
                        <Td>
                          <a href={`/property/${p._id}`} target="_blank" rel="noreferrer"
                            className="text-[10px] text-zinc-500 hover:text-blue-400 flex items-center gap-1 transition-colors">
                            <Eye size={11} /> View
                          </a>
                        </Td>
                      </Tr>
                    ))}
                  </Table>
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] text-zinc-600">
                <span>{propertiesData.total} properties</span>
                <div className="flex items-center gap-1.5">
                  <Btn size="xs" variant="ghost" disabled={propPage <= 1} onClick={() => setPropPage(p => p - 1)}><ChevronLeft size={11} /></Btn>
                  <span>Page {propPage} / {propertiesData.pages || 1}</span>
                  <Btn size="xs" variant="ghost" disabled={propPage >= (propertiesData.pages || 1)} onClick={() => setPropPage(p => p + 1)}><ChevronRight size={11} /></Btn>
                </div>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              BOOKINGS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'bookings' && (
            <div className="space-y-4">
              <SectionHead title="Bookings" sub={`${fmtN(bookingsData.total)} total bookings`} />

              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input value={bookSearch} onChange={e => { setBookSearch(e.target.value); setBookPage(1); }}
                    placeholder="Search…"
                    className="pl-7 pr-3 py-1.5 bg-[#111827] border border-white/10 rounded text-xs text-zinc-300 placeholder-zinc-700 focus:outline-none w-56" />
                </div>
                <select value={bookStatus} onChange={e => { setBookStatus(e.target.value); setBookPage(1); }}
                  className="bg-[#111827] border border-white/10 rounded text-xs text-zinc-400 px-2.5 py-1.5 focus:outline-none">
                  <option value="">All statuses</option>
                  {['pending','confirmed','cancelled','completed','pending_payment'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                {loading['bookings'] ? <Spin /> : (
                  <Table heads={['Ref', 'Guest', 'Property', 'Check-in → Check-out', 'Amount', 'Status']}
                    empty={bookingsData.bookings.length === 0 && <Empty icon={CalendarDays} msg="No bookings found" />}>
                    {bookingsData.bookings.map(b => (
                      <Tr key={b._id}>
                        <Td mono muted>#{b._id?.slice(-6)}</Td>
                        <Td>
                          <div className="font-semibold text-zinc-200">{b.guestName || '—'}</div>
                          <div className="text-[10px] text-zinc-600">{b.guestEmail || ''}</div>
                        </Td>
                        <Td><div className="max-w-[160px] truncate text-zinc-300">{b.propertyTitle || '—'}</div></Td>
                        <Td muted mono>{b.checkIn} → {b.checkOut}</Td>
                        <Td><span className="font-bold tabular-nums text-blue-400">{fmt(b.totalAmount || b.pricing?.total)}</span></Td>
                        <Td><Badge status={b.status} /></Td>
                      </Tr>
                    ))}
                  </Table>
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] text-zinc-600">
                <span>{bookingsData.total} bookings</span>
                <div className="flex items-center gap-1.5">
                  <Btn size="xs" variant="ghost" disabled={bookPage <= 1} onClick={() => setBookPage(p => p - 1)}><ChevronLeft size={11} /></Btn>
                  <span>Page {bookPage} / {bookingsData.pages || 1}</span>
                  <Btn size="xs" variant="ghost" disabled={bookPage >= (bookingsData.pages || 1)} onClick={() => setBookPage(p => p + 1)}><ChevronRight size={11} /></Btn>
                </div>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              MARKETPLACE
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'marketplace' && (
            <div className="space-y-4">
              <SectionHead title="Marketplace & Deals" sub="R2R deal listings and host offers between landlords and hosts" />

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex bg-[#111827] border border-white/10 rounded overflow-hidden">
                  {['listings', 'offers'].map(t => (
                    <button key={t} onClick={() => setMlTab(t)}
                      className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors capitalize
                        ${mlTab === t ? 'bg-[#1F3A68] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
                      {t} ({t === 'listings' ? mlData.total : moData.total})
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input value={mlSearch} onChange={e => setMlSearch(e.target.value)} placeholder="Search…"
                    className="pl-7 pr-3 py-1.5 bg-[#111827] border border-white/10 rounded text-xs text-zinc-300 placeholder-zinc-700 focus:outline-none w-48" />
                </div>
              </div>

              {loading['marketplace'] ? <Spin /> : (
                <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                  {mlTab === 'listings' && (
                    <Table heads={['Address', 'Landlord', 'Asking Rent', 'Beds', 'Status']}
                      empty={mlData.listings.length === 0 && <Empty icon={Handshake} msg="No listings found" />}>
                      {mlData.listings.map(l => (
                        <Tr key={l._id}>
                          <Td><div className="font-semibold text-zinc-200">{l.address || l.title || '—'}</div></Td>
                          <Td muted>{l.landlordName || '—'}</Td>
                          <Td><span className="font-bold tabular-nums text-blue-400">{l.askingRent ? `£${l.askingRent}/mo` : '—'}</span></Td>
                          <Td muted>{l.bedrooms || '—'}</Td>
                          <Td><Badge status={l.status} /></Td>
                        </Tr>
                      ))}
                    </Table>
                  )}
                  {mlTab === 'offers' && (
                    <Table heads={['Property', 'Host', 'Landlord', 'Proposed Rent', 'Status']}
                      empty={moData.offers.length === 0 && <Empty icon={Handshake} msg="No offers found" />}>
                      {moData.offers.map(o => (
                        <Tr key={o._id}>
                          <Td><div className="font-semibold text-zinc-200">{o.listingAddress || '—'}</div></Td>
                          <Td muted>{o.hostName || '—'}</Td>
                          <Td muted>{o.landlordName || '—'}</Td>
                          <Td><span className="font-bold tabular-nums text-blue-400">{o.proposedRent ? `£${o.proposedRent}/mo` : '—'}</span></Td>
                          <Td><Badge status={o.status} /></Td>
                        </Tr>
                      ))}
                    </Table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              RESTAURANTS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'restaurants' && (
            <div className="space-y-4">
              <SectionHead title="Restaurants" sub="Restaurant partner roster, FHRS ratings, fulfilment mode" />

              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Registered" value={fmtN(restaurantRosterRegistered.length)} icon={Utensils} />
                <Kpi label="Onboarding" value={fmtN(restaurantRosterLimbo.length + restaurantRosterRegistered.filter(r => !['active','banned','paused'].includes(r.status)).length)} icon={Clock} />
                <Kpi label="Accepting Orders" value={fmtN(restaurantRosterRegistered.filter(r => r.isAcceptingOrders).length)} icon={CheckCircle} />
              </div>

              {loading['restaurants-roster'] ? <Spin /> : (
                <>
                  <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                      <span className="text-xs font-bold text-zinc-300">Registered Restaurants</span>
                      <span className="text-[10px] text-zinc-600">{restaurantRosterRegistered.length} total</span>
                    </div>
                    {restaurantRosterRegistered.length > 0 ? (
                      <Table heads={['Restaurant', 'City', 'FHRS', 'Mode', 'Status', 'Delivery Live', 'Orders', 'Joined']}>
                        {restaurantRosterRegistered.map(r => {
                          const tone =
                            r.status === 'active' ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/25' :
                            r.status === 'banned' ? 'bg-red-500/15 text-red-300 ring-red-500/25' :
                            r.status === 'paused' ? 'bg-amber-500/15 text-amber-300 ring-amber-500/25' :
                                                    'bg-blue-500/15 text-blue-300 ring-blue-500/25';
                          const fhrs = r.fhrsRating;
                          const fhrsTone =
                            fhrs == null ? 'text-zinc-600' :
                            fhrs >= 4    ? 'text-emerald-400' :
                            fhrs >= 2    ? 'text-amber-400'   :
                                           'text-red-400';
                          const usesPlatformDelivery = r.fulfillmentMode === 'platform_delivery';
                          return (
                            <Tr key={r.id}>
                              <Td>
                                <div className="font-semibold text-zinc-200">{r.restaurantName || '—'}</div>
                                <div className="text-[10px] text-zinc-600 font-mono">{r.email}</div>
                              </Td>
                              <Td muted>{r.city || '—'}</Td>
                              <Td><span className={`font-bold ${fhrsTone}`}>{fhrs ?? '—'}</span></Td>
                              <Td muted>{(r.fulfillmentMode || '—').replace('_', ' ')}</Td>
                              <Td>
                                <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${tone}`}>
                                  {(r.status || 'pending').replace('_', ' ')}
                                </span>
                              </Td>
                              <Td>
                                {usesPlatformDelivery ? (
                                  <Btn
                                    size="xs"
                                    variant={r.platformDeliveryReady ? 'success' : 'ghost'}
                                    onClick={async () => {
                                      try {
                                        await adminAPI.setRestaurantPlatformDeliveryReady(r.id, !r.platformDeliveryReady, getAuthToken());
                                        toast({ title: r.platformDeliveryReady ? 'Set to Coming Soon' : 'Flipped Live' });
                                        setRefreshKey(k => k + 1);
                                      } catch {
                                        toast({ title: 'Could not update', variant: 'destructive' });
                                      }
                                    }}
                                  >
                                    {r.platformDeliveryReady ? 'Live' : 'Coming Soon'}
                                  </Btn>
                                ) : (
                                  <span className="text-[10px] text-zinc-600">n/a (self)</span>
                                )}
                              </Td>
                              <Td mono>{fmtN(r.totalOrders || 0)}</Td>
                              <Td muted mono>{fmtDate(r.createdAt)}</Td>
                            </Tr>
                          );
                        })}
                      </Table>
                    ) : (
                      <Empty icon={Utensils} msg="No registered restaurants yet" />
                    )}
                  </div>

                  {restaurantRosterLimbo.length > 0 && (
                    <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-amber-500/15 flex items-center gap-2">
                        <AlertTriangle size={13} className="text-amber-400" />
                        <span className="text-xs font-bold text-amber-300">Added restaurant role but never completed onboarding</span>
                        <span className="text-[10px] text-amber-400/70 ml-auto">{restaurantRosterLimbo.length} user{restaurantRosterLimbo.length !== 1 ? 's' : ''}</span>
                      </div>
                      <Table heads={['User', 'Email', 'Phone', 'Joined']}>
                        {restaurantRosterLimbo.map(u => (
                          <Tr key={u.userId}>
                            <Td><div className="font-semibold text-zinc-200">{u.name || '—'}</div></Td>
                            <Td muted mono>{u.email}</Td>
                            <Td muted>{u.phone || '—'}</Td>
                            <Td muted mono>{fmtDate(u.createdAt)}</Td>
                          </Tr>
                        ))}
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              TAXI DRIVERS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'drivers' && (() => {
            const taxiItems = flaggedRiders.filter(r =>
              (r.vertical || r.role || r.type || '').toLowerCase().includes('driv')
              || (r.vertical || r.role || r.type || '').toLowerCase().includes('taxi')
              || (r.vertical || r.role || r.type || '').toLowerCase().includes('phv')
            );
            const taxiIncidents = incidents.filter(i =>
              (i.vertical || i.category || i.type || '').toLowerCase().includes('driv')
              || (i.vertical || i.category || i.type || '').toLowerCase().includes('taxi')
              || (i.vertical || i.category || i.type || '').toLowerCase().includes('ride')
            );
            return (
              <div className="space-y-4">
                <SectionHead title="Taxi Drivers" sub="Private hire / PHV drivers — launch pending TfL Operator Licence" />

                {!controls.ridesEnabled && (
                  <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg px-4 py-3 flex items-start gap-2.5">
                    <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-[11px] text-zinc-400 leading-relaxed">
                      <span className="text-amber-300 font-semibold">Taxi vertical disabled. </span>
                      Ride requests are blocked platform-wide until the TfL PHV Operator Licence is active. Enable the <span className="text-zinc-200 font-semibold">Rides &amp; Transport</span> switch in System Controls when ready.
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3">
                  <Kpi label="Registered"       value={fmtN(driverRosterRegistered.length)}                                icon={Car} />
                  <Kpi label="Onboarding"       value={fmtN(driverRosterLimbo.length + driverRosterRegistered.filter(r => !['active','banned','paused','payout_frozen'].includes(r.status)).length)} icon={Clock} />
                  <Kpi label="Flagged Drivers"  value={fmtN(taxiItems.length)}                                            icon={AlertTriangle} danger={taxiItems.length > 0} />
                  <Kpi label="Open Incidents"   value={fmtN(taxiIncidents.filter(i => i.status === 'open').length)}       icon={ShieldAlert}   danger={taxiIncidents.filter(i => i.status === 'open').length > 0} />
                </div>

                {loading['risk'] ? <Spin /> : (
                  <>
                    <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                        <span className="text-xs font-bold text-zinc-300">Registered Taxi Drivers</span>
                        <span className="text-[10px] text-zinc-600">{driverRosterRegistered.length} total</span>
                      </div>
                      {driverRosterRegistered.length > 0 ? (
                        <Table heads={['Driver', 'Vehicle', 'City', 'Service', 'Status', 'Rides', 'Joined']}>
                          {driverRosterRegistered.map(r => {
                            const tone =
                              r.status === 'active'        ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/25' :
                              r.status === 'banned'        ? 'bg-red-500/15 text-red-300 ring-red-500/25' :
                              r.status === 'paused'        ? 'bg-amber-500/15 text-amber-300 ring-amber-500/25' :
                              r.status === 'payout_frozen' ? 'bg-amber-500/15 text-amber-300 ring-amber-500/25' :
                                                             'bg-blue-500/15 text-blue-300 ring-blue-500/25';
                            return (
                              <Tr key={r.id}>
                                <Td>
                                  <div className="font-semibold text-zinc-200">{r.name || '—'}</div>
                                  <div className="text-[10px] text-zinc-600 font-mono">{r.email}</div>
                                </Td>
                                <Td muted>
                                  {r.vehicleType || '—'}
                                  {r.plate && <div className="text-[10px] text-zinc-600 font-mono">{r.plate}</div>}
                                </Td>
                                <Td muted>{r.city || '—'}</Td>
                                <Td muted>{(r.serviceTypes || []).join(', ') || '—'}</Td>
                                <Td>
                                  <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${tone}`}>
                                    {(r.status || 'pending').replace('_', ' ')}
                                  </span>
                                </Td>
                                <Td mono>{fmtN(r.totalRides || 0)}</Td>
                                <Td muted mono>{fmtDate(r.createdAt)}</Td>
                              </Tr>
                            );
                          })}
                        </Table>
                      ) : (
                        <Empty icon={Car} msg="No registered taxi drivers yet" />
                      )}
                    </div>

                    {driverRosterLimbo.length > 0 && (
                      <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 border-b border-amber-500/15 flex items-center gap-2">
                          <AlertTriangle size={13} className="text-amber-400" />
                          <span className="text-xs font-bold text-amber-300">Added driver role but never completed onboarding</span>
                          <span className="text-[10px] text-amber-400/70 ml-auto">{driverRosterLimbo.length} user{driverRosterLimbo.length !== 1 ? 's' : ''}</span>
                        </div>
                        <Table heads={['User', 'Email', 'Phone', 'Joined']}>
                          {driverRosterLimbo.map(u => (
                            <Tr key={u.userId}>
                              <Td><div className="font-semibold text-zinc-200">{u.name || '—'}</div></Td>
                              <Td muted mono>{u.email}</Td>
                              <Td muted>{u.phone || '—'}</Td>
                              <Td muted mono>{fmtDate(u.createdAt)}</Td>
                            </Tr>
                          ))}
                        </Table>
                      </div>
                    )}

                    <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6">
                        <span className="text-xs font-bold text-zinc-300">Flagged Taxi Drivers</span>
                      </div>
                      {taxiItems.length > 0 ? (
                        <Table heads={['Driver', 'Reason', 'Risk Score', '']}>
                          {taxiItems.map((r, i) => (
                            <Tr key={i}>
                              <Td><div className="font-semibold text-zinc-200">{r.name || r.riderId || r.driverId}</div></Td>
                              <Td muted>{r.reason || '—'}</Td>
                              <Td danger>{r.riskScore ?? '—'}</Td>
                              <Td>
                                <button onClick={() => setSection('risk')} className="text-[10px] text-blue-400 hover:text-blue-300">
                                  View in Risk →
                                </button>
                              </Td>
                            </Tr>
                          ))}
                        </Table>
                      ) : (
                        <Empty icon={Car} msg="No flagged taxi drivers — all clear" />
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              DELIVERY RIDERS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'delivery-riders' && (() => {
            const deliveryItems = flaggedRiders.filter(r => {
              const v = (r.vertical || r.role || r.type || '').toLowerCase();
              if (!v) return true; // uncategorised → show here by default
              return v.includes('deliv') || v.includes('rider') || v.includes('food');
            });
            const deliveryIncidents = incidents.filter(i => {
              const v = (i.vertical || i.category || i.type || '').toLowerCase();
              if (!v) return true;
              return v.includes('deliv') || v.includes('food') || v.includes('rider');
            });
            return (
              <div className="space-y-4">
                <SectionHead title="Delivery Riders" sub="Food-delivery rider profiles, flagged riders, and incident reports" />

                {!controls.deliveryEnabled && (
                  <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg px-4 py-3 flex items-start gap-2.5">
                    <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-[11px] text-zinc-400 leading-relaxed">
                      <span className="text-amber-300 font-semibold">Delivery vertical disabled. </span>
                      New deliveries are blocked. Re-enable <span className="text-zinc-200 font-semibold">Delivery</span> in System Controls to resume dispatch.
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3">
                  <Kpi label="Registered"       value={fmtN(deliveryRosterRegistered.length)}                                  icon={Truck} />
                  <Kpi label="Onboarding"       value={fmtN(deliveryRosterLimbo.length + deliveryRosterRegistered.filter(r => r.status !== 'active' && r.status !== 'banned' && r.status !== 'paused' && r.status !== 'payout_frozen').length)} icon={Clock} />
                  <Kpi label="Flagged Riders"   value={fmtN(deliveryItems.length)}                                             icon={AlertTriangle} danger={deliveryItems.length > 0} />
                  <Kpi label="Open Incidents"   value={fmtN(deliveryIncidents.filter(i => i.status === 'open').length)}        icon={ShieldAlert}   danger={deliveryIncidents.filter(i => i.status === 'open').length > 0} />
                </div>

                {loading['risk'] ? <Spin /> : (
                  <>
                    {/* All registered riders — every row in delivery_rider_profiles */}
                    <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                        <span className="text-xs font-bold text-zinc-300">Registered Delivery Riders</span>
                        <span className="text-[10px] text-zinc-600">{deliveryRosterRegistered.length} total</span>
                      </div>
                      {deliveryRosterRegistered.length > 0 ? (
                        <Table heads={['Rider', 'Vehicle', 'City', 'Status', 'Deliveries', 'Joined']}>
                          {deliveryRosterRegistered.map(r => {
                            const tone =
                              r.status === 'active'         ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/25' :
                              r.status === 'banned'         ? 'bg-red-500/15 text-red-300 ring-red-500/25' :
                              r.status === 'paused'         ? 'bg-amber-500/15 text-amber-300 ring-amber-500/25' :
                              r.status === 'payout_frozen'  ? 'bg-amber-500/15 text-amber-300 ring-amber-500/25' :
                                                              'bg-blue-500/15 text-blue-300 ring-blue-500/25';
                            return (
                              <Tr key={r.id}>
                                <Td>
                                  <div className="font-semibold text-zinc-200">{r.name || '—'}</div>
                                  <div className="text-[10px] text-zinc-600 font-mono">{r.email}</div>
                                </Td>
                                <Td muted>
                                  {(r.deliveryMethod || '—').replace('_', ' ')}
                                  {r.vehicleMakeModel && <div className="text-[10px] text-zinc-600">{r.vehicleMakeModel}</div>}
                                  {r.registrationPlate && <div className="text-[10px] text-zinc-600 font-mono">{r.registrationPlate}</div>}
                                </Td>
                                <Td muted>{r.city || '—'}</Td>
                                <Td>
                                  <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${tone}`}>
                                    {(r.status || 'pending').replace('_', ' ')}
                                  </span>
                                </Td>
                                <Td mono>{fmtN(r.totalDeliveries || 0)}</Td>
                                <Td muted mono>{fmtDate(r.createdAt)}</Td>
                              </Tr>
                            );
                          })}
                        </Table>
                      ) : (
                        <Empty icon={Truck} msg="No registered delivery riders yet" />
                      )}
                    </div>

                    {/* Limbo state — has the role but never completed the
                        compliance form. Flag visibly so the founder can
                        nudge them through onboarding. */}
                    {deliveryRosterLimbo.length > 0 && (
                      <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 border-b border-amber-500/15 flex items-center gap-2">
                          <AlertTriangle size={13} className="text-amber-400" />
                          <span className="text-xs font-bold text-amber-300">Added rider role but never completed onboarding</span>
                          <span className="text-[10px] text-amber-400/70 ml-auto">{deliveryRosterLimbo.length} user{deliveryRosterLimbo.length !== 1 ? 's' : ''}</span>
                        </div>
                        <Table heads={['User', 'Email', 'Phone', 'Joined']}>
                          {deliveryRosterLimbo.map(u => (
                            <Tr key={u.userId}>
                              <Td><div className="font-semibold text-zinc-200">{u.name || '—'}</div></Td>
                              <Td muted mono>{u.email}</Td>
                              <Td muted>{u.phone || '—'}</Td>
                              <Td muted mono>{fmtDate(u.createdAt)}</Td>
                            </Tr>
                          ))}
                        </Table>
                      </div>
                    )}

                    {/* Flagged riders — preserved from before for safety triage */}
                    <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6">
                        <span className="text-xs font-bold text-zinc-300">Flagged Delivery Riders</span>
                      </div>
                      {deliveryItems.length > 0 ? (
                        <Table heads={['Rider', 'Reason', 'Risk Score', '']}>
                          {deliveryItems.map((r, i) => (
                            <Tr key={i}>
                              <Td><div className="font-semibold text-zinc-200">{r.name || r.riderId}</div></Td>
                              <Td muted>{r.reason || '—'}</Td>
                              <Td danger>{r.riskScore ?? '—'}</Td>
                              <Td>
                                <button onClick={() => setSection('risk')} className="text-[10px] text-blue-400 hover:text-blue-300">
                                  View in Risk →
                                </button>
                              </Td>
                            </Tr>
                          ))}
                        </Table>
                      ) : (
                        <Empty icon={Truck} msg="No flagged delivery riders — all clear" />
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              SERVICE PROVIDERS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'providers' && (
            <div className="space-y-4">
              <SectionHead title="Service Providers" sub="Provider roster with compliance status (PLI, DBS, Right to Work)" />

              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Registered" value={fmtN(providerRosterRegistered.length)} icon={Wrench} />
                <Kpi label="Onboarding" value={fmtN(providerRosterLimbo.length + providerRosterRegistered.filter(r => !['active','banned','paused'].includes(r.status)).length)} icon={Clock} />
                <Kpi label="Public Profile" value={fmtN(providerRosterRegistered.filter(r => r.publicProfile).length)} icon={CheckCircle} />
              </div>

              {loading['providers-roster'] ? <Spin /> : (
                <>
                  <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                      <span className="text-xs font-bold text-zinc-300">Registered Service Providers</span>
                      <span className="text-[10px] text-zinc-600">{providerRosterRegistered.length} total</span>
                    </div>
                    {providerRosterRegistered.length > 0 ? (
                      <Table heads={['Provider', 'Business', 'City', 'Compliance', 'Status', 'Reviews', 'Joined']}>
                        {providerRosterRegistered.map(r => {
                          const tone =
                            r.status === 'active' ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/25' :
                            r.status === 'banned' ? 'bg-red-500/15 text-red-300 ring-red-500/25' :
                            r.status === 'paused' ? 'bg-amber-500/15 text-amber-300 ring-amber-500/25' :
                                                    'bg-blue-500/15 text-blue-300 ring-blue-500/25';
                          const dot = (ok) => ok
                            ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            : <span className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-700" />;
                          return (
                            <Tr key={r.id}>
                              <Td>
                                <div className="font-semibold text-zinc-200">{r.name || '—'}</div>
                                <div className="text-[10px] text-zinc-600 font-mono">{r.email}</div>
                              </Td>
                              <Td muted>{r.businessName || '—'}</Td>
                              <Td muted>{r.city || '—'}</Td>
                              <Td>
                                <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                                  <span className="flex items-center gap-1" title="Public Liability Insurance">{dot(r.pliVerified)} PLI</span>
                                  <span className="flex items-center gap-1" title="DBS Check">{dot(r.dbsVerified)} DBS</span>
                                  <span className="flex items-center gap-1" title="Right to Work">{dot(r.rtwVerified)} RTW</span>
                                </div>
                              </Td>
                              <Td>
                                <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${tone}`}>
                                  {(r.status || 'pending').replace('_', ' ')}
                                </span>
                              </Td>
                              <Td mono>{fmtN(r.totalReviews || 0)}</Td>
                              <Td muted mono>{fmtDate(r.createdAt)}</Td>
                            </Tr>
                          );
                        })}
                      </Table>
                    ) : (
                      <Empty icon={Wrench} msg="No registered service providers yet" />
                    )}
                  </div>

                  {providerRosterLimbo.length > 0 && (
                    <div className="bg-amber-500/5 border border-amber-500/25 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-amber-500/15 flex items-center gap-2">
                        <AlertTriangle size={13} className="text-amber-400" />
                        <span className="text-xs font-bold text-amber-300">Added provider role but never completed onboarding</span>
                        <span className="text-[10px] text-amber-400/70 ml-auto">{providerRosterLimbo.length} user{providerRosterLimbo.length !== 1 ? 's' : ''}</span>
                      </div>
                      <Table heads={['User', 'Email', 'Phone', 'Joined']}>
                        {providerRosterLimbo.map(u => (
                          <Tr key={u.userId}>
                            <Td><div className="font-semibold text-zinc-200">{u.name || '—'}</div></Td>
                            <Td muted mono>{u.email}</Td>
                            <Td muted>{u.phone || '—'}</Td>
                            <Td muted mono>{fmtDate(u.createdAt)}</Td>
                          </Tr>
                        ))}
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              FINANCIAL
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'financial' && (
            <div className="space-y-5">
              <SectionHead
                title="Financial Overview"
                sub={`Platform-wide financial data · ${period}`}
                action={balanceCheck && (
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-bold uppercase tracking-wider
                    ${balanceCheck.balanced
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                      : 'bg-red-500/15 border-red-500/30 text-red-400 animate-pulse'
                    }`}>
                    {balanceCheck.balanced
                      ? <><CheckCircle size={11} /> Ledger Balanced</>
                      : <><AlertTriangle size={11} /> Δ £{Math.abs(balanceCheck.delta).toFixed(2)} — Leaking Ledger</>
                    }
                  </div>
                )}
              />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="GMV"              value={fmt(financialOverview?.gmv)}              icon={TrendingUp} />
                <Kpi label="Platform Revenue" value={fmt(financialOverview?.platformRevenue)}  icon={DollarSign} />
                <Kpi label="Host Earnings"    value={fmt(financialOverview?.hostEarnings)}     icon={Home}       />
                <Kpi label="VAT Collected"    value={fmt(financialOverview?.vatCollected)}     icon={Receipt}    />
              </div>

              {/* Monthly bar chart */}
              <div className="bg-[#111827] border border-white/6 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold text-zinc-300">Monthly Revenue Breakdown</span>
                </div>
                {monthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={monthlyTrend} barGap={3}>
                      <CartesianGrid strokeDasharray="4 4" stroke="#27272a" />
                      <XAxis dataKey="month" tick={chartStyle.tick} axisLine={false} tickLine={false} />
                      <YAxis tick={chartStyle.tick} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                      <Tooltip {...chartStyle.tooltip} formatter={v => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 10, color: '#71717a' }} />
                      <Bar dataKey="gmv"     name="GMV"     fill="#1F3A68" radius={[3,3,0,0]} />
                      <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-52 flex items-center justify-center text-zinc-700 text-xs">No data for period</div>}
              </div>

              {/* Commission + earners */}
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/6">
                    <span className="text-xs font-bold text-zinc-300">Commission Audit</span>
                  </div>
                  {commissionAudit?.audit?.length > 0 ? (
                    <Table heads={['Role', 'Count', 'Total Commissions']}>
                      {commissionAudit.audit.map((row, i) => (
                        <Tr key={i}>
                          <Td><span className="capitalize">{row._id || row.role}</span></Td>
                          <Td muted>{row.count}</Td>
                          <Td><span className="font-bold tabular-nums text-blue-400">{fmt(row.totalCommissions)}</span></Td>
                        </Tr>
                      ))}
                    </Table>
                  ) : <Empty icon={BarChart3} msg="No commission data for period" />}
                </div>

                <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-300">Top Earners</span>
                    <select
                      value={earnerRole}
                      onChange={e => setEarnerRole(e.target.value)}
                      className="bg-white/6 border border-white/10 text-zinc-400 rounded text-[10px] px-2 py-1 focus:outline-none"
                    >
                      {['host','driver','delivery_rider','restaurant_partner'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  {topEarners?.earners?.length > 0 ? (
                    <div className="p-3 space-y-2">
                      {topEarners.earners.slice(0, 8).map((e, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <span className="w-5 h-5 rounded bg-[#1F3A68]/60 text-blue-300 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-xs text-zinc-300 flex-1 truncate">{e.name || e._id}</span>
                          <span className="text-xs font-bold tabular-nums text-blue-400">{fmt(e.earnings)}</span>
                        </div>
                      ))}
                    </div>
                  ) : <Empty icon={Star} msg="No earner data" />}
                </div>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              PAYOUTS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'payouts' && (
            <div className="space-y-4">
              <SectionHead
                title="Payouts"
                sub="Weekly automated payouts to hosts, drivers, riders, and restaurant partners"
                action={
                  <Btn onClick={handlePreflightCheck} disabled={triggeringPayout || preflightLoading} variant="primary">
                    {(triggeringPayout || preflightLoading) ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />}
                    Pre-Flight Check & Payout
                  </Btn>
                }
              />

              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Connected Accounts"  value={fmtN(connectStatus?.total)}       icon={CreditCard}  />
                <Kpi label="Pending Onboarding"  value={fmtN(connectStatus?.pendingCount)} icon={AlertTriangle} danger={connectStatus?.pendingCount > 0} />
                <Kpi label="Total Payout Runs"   value={fmtN(payoutRuns.length)}           icon={Receipt}     />
              </div>

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6">
                  <span className="text-xs font-bold text-zinc-300">Payout Run History</span>
                </div>
                {loading['payouts'] ? <Spin /> : (
                  <Table heads={['Date', 'Recipients', 'Total Paid', 'Status']}
                    empty={payoutRuns.length === 0 && <Empty icon={CreditCard} msg="No payout runs yet" />}>
                    {payoutRuns.map((run, i) => (
                      <Tr key={i}>
                        <Td mono muted>{fmtDate(run.weekOf || run.createdAt)}</Td>
                        <Td muted>{run.recipientCount ?? '—'}</Td>
                        <Td><span className="font-bold tabular-nums text-blue-400">{fmt(run.totalPaid)}</span></Td>
                        <Td><Badge status={run.status || 'completed'} /></Td>
                      </Tr>
                    ))}
                  </Table>
                )}
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              SUBSCRIPTIONS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'subscriptions' && (
            <div className="space-y-4">
              <SectionHead title="Subscriptions" sub="Subscription plans across all platform users" />

              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Active Subscriptions" value={fmtN(subscriptions?.active)}  icon={CheckCircle} />
                <Kpi label="MRR"                   value={fmt(subscriptions?.mrr)}      icon={DollarSign} />
                <Kpi label="Churned (Period)"       value={fmtN(subscriptions?.churned)} icon={XCircle} danger={subscriptions?.churned > 0} />
              </div>

              <div className="flex gap-2">
                {['active','cancelled','trialing'].map(s => (
                  <button key={s} onClick={() => setSubStatus(s)}
                    className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest transition-colors capitalize
                      ${subStatus === s ? 'bg-[#1F3A68] text-white' : 'bg-[#111827] border border-white/10 text-zinc-500 hover:text-zinc-300'}`}>
                    {s}
                  </button>
                ))}
              </div>

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                {loading['subscriptions'] ? <Spin /> : (
                  <Table heads={['User', 'Plan', 'Amount', 'Started', 'Status']}
                    empty={subsList.length === 0 && <Empty icon={Receipt} msg={`No ${subStatus} subscriptions`} />}>
                    {subsList.map((s, i) => (
                      <Tr key={i}>
                        <Td><div className="font-semibold text-zinc-200">{s.userName || s.userId}</div></Td>
                        <Td muted>{s.plan}</Td>
                        <Td><span className="font-bold tabular-nums text-blue-400">{fmt(s.amount)}/mo</span></Td>
                        <Td mono muted>{fmtDate(s.startDate)}</Td>
                        <Td><Badge status={s.status} /></Td>
                      </Tr>
                    ))}
                  </Table>
                )}
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              RISK & SAFETY
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'risk' && (
            <div className="space-y-4">
              <SectionHead title="Risk & Safety" sub="Fraud signals, incidents, and flagged users" />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Refund Rate"    value={fmtPct(riskData?.refundRate)}           icon={TrendingDown} danger />
                <Kpi label="Open Disputes"  value={fmtN(riskData?.disputeCount)}            icon={Scale}        danger={riskData?.disputeCount > 0} />
                <Kpi label="Chargebacks"    value={fmtN(riskData?.chargebackCount)}         icon={AlertTriangle} danger={riskData?.chargebackCount > 0} />
                <Kpi label="Flagged Users"  value={fmtN(riskData?.flaggedUsers?.length)}   icon={Ban}          danger={riskData?.flaggedUsers?.length > 0} />
              </div>

              {/* Incidents */}
              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center justify-between">
                  <span className="text-xs font-bold text-zinc-300">Incidents</span>
                  <select value={incidentStatus} onChange={e => setIncidentStatus(e.target.value)}
                    className="bg-white/6 border border-white/10 text-zinc-400 rounded text-[10px] px-2 py-1 focus:outline-none">
                    <option value="">All</option>
                    <option value="open">Open</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                {loading['risk'] ? <Spin /> : (
                  <Table heads={['Rider', 'Type', 'Severity', 'Date', 'Status']}
                    empty={incidents.length === 0 && <Empty icon={ShieldAlert} msg="No incidents" />}>
                    {incidents.map((inc, i) => (
                      <Tr key={i}>
                        <Td><div className="font-semibold text-zinc-200">{inc.riderName || inc.riderId}</div></Td>
                        <Td muted>{inc.type}</Td>
                        <Td>
                          <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded
                            ${inc.severity === 'high' ? 'bg-red-500/15 text-red-400'
                            : inc.severity === 'medium' ? 'bg-amber-500/15 text-amber-400'
                            : 'bg-zinc-500/15 text-zinc-400'}`}>
                            {inc.severity}
                          </span>
                        </Td>
                        <Td mono muted>{fmtDate(inc.createdAt)}</Td>
                        <Td><Badge status={inc.status} /></Td>
                      </Tr>
                    ))}
                  </Table>
                )}
              </div>

              {/* Flagged users */}
              {riskData?.flaggedUsers?.length > 0 && (
                <div className="bg-[#111827] border border-red-500/20 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                    <AlertTriangle size={12} className="text-red-400" />
                    <span className="text-xs font-bold text-red-400">Flagged Users — High Refund Activity</span>
                  </div>
                  <Table heads={['User', 'Email', 'Refund Count', 'Total Refunded']}>
                    {riskData.flaggedUsers.map((u, i) => (
                      <Tr key={i}>
                        <Td><div className="font-semibold text-zinc-200">{u.name || u._id}</div></Td>
                        <Td mono muted>{u.email || '—'}</Td>
                        <Td danger>{u.refundCount}</Td>
                        <Td danger>{fmt(u.totalRefunded)}</Td>
                      </Tr>
                    ))}
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              DISPUTES
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'disputes' && (
            <div className="space-y-4">
              <SectionHead
                title="Dispute Management"
                sub="Light-touch arbitration — admin facilitates, Host retains refund authority"
              />

              {/* Model explanation banner */}
              <div className="flex items-start gap-3 bg-[#111827] border border-white/6 rounded-lg p-3">
                <Scale size={13} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-zinc-500 leading-relaxed">
                  <span className="text-zinc-300 font-semibold">Neutral Observer Model: </span>
                  Ariv acts as a payment facilitator, not an arbitrator.
                  "Initiate Refund" sends a request to the Host — the Host approves or contests.
                  Unresolved disputes can be marked <span className="text-red-400 font-semibold">Off-Platform Legal</span> for Small Claims Court referral.
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                {['', 'open', 'under_review', 'awaiting_host_consent', 'off_platform_legal', 'closed'].map(f => (
                  <button key={f} onClick={() => { setDisputeFilter(f); setRefreshKey(k => k + 1); }}
                    className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest transition-colors
                      ${disputeFilter === f ? 'bg-[#1F3A68] text-white' : 'bg-[#111827] border border-white/10 text-zinc-500 hover:text-zinc-300'}`}>
                    {f ? f.replace(/_/g, ' ') : 'All'}
                  </button>
                ))}
              </div>

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                {loading['disputes'] ? <Spin /> : (
                  <Table
                    heads={['Ref', 'Complainant', 'Type', 'Reason', 'Amount', 'Escrow', 'Status', 'Actions']}
                    empty={disputes.length === 0 && <Empty icon={Scale} msg="No disputes found" />}
                  >
                    {disputes.map((d, i) => {
                      const dispId = d._id || d.orderId;
                      const isActioning = actioningDispute?.startsWith(dispId);
                      return (
                        <Tr key={i}>
                          <Td mono muted>#{(d.orderId || d._id)?.slice(-6)?.toUpperCase()}</Td>
                          <Td>
                            <div className="font-semibold text-zinc-200">{d.complainantName || '—'}</div>
                            <div className="text-[10px] text-zinc-600 mt-0.5">{d.relatedType || 'booking'}</div>
                          </Td>
                          <Td muted>
                            <span className="capitalize text-[10px]">{(d.relatedType || 'booking').replace(/_/g, ' ')}</span>
                          </Td>
                          <Td><div className="max-w-[140px] truncate text-zinc-400 text-[11px]">{d.reason || '—'}</div></Td>
                          <Td><span className="font-bold tabular-nums text-blue-400">{fmt(d.amount)}</span></Td>
                          <Td>
                            {d.escrowFrozen
                              ? <span className="flex items-center gap-1 text-[9px] font-bold text-purple-400 uppercase"><Snowflake size={10} /> Frozen</span>
                              : <span className="text-[10px] text-zinc-700">—</span>
                            }
                          </Td>
                          <Td><Badge status={d.status} /></Td>
                          <Td>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {/* Initiate Refund — replaces direct "Refund" — sends to host for consent */}
                              {['open', 'under_review'].includes(d.status) && (
                                <Btn
                                  size="xs"
                                  variant="primary"
                                  disabled={isActioning}
                                  onClick={() => handleDisputeAction(dispId, 'initiate-refund')}
                                >
                                  {isActioning && actioningDispute?.endsWith('initiate-refund')
                                    ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />}
                                  Request Refund
                                </Btn>
                              )}
                              {/* Legal — off-platform escalation */}
                              {['open', 'under_review', 'awaiting_host_consent'].includes(d.status) && (
                                <Btn
                                  size="xs"
                                  variant="danger"
                                  disabled={isActioning}
                                  onClick={() => handleDisputeAction(dispId, 'legal')}
                                >
                                  {isActioning && actioningDispute?.endsWith('legal')
                                    ? <Loader2 size={9} className="animate-spin" /> : <Gavel size={9} />}
                                  Legal
                                </Btn>
                              )}
                              {/* Close — no action */}
                              {!['closed', 'off_platform_legal'].includes(d.status) && (
                                <Btn
                                  size="xs"
                                  variant="ghost"
                                  disabled={isActioning}
                                  onClick={() => handleDisputeAction(dispId, 'close')}
                                >
                                  {isActioning && actioningDispute?.endsWith('close')
                                    ? <Loader2 size={9} className="animate-spin" /> : <XCircle size={9} />}
                                  Close
                                </Btn>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Table>
                )}
              </div>

              {/* Dispute status legend */}
              <div className="bg-[#111827] border border-white/6 rounded-lg p-3">
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Status Legend</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { status: 'open',                   label: 'Open — awaiting admin review' },
                    { status: 'awaiting_host_consent',  label: 'Waiting — host must approve or contest' },
                    { status: 'held_in_escrow',         label: 'Escrow — funds frozen pending resolution' },
                    { status: 'off_platform_legal',     label: 'Legal — referred to Small Claims / mediation' },
                    { status: 'resolved',               label: 'Resolved — closed with action' },
                    { status: 'closed',                 label: 'Closed — no action taken' },
                  ].map(({ status, label }) => (
                    <div key={status} className="flex items-center gap-2">
                      <Badge status={status} />
                      <span className="text-[10px] text-zinc-600">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              VERIFICATION
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'verification' && (
            <div className="space-y-4">
              <SectionHead
                title="Verification Pipeline"
                sub="Restaurant onboarding · FSA certification and VAT verification"
              />

              {loading['verification'] ? <Spin /> : (
                <div className="space-y-2">
                  {pipeline.length === 0 && (
                    <div className="bg-[#111827] border border-white/6 rounded-lg p-8">
                      <Empty icon={BadgeCheck} msg="No restaurants pending verification" />
                    </div>
                  )}
                  {pipeline.map((r, i) => (
                    <div key={i} className={`bg-[#111827] border rounded-lg p-4 flex items-center gap-4
                      ${r.status === 'approved' ? 'border-emerald-500/20' : 'border-amber-500/20'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-zinc-200">{r.name}</span>
                          <Badge status={r.status} />
                        </div>
                        <div className="text-[10px] text-zinc-600 font-mono mb-2">{r.email} · {r.location}</div>
                        <div className="flex gap-2">
                          <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wide ring-1
                            ${r.fsaVerified
                              ? 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25'
                              : 'bg-amber-500/15 text-amber-400 ring-amber-500/25'}`}>
                            FSA: {r.fsaVerified ? 'Verified' : 'Pending'}
                          </span>
                          <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wide ring-1
                            ${r.vatVerified
                              ? 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25'
                              : r.vatNumber
                                ? 'bg-amber-500/15 text-amber-400 ring-amber-500/25'
                                : 'bg-zinc-500/15 text-zinc-500 ring-zinc-500/25'}`}>
                            VAT: {r.vatVerified ? 'Verified' : r.vatNumber ? 'Pending' : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <Btn
                        onClick={() => handleApprovePipeline(r._id)}
                        disabled={approvingPipeline === r._id || r.status === 'approved'}
                        variant={r.status === 'approved' ? 'ghost' : 'primary'}
                      >
                        {approvingPipeline === r._id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <CheckCircle size={12} />}
                        {r.status === 'approved' ? 'Approved' : 'Approve'}
                      </Btn>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              FRAUD SIGNALS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'fraud' && (
            <div className="space-y-4">
              <SectionHead
                title="Fraud & Anomaly Signals"
                sub="Real-time behavioural detection — burst enquiries, mule indicators, suspicious account patterns"
                action={
                  <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                    <RefreshCw size={11} /> Refresh
                  </Btn>
                }
              />
              {loading['fraud'] ? <Spin /> : fraudSignals.length === 0 ? (
                <div className="bg-[#111827] border border-emerald-500/20 rounded-xl p-10 flex flex-col items-center gap-3">
                  <Shield size={32} className="text-emerald-400 opacity-60" />
                  <p className="text-emerald-400 text-sm font-semibold">No anomalies detected</p>
                  <p className="text-zinc-600 text-xs">All behavioural signals are within normal parameters</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fraudSignals.map((s, i) => (
                    <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border
                      ${s.severity === 'high' ? 'bg-red-500/8 border-red-500/25'
                        : s.severity === 'medium' ? 'bg-amber-500/8 border-amber-500/25'
                        : 'bg-[#111827] border-white/6'}`}>
                      <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 mt-0.5
                        ${s.severity === 'high' ? 'bg-red-500/20' : s.severity === 'medium' ? 'bg-amber-500/20' : 'bg-zinc-500/20'}`}>
                        <AlertOctagon size={13} className={s.severity === 'high' ? 'text-red-400' : s.severity === 'medium' ? 'text-amber-400' : 'text-zinc-400'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded
                            ${s.severity === 'high' ? 'bg-red-500/20 text-red-400' : s.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                            {s.severity}
                          </span>
                          <span className="text-[10px] font-bold text-zinc-300 uppercase tracking-wide">
                            {s.type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400">{s.detail}</p>
                        {s.userEmail && (
                          <p className="text-[10px] text-zinc-600 font-mono mt-0.5">{s.userName} · {s.userEmail}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 flex gap-1.5">
                        <Btn size="xs" variant="danger" onClick={() => handleSuspend(s.userId, false)}>
                          <Ban size={9} /> Suspend
                        </Btn>
                        <Btn size="xs" variant="ghost" onClick={() => { setSection('users'); }}>
                          <Eye size={9} /> View
                        </Btn>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              SOFT LAUNCH
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'soft-launch' && (
            <div className="space-y-5">
              <SectionHead
                title="Soft Launch Monitor"
                sub="High-intensity triage view for beta cohort — friction feed, activation tracking, bug triage"
                action={
                  <div className="flex items-center gap-2">
                    <select
                      value={frictionHours}
                      onChange={e => setFrictionHours(Number(e.target.value))}
                      className="bg-white/6 border border-white/10 text-zinc-300 rounded text-[10px] px-2.5 py-1 focus:outline-none"
                    >
                      {[1, 6, 24, 48, 72].map(h => <option key={h} value={h}>Last {h}h</option>)}
                    </select>
                    <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                      <RefreshCw size={11} />
                    </Btn>
                  </div>
                }
              />

              {/* Beta user watchlist */}
              <div className="bg-[#111827] border border-violet-500/20 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <FlaskConical size={13} className="text-violet-400" />
                  <span className="text-xs font-bold text-violet-300">Beta Cohort Watchlist</span>
                  <span className="ml-auto text-[10px] text-zinc-600">{betaUsers.length} users</span>
                </div>
                {loading['soft-launch'] ? <Spin /> : betaUsers.length === 0 ? (
                  <div className="p-6">
                    <Empty icon={FlaskConical} msg="No beta users flagged — use the ⚗ button in Users to add them" />
                  </div>
                ) : (
                  <Table heads={['User', 'Roles', 'Bookings', 'Listings', 'Last Seen', 'Ambassador', 'Actions']}>
                    {betaUsers.map(u => (
                      <Tr key={u._id}>
                        <Td>
                          <div className="font-semibold text-zinc-200">{u.name || '—'}</div>
                          <div className="text-[10px] text-zinc-600 font-mono">{u.email}</div>
                        </Td>
                        <Td>
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || []).map(r => (
                              <span key={r} className="text-[9px] bg-[#1F3A68]/40 text-blue-300 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide">{r}</span>
                            ))}
                          </div>
                        </Td>
                        <Td mono>{u.bookingCount}</Td>
                        <Td mono>{u.listingCount}</Td>
                        <Td muted mono>{u.lastSeen ? fmtDate(u.lastSeen) : '—'}</Td>
                        <Td>
                          {u.isAmbassador
                            ? <span className="text-[9px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded font-bold">Yes</span>
                            : <span className="text-[9px] text-zinc-600">—</span>
                          }
                        </Td>
                        <Td>
                          <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                            <Btn size="xs" variant="ghost" onClick={() => handleSetBeta(u._id, false)}>
                              Remove
                            </Btn>
                            <Btn size="xs" variant="ghost" onClick={() => handleSetAmbassador(u._id, !u.isAmbassador)}
                              className={u.isAmbassador ? 'text-amber-400' : ''}>
                              <Award size={9} /> {u.isAmbassador ? 'Revoke' : 'Ambassador'}
                            </Btn>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </Table>
                )}
              </div>

              {/* Live friction feed */}
              <div className="bg-[#111827] border border-red-500/15 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <Flame size={13} className="text-red-400" />
                  <span className="text-xs font-bold text-zinc-200">Live Friction Feed</span>
                  <span className="text-[10px] text-zinc-600 ml-1">— last {frictionHours}h</span>
                  <span className="ml-auto text-[10px] text-zinc-600">{frictionFeed.length} events</span>
                </div>
                {loading['soft-launch'] ? <Spin /> : frictionFeed.length === 0 ? (
                  <div className="p-6">
                    <Empty icon={CheckCircle} msg="No friction events in this window — platform running smoothly" />
                  </div>
                ) : (
                  <div className="divide-y divide-white/4">
                    {frictionFeed.map((ev, i) => (
                      <div key={i} className={`flex items-start gap-3 px-4 py-3
                        ${ev.severity === 'high' ? 'bg-red-500/5' : ev.severity === 'medium' ? 'bg-amber-500/5' : ''}`}>
                        <div className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full
                          ${ev.severity === 'high' ? 'bg-red-400' : ev.severity === 'medium' ? 'bg-amber-400' : 'bg-zinc-500'}`} />
                        <div className="flex-1 min-w-0">
                          <span className={`text-[9px] font-bold uppercase tracking-widest mr-2
                            ${ev.type === 'payment_failed' ? 'text-red-400' : ev.type === 'abandoned_booking' ? 'text-amber-400' : 'text-zinc-500'}`}>
                            {ev.type.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-zinc-300">{ev.detail}</span>
                          {ev.amount != null && (
                            <span className="ml-2 text-[10px] font-bold text-blue-400">{fmt(ev.amount)}</span>
                          )}
                        </div>
                        <span className="text-[9px] text-zinc-700 font-mono flex-shrink-0">{fmtDate(ev.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              GROWTH METRICS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'growth' && (
            <div className="space-y-5">
              <SectionHead
                title="Growth Metrics"
                sub="Conversion funnel and vertical performance ratios"
                action={
                  <select value={period} onChange={e => setPeriod(e.target.value)}
                    className="bg-white/6 border border-white/10 text-zinc-300 rounded text-[10px] px-2.5 py-1 focus:outline-none">
                    <option value="7days">7 days</option>
                    <option value="30days">30 days</option>
                    <option value="90days">90 days</option>
                    <option value="6months">6 months</option>
                  </select>
                }
              />

              {loading['growth'] ? <Spin /> : (
                <>
                  {/* Conversion Funnel */}
                  <div className="bg-[#111827] border border-white/6 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart2 size={14} className="text-blue-400" />
                      <span className="text-sm font-bold text-white">Conversion Funnel</span>
                    </div>
                    <div className="space-y-3">
                      {funnelData.map((stage, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-zinc-300">{stage.stage}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] font-mono text-zinc-500">{fmtN(stage.count)}</span>
                              <span className={`text-xs font-bold tabular-nums w-12 text-right
                                ${i === 0 ? 'text-blue-400' : stage.pct >= 50 ? 'text-emerald-400' : stage.pct >= 20 ? 'text-amber-400' : 'text-red-400'}`}>
                                {stage.pct}%
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-white/6 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500
                                ${i === 0 ? 'bg-blue-500' : stage.pct >= 50 ? 'bg-emerald-500' : stage.pct >= 20 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.max(stage.pct, 0.5)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {funnelData.length === 0 && <Empty icon={BarChart2} msg="No funnel data for this period" />}
                  </div>

                  {/* Vertical Performance */}
                  <div className="bg-[#111827] border border-white/6 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Layers size={14} className="text-emerald-400" />
                      <span className="text-sm font-bold text-white">Vertical Performance</span>
                      <span className="text-[10px] text-zinc-600 ml-1">— GMV & volume by service type</span>
                    </div>
                    {verticalPerf.length === 0
                      ? <Empty icon={Layers} msg="No transaction data for this period" />
                      : (
                        <div className="h-56">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={verticalPerf} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                              <CartesianGrid {...chartStyle.cartesian} />
                              <XAxis dataKey="vertical" {...chartStyle.tick} />
                              <YAxis {...chartStyle.tick} />
                              <Tooltip {...chartStyle.tooltip} formatter={(v) => [`£${v.toFixed(2)}`, 'GMV']} />
                              <Bar dataKey="gmvGbp" fill="#1F3A68" radius={[3,3,0,0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )
                    }
                    {verticalPerf.length > 0 && (
                      <Table heads={['Vertical', 'Transactions', 'GMV']}>
                        {verticalPerf.map((v, i) => (
                          <Tr key={i}>
                            <Td><span className="font-semibold text-zinc-200 capitalize">{v.vertical.replace(/_/g, ' ')}</span></Td>
                            <Td mono>{fmtN(v.count)}</Td>
                            <Td><span className="font-bold text-emerald-400">{fmt(v.gmvGbp)}</span></Td>
                          </Tr>
                        ))}
                      </Table>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              RETENTION
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'retention' && (
            <div className="space-y-5">
              <SectionHead
                title="Customer Retention"
                sub="Low-rating triage queue and stalled onboarding — catch churn before it happens"
                action={
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-500">Rating threshold:</span>
                    <select value={ratingThreshold} onChange={e => setRatingThreshold(Number(e.target.value))}
                      className="bg-white/6 border border-white/10 text-zinc-300 rounded text-[10px] px-2 py-1 focus:outline-none">
                      {[2, 3, 4].map(n => <option key={n} value={n}>Below {n}★</option>)}
                    </select>
                  </div>
                }
              />
              {loading['retention'] ? <Spin /> : (
                <>
                  {/* Low ratings */}
                  <div className="bg-[#111827] border border-amber-500/20 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                      <Star size={13} className="text-amber-400" />
                      <span className="text-xs font-bold text-amber-300">First-Response Queue — Low Ratings</span>
                      <span className="ml-auto text-[10px] text-zinc-600">{lowRatings.length} reviews below {ratingThreshold}★</span>
                    </div>
                    {lowRatings.length === 0 ? (
                      <div className="p-6"><Empty icon={Star} msg={`No ratings below ${ratingThreshold}★`} /></div>
                    ) : (
                      <Table heads={['Rating', 'Comment', 'Type', 'Date', 'Actions']}>
                        {lowRatings.map(r => (
                          <Tr key={r._id}>
                            <Td>
                              <span className="text-red-400 font-bold text-sm">{'★'.repeat(r.rating || 1)}</span>
                            </Td>
                            <Td>
                              <span className="text-zinc-300 text-xs">{(r.comment || '—').slice(0, 80)}</span>
                            </Td>
                            <Td><Badge status={r.entityType || r.targetType || 'review'} /></Td>
                            <Td muted mono>{fmtDate(r.createdAt)}</Td>
                            <Td>
                              <Btn size="xs" variant="ghost" onClick={() => setSection('disputes')}>
                                <Gavel size={9} /> Open Dispute
                              </Btn>
                            </Td>
                          </Tr>
                        ))}
                      </Table>
                    )}
                  </div>

                  {/* Stalled users */}
                  <div className="bg-[#111827] border border-white/6 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                      <UserPlus size={13} className="text-blue-400" />
                      <span className="text-xs font-bold text-zinc-200">Stalled Onboarding</span>
                      <span className="text-[10px] text-zinc-600 ml-1">— signed up but no transactions yet</span>
                      <span className="ml-auto text-[10px] text-zinc-600">{stalledUsers.length} users</span>
                    </div>
                    {stalledUsers.length === 0 ? (
                      <div className="p-6"><Empty icon={UserPlus} msg="No stalled users — great activation rate!" /></div>
                    ) : (
                      <Table heads={['User', 'Roles', 'Joined', 'Days Stalled', 'Actions']}>
                        {stalledUsers.map(u => (
                          <Tr key={u._id}>
                            <Td>
                              <div className="font-semibold text-zinc-200">{u.name || '—'}</div>
                              <div className="text-[10px] text-zinc-600 font-mono">{u.email}</div>
                            </Td>
                            <Td>
                              <div className="flex flex-wrap gap-1">
                                {(u.roles || []).map(r => (
                                  <span key={r} className="text-[9px] bg-[#1F3A68]/40 text-blue-300 px-1.5 py-0.5 rounded-sm font-bold uppercase">{r}</span>
                                ))}
                              </div>
                            </Td>
                            <Td muted mono>{fmtDate(u.createdAt)}</Td>
                            <Td danger>{u.daysSince ?? '—'}</Td>
                            <Td>
                              <div className="flex gap-1.5">
                                <Btn size="xs" variant="ghost" onClick={() => handleSetBeta(u._id, true)}>
                                  <FlaskConical size={9} /> Add Beta
                                </Btn>
                                <Btn size="xs" variant="ghost" onClick={() => {
                                  setNewIncentive(p => ({ ...p, userId: u._id }));
                                  setShowIncentiveModal(true);
                                }}>
                                  <Gift size={9} /> Incentive
                                </Btn>
                              </div>
                            </Td>
                          </Tr>
                        ))}
                      </Table>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              USER FEEDBACK
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'user-feedback' && (() => {
            const categoryLabels = {
              'booking': 'Booking & Stays', 'payments': 'Payments & Payouts',
              'messaging': 'Messaging', 'food-delivery': 'Food & Delivery',
              'rides': 'Rides & Transport', 'services': 'Home Services (Fix)',
              'deals': 'Deal Marketplace (Invest)', 'referrals': 'Referrals & Earn',
              'account': 'Account & Profile', 'ui-ux': 'UI / Design',
              'performance': 'Speed / Bugs', 'onboarding': 'Onboarding',
              'other': 'Other', '': 'Uncategorised',
            };
            const categoryColors = {
              'booking': 'bg-blue-500/20 text-blue-300', 'payments': 'bg-green-500/20 text-green-300',
              'messaging': 'bg-purple-500/20 text-purple-300', 'food-delivery': 'bg-orange-500/20 text-orange-300',
              'rides': 'bg-yellow-500/20 text-yellow-300', 'services': 'bg-cyan-500/20 text-cyan-300',
              'deals': 'bg-emerald-500/20 text-emerald-300', 'referrals': 'bg-pink-500/20 text-pink-300',
              'account': 'bg-indigo-500/20 text-indigo-300', 'ui-ux': 'bg-rose-500/20 text-rose-300',
              'performance': 'bg-red-500/20 text-red-300', 'onboarding': 'bg-teal-500/20 text-teal-300',
              'other': 'bg-zinc-500/20 text-zinc-300', '': 'bg-zinc-500/20 text-zinc-400',
            };
            const portalColors = {
              'Guest': 'bg-sky-500/20 text-sky-300 border-sky-500/30',
              'Host': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
              'Landlord': 'bg-violet-500/20 text-violet-300 border-violet-500/30',
              'Service Provider': 'bg-lime-500/20 text-lime-300 border-lime-500/30',
              'Admin': 'bg-red-500/20 text-red-300 border-red-500/30',
            };

            // Date helpers
            const now = new Date();
            const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
            const startOfWeek = new Date(startOfToday); startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

            const dateTabs = [
              { id: 'all', label: 'All Time' },
              { id: 'today', label: 'Today' },
              { id: 'yesterday', label: 'Yesterday' },
              { id: 'this-week', label: 'This Week' },
              { id: 'this-month', label: 'This Month' },
              { id: 'last-month', label: 'Last Month' },
            ];

            const dateFilter = (item) => {
              if (feedbackDateTab === 'all') return true;
              const d = new Date(item.created_at);
              if (feedbackDateTab === 'today') return d >= startOfToday;
              if (feedbackDateTab === 'yesterday') return d >= startOfYesterday && d < startOfToday;
              if (feedbackDateTab === 'this-week') return d >= startOfWeek;
              if (feedbackDateTab === 'this-month') return d >= startOfMonth;
              if (feedbackDateTab === 'last-month') return d >= startOfLastMonth && d < startOfMonth;
              return true;
            };

            // Collect counts
            const portalCounts = {};
            const catCounts = {};
            feedbackItems.forEach(f => {
              const p = f.portal || f.user_role || 'Guest';
              portalCounts[p] = (portalCounts[p] || 0) + 1;
              const c = f.category || '';
              catCounts[c] = (catCounts[c] || 0) + 1;
            });

            // Filter chain: portal → category → date
            let filtered = feedbackItems;
            if (feedbackFilterPortal !== 'all') filtered = filtered.filter(f => (f.portal || f.user_role || 'Guest') === feedbackFilterPortal);
            if (feedbackFilterCat !== 'all') filtered = filtered.filter(f => (f.category || '') === feedbackFilterCat);
            filtered = filtered.filter(dateFilter);

            // Group by date for display
            const grouped = {};
            filtered.forEach(item => {
              const d = new Date(item.created_at);
              const key = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(item);
            });

            return (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold text-white">User Feedback</h2>
                  <span className="text-sm text-zinc-500">{feedbackItems.length} total · {feedbackUnread} unread · {filtered.length} showing</span>
                </div>

                {/* Row 1: Date Tabs */}
                <div className="flex gap-1 bg-white/4 rounded-xl p-1">
                  {dateTabs.map(t => (
                    <button key={t.id} onClick={() => setFeedbackDateTab(t.id)}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${feedbackDateTab === t.id ? 'bg-blue-600 text-white shadow' : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/6'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Row 2: Portal filter */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1.5 font-semibold">Portal</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setFeedbackFilterPortal('all')}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${feedbackFilterPortal === 'all' ? 'bg-blue-600 text-white border-blue-500' : 'bg-white/4 text-zinc-400 border-white/8 hover:bg-white/8'}`}>
                      All Portals
                    </button>
                    {Object.keys(portalCounts).sort().map(p => (
                      <button key={p} onClick={() => setFeedbackFilterPortal(p)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${feedbackFilterPortal === p ? 'bg-blue-600 text-white border-blue-500' : (portalColors[p] || 'bg-white/4 text-zinc-400 border-white/8') + ' hover:opacity-80'}`}>
                        {p} ({portalCounts[p]})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 3: Category filter */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1.5 font-semibold">Category</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setFeedbackFilterCat('all')}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${feedbackFilterCat === 'all' ? 'bg-blue-600 text-white' : 'bg-white/6 text-zinc-400 hover:bg-white/10'}`}>
                      All
                    </button>
                    {Object.keys(categoryLabels).filter(k => catCounts[k] > 0).map(cat => (
                      <button key={cat || 'none'} onClick={() => setFeedbackFilterCat(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${feedbackFilterCat === cat ? 'bg-blue-600 text-white' : 'bg-white/6 text-zinc-400 hover:bg-white/10'}`}>
                        {categoryLabels[cat]} ({catCounts[cat]})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Feedback cards grouped by date */}
                {loading['user-feedback'] ? <Spin /> : (
                  <>
                    {Object.entries(grouped).map(([dateLabel, items]) => (
                      <div key={dateLabel}>
                        <div className="flex items-center gap-3 mt-4 mb-2">
                          <div className="h-px flex-1 bg-white/8" />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold whitespace-nowrap">{dateLabel} ({items.length})</span>
                          <div className="h-px flex-1 bg-white/8" />
                        </div>
                        <div className="space-y-2">
                          {items.map(item => (
                            <div key={item._id} className={`p-4 rounded-xl border ${item.read ? 'bg-[#111827] border-white/8' : 'bg-blue-900/20 border-blue-500/30'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* Portal badge */}
                                  <span className={`text-[10px] px-2 py-0.5 rounded border font-bold uppercase tracking-wide ${portalColors[item.portal || item.user_role] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
                                    {item.portal || item.user_role || 'Guest'}
                                  </span>
                                  {/* Category badge */}
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${categoryColors[item.category || ''] || 'bg-zinc-500/20 text-zinc-400'}`}>
                                    {categoryLabels[item.category || ''] || 'Uncategorised'}
                                  </span>
                                  {/* User */}
                                  <span className="font-medium text-sm text-zinc-200">{item.user_name}</span>
                                  <span className="text-[10px] text-zinc-600">({item.user_email})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-zinc-500">{new Date(item.created_at).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                                  {!item.read && (
                                    <button onClick={async () => { const token = getAuthToken(); await faqFeedbackAPI.markRead(token, item._id); fetchFeedback(); }} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors">
                                      Mark Read
                                    </button>
                                  )}
                                </div>
                              </div>
                              {item.challenges && (
                                <div className="mb-2 bg-white/3 rounded-lg p-3">
                                  <p className="text-[10px] font-bold text-red-400/70 uppercase tracking-wide mb-1">Challenge</p>
                                  <p className="text-sm text-zinc-300">{item.challenges}</p>
                                </div>
                              )}
                              {item.suggestions && (
                                <div className="bg-white/3 rounded-lg p-3">
                                  <p className="text-[10px] font-bold text-green-400/70 uppercase tracking-wide mb-1">Suggestion</p>
                                  <p className="text-sm text-zinc-300">{item.suggestions}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {filtered.length === 0 && (
                      <div className="text-center py-12 text-zinc-500">
                        <p>No feedback matches the current filters.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              CONTACT MESSAGES
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'contact-messages' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Contact Messages</h2>
                <span className="text-sm text-zinc-500">{contactItems.length} total · {contactUnread} unread</span>
              </div>
              {loading['contact-messages'] ? <Spin /> : (
                <div className="space-y-3">
                  {contactItems.map((m) => (
                    <div key={m._id} className={`rounded-xl border p-4 ${m.read ? 'border-white/8 bg-white/[0.02]' : 'border-blue-500/40 bg-blue-500/[0.06]'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-white">{m.name} <span className="font-normal text-zinc-400">· {m.email}</span></p>
                          <p className="text-xs text-zinc-500 mt-0.5">{m.type || 'general'}{m.subject ? ` · ${m.subject}` : ''} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <a href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + (m.subject || 'Your message to Ariv'))}`} className="text-xs bg-white/8 text-zinc-200 px-2 py-1 rounded hover:bg-white/12 transition-colors">Reply</a>
                          {!m.read && (
                            <button onClick={async () => { const token = getAuthToken(); await contactAPI.markRead(token, m._id); fetchContact(); }} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors">Mark read</button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-zinc-300 mt-3 whitespace-pre-wrap">{m.message}</p>
                    </div>
                  ))}
                  {contactItems.length === 0 && (
                    <div className="text-center py-12 text-zinc-500">
                      <p>No contact messages yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              INCENTIVES / FEE WAIVERS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'incentives' && (
            <div className="space-y-4">
              <SectionHead
                title="Incentive & Fee Waiver Manager"
                sub="Commission-free periods, referral bonuses, and ambassador status — surgical growth tools"
                action={
                  <Btn size="sm" variant="primary" onClick={() => setShowIncentiveModal(true)}>
                    <PlusCircle size={12} /> Grant Incentive
                  </Btn>
                }
              />

              {/* Incentive type guide */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { type: 'commission_waiver', label: 'Commission Waiver', desc: 'Platform fee set to 0% for a defined period or volume', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: Percent },
                  { type: 'referral_bonus',    label: 'Referral Bonus',    desc: 'Pay a fixed reward to referrer and new user on sign-up', color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/20',    icon: Tag },
                  { type: 'ambassador',        label: 'Ambassador',        desc: 'Flag as platform champion — zero-fee + tracking badge', color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20',   icon: Award },
                ].map(t => {
                  const Icon = t.icon;
                  return (
                    <div key={t.type} className={`flex gap-3 p-3 rounded-xl border ${t.bg}`}>
                      <Icon size={16} className={`${t.color} flex-shrink-0 mt-0.5`} />
                      <div>
                        <div className={`text-xs font-bold ${t.color}`}>{t.label}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{t.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {loading['incentives'] ? <Spin /> : (
                <div className="bg-[#111827] border border-white/6 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                    <Gift size={13} className="text-blue-400" />
                    <span className="text-xs font-bold text-zinc-200">Active Incentives</span>
                    <span className="ml-auto text-[10px] text-zinc-600">{incentives.filter(i => i.active).length} active</span>
                  </div>
                  {incentives.length === 0 ? (
                    <div className="p-6"><Empty icon={Gift} msg="No incentives granted yet" /></div>
                  ) : (
                    <Table heads={['User', 'Type', 'Scope', 'Discount', 'Uses', 'Expires', 'Status', 'Actions']}>
                      {incentives.map(inc => (
                        <Tr key={inc._id}>
                          <Td>
                            <div className="font-semibold text-zinc-200">{inc.userName || inc.userId}</div>
                            <div className="text-[10px] text-zinc-600 font-mono">{inc.userEmail}</div>
                          </Td>
                          <Td>
                            <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded
                              ${inc.incentiveType === 'commission_waiver' ? 'bg-emerald-500/15 text-emerald-400'
                                : inc.incentiveType === 'ambassador' ? 'bg-amber-500/15 text-amber-400'
                                : 'bg-blue-500/15 text-blue-400'}`}>
                              {inc.incentiveType.replace(/_/g, ' ')}
                            </span>
                          </Td>
                          <Td muted>{inc.roleScope || 'all'}</Td>
                          <Td><span className="font-bold text-emerald-400">{inc.discountPct}%</span></Td>
                          <Td mono muted>{inc.currentUses ?? 0}{inc.maxUses ? ` / ${inc.maxUses}` : ''}</Td>
                          <Td muted mono>{inc.expiresAt ? fmtDate(inc.expiresAt) : '∞'}</Td>
                          <Td><Badge status={inc.active ? 'active' : 'inactive'} /></Td>
                          <Td>
                            <Btn size="xs" variant="danger" onClick={() => handleRevokeIncentive(inc._id)}>
                              Revoke
                            </Btn>
                          </Td>
                        </Tr>
                      ))}
                    </Table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              EMAIL TOOLS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'email-tools' && (
            <AdminEmailTools getToken={getAuthToken} />
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              TRACKING LINKS (utm acquisition sources)
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'acquisition' && (
            <AdminAcquisitionSources getToken={getAuthToken} />
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              REFERRAL CODES
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'referrals' && (
            <div className="space-y-4">
              <SectionHead
                title="Referral Code Manager"
                sub="Mint codes with custom reward rules and global spend caps to control burn rate"
                action={
                  <Btn size="sm" variant="primary" onClick={() => setShowMintModal(true)}>
                    <Tag size={12} /> Mint Code
                  </Btn>
                }
              />
              {loading['referrals'] ? <Spin /> : (
                <div className="bg-[#111827] border border-white/6 rounded-xl overflow-hidden">
                  {referralCodes.length === 0 ? (
                    <div className="p-10"><Empty icon={Tag} msg="No referral codes yet — click Mint Code to create one" /></div>
                  ) : (
                    <Table heads={['Code', 'Referrer Reward', 'New User Reward', 'Uses', 'Cap', 'Total Paid', 'Status', 'Actions']}>
                      {referralCodes.map(c => (
                        <Tr key={c._id}>
                          <Td mono>
                            <span className="text-blue-400 font-bold tracking-widest">{c.code}</span>
                          </Td>
                          <Td><span className="text-emerald-400 font-bold">{fmt(c.referrerRewardGbp)}</span></Td>
                          <Td><span className="text-blue-400 font-bold">{fmt(c.newUserRewardGbp)}</span></Td>
                          <Td mono muted>{c.currentUses}{c.maxUses ? ` / ${c.maxUses}` : ''}</Td>
                          <Td muted mono>{c.globalCapGbp ? fmt(c.globalCapGbp) : '—'}</Td>
                          <Td mono>{fmt(c.totalPaidGbp)}</Td>
                          <Td><Badge status={c.active ? 'active' : 'inactive'} /></Td>
                          <Td>
                            <Btn size="xs" variant={c.active ? 'danger' : 'success'} onClick={() => handleToggleCode(c.code)}>
                              {c.active ? 'Deactivate' : 'Activate'}
                            </Btn>
                          </Td>
                        </Tr>
                      ))}
                    </Table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              AUDIT LOG
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'audit-log' && (() => {
            const detailsFor = (entry) => {
              // Pick the most useful single-line summary we can from the entry payload
              if (entry.changes)   return JSON.stringify(entry.changes);
              if (entry.newValue)  return JSON.stringify(entry.newValue);
              if (entry.decision)  return `decision: ${entry.decision}`;
              if (entry.reason)    return entry.reason;
              if (entry.note)      return entry.note;
              // Fallback: strip known keys, show what's left
              const shown = { ...entry };
              ['_id','createdAt','adminId','adminName','adminEmail','action','type','targetType','targetId'].forEach(k => delete shown[k]);
              const s = JSON.stringify(shown);
              return s === '{}' ? '—' : s;
            };
            const summary = auditLog.reduce((a, e) => {
              const k = (e.action || 'unknown');
              a[k] = (a[k] || 0) + 1;
              return a;
            }, {});
            return (
              <div className="space-y-4">
                <SectionHead
                  title="Admin Audit Log"
                  sub="Immutable record of every privileged action — kill-switch flips, user suspensions, appeal decisions, fee waivers, referral payouts, dispute actions"
                  action={
                    <div className="flex items-center gap-2">
                      <input
                        value={auditFilter}
                        onChange={e => setAuditFilter(e.target.value)}
                        placeholder="Search action or admin…"
                        className="bg-white/6 border border-white/10 text-zinc-300 rounded text-[10px] px-3 py-1.5 w-52 focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                      />
                      <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                        <RefreshCw size={11} />
                      </Btn>
                    </div>
                  }
                />
                {Object.keys(summary).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(summary).slice(0, 8).map(([action, count]) => (
                      <button
                        key={action}
                        onClick={() => setAuditFilter(action)}
                        className="text-[10px] bg-[#1F3A68]/30 text-blue-300 px-2.5 py-1 rounded-full border border-blue-500/20 hover:bg-[#1F3A68]/50 transition-colors"
                      >
                        {action.replace(/_/g, ' ')} <span className="font-mono">×{count}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="bg-[#111827] border border-white/6 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-white/6 flex items-center gap-2">
                    <ClipboardList size={12} className="text-zinc-500" />
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Write-once · cannot be deleted or modified</span>
                    <span className="ml-auto text-[10px] text-zinc-600">{auditLog.length} entries</span>
                  </div>
                  {loading['audit-log'] ? <Spin /> : auditLog.length === 0 ? (
                    <div className="p-8"><Empty icon={ClipboardList} msg="No audit entries yet" /></div>
                  ) : (
                    <Table heads={['Time', 'Admin', 'Action', 'Target', 'Details']}>
                      {auditLog.map(entry => (
                        <Tr key={entry._id}>
                          <Td muted mono>{fmtDate(entry.createdAt)}</Td>
                          <Td muted>{entry.adminName || entry.adminEmail || (entry.adminId ? `${entry.adminId.slice(0, 8)}…` : '—')}</Td>
                          <Td>
                            <span className="text-[10px] font-bold bg-[#1F3A68]/30 text-blue-300 px-2 py-0.5 rounded uppercase tracking-wide whitespace-nowrap">
                              {(entry.action || '').replace(/_/g, ' ')}
                            </span>
                          </Td>
                          <Td muted mono>
                            {entry.targetType ? `${entry.targetType}/${(entry.targetId || '').slice(0, 8)}…` : '—'}
                          </Td>
                          <Td muted>
                            <span className="font-mono text-[9px] text-zinc-400 break-all">{detailsFor(entry).slice(0, 120)}</span>
                          </Td>
                        </Tr>
                      ))}
                    </Table>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              REFERRAL COMMAND CENTRE
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'referral-command' && (
            <div className="space-y-5">
              <SectionHead
                title="Referral Campaign — First 1,000"
                sub="Ongoing share: 3% hosts · 2% drivers, restaurants, providers · 1% riders · 0.5% guest wallet credit · capped at Ariv's own fee · every payout approved manually"
              />

              {loading['referral-command'] || !refCommandCentre ? <Spin /> : (
                <>
                  {/* Campaign status bar */}
                  {refCommandCentre.campaign && (
                    <div className="bg-[#111827] border border-[#1F3A68]/40 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Award size={15} className="text-amber-400" />
                          <span className="text-sm font-bold text-white">Live Referral Counter</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded
                            ${refCommandCentre.campaign.newReferralsActive
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-red-500/15 text-red-400'}`}>
                            {refCommandCentre.campaign.newReferralsActive ? 'Accepting new referrals' : 'New referrals PAUSED'}
                          </span>
                          <KillSwitch
                            label=""
                            sub=""
                            enabled={refCommandCentre.campaign.newReferralsActive}
                            onChange={handleToggleReferralKillSwitch}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-zinc-400 font-semibold">
                          {fmtN(refCommandCentre.campaign.currentCount)} / {fmtN(refCommandCentre.campaign.globalCap)} referred users
                        </span>
                        <span className={`text-xs font-bold tabular-nums
                          ${refCommandCentre.campaign.pctFilled >= 80 ? 'text-amber-400'
                            : refCommandCentre.campaign.pctFilled >= 50 ? 'text-blue-400'
                            : 'text-emerald-400'}`}>
                          {refCommandCentre.campaign.pctFilled}%
                        </span>
                      </div>
                      <div className="h-3 bg-white/6 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700
                            ${refCommandCentre.campaign.pctFilled >= 80 ? 'bg-amber-500'
                              : refCommandCentre.campaign.pctFilled >= 50 ? 'bg-blue-500'
                              : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(refCommandCentre.campaign.pctFilled, 100)}%` }}
                        />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                        <div>
                          <div className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold">Counted toward cap</div>
                          <div className="text-xs font-bold text-white">Reward-eligible referrals only</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold">Recorded while paused / full</div>
                          <div className="text-xs font-bold text-white">{fmtN(refCommandCentre.campaign.unrewardedCount || 0)} (never paid)</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Burn vs Earn */}
                  {refCommandCentre.financials && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-[#111827] border border-red-500/15 rounded-xl p-4">
                          <div className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold mb-1">🔥 Total Burn (Committed)</div>
                          <div className="text-2xl font-bold text-red-400 tabular-nums">
                            {fmt(refCommandCentre.financials.totalCommittedGbp)}
                          </div>
                          <div className="text-[10px] text-zinc-600 mt-1">{fmtN(refCommandCentre.financials.totalPayoutCount)} payout records</div>
                        </div>
                        <div className="bg-[#111827] border border-emerald-500/15 rounded-xl p-4">
                          <div className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold mb-1">💚 Earn (Referral GMV)</div>
                          <div className="text-2xl font-bold text-emerald-400 tabular-nums">
                            {fmt(refCommandCentre.financials.referralGmvGbp)}
                          </div>
                          <div className="text-[10px] text-zinc-600 mt-1">Platform retained: <span className="text-blue-400 font-bold">{fmt(refCommandCentre.financials.platformRetainedGbp)}</span></div>
                        </div>
                      </div>

                      {/* Incentive ROI row */}
                      <div className="bg-gradient-to-r from-amber-500/5 to-sky-500/5 border border-amber-500/15 rounded-xl p-4">
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-2">
                          🏆 Total Value of Incentives Given (Launch ROI)
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <div className="text-xl font-black text-amber-400 tabular-nums">
                              {fmt(refCommandCentre.financials.incentiveValueGbp)}
                            </div>
                            <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">Referral Commissions</div>
                          </div>
                          <div>
                            <div className="text-xl font-black text-sky-400 tabular-nums">
                              {fmt(refCommandCentre.financials.feeWaiversTotalGbp)}
                            </div>
                            <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">Fee Waivers</div>
                          </div>
                          <div>
                            <div className="text-xl font-black text-white tabular-nums">
                              {fmt(
                                (refCommandCentre.financials.incentiveValueGbp || 0) +
                                (refCommandCentre.financials.feeWaiversTotalGbp || 0)
                              )}
                            </div>
                            <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">Combined Total</div>
                          </div>
                        </div>
                        <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
                          Tracks total marketing spend for the 1,000 Club launch. Compare against referral GMV to calculate acquisition ROI.
                        </p>
                      </div>
                    </>
                  )}

                  {/* Status breakdown */}
                  {refCommandCentre.byStatus && Object.keys(refCommandCentre.byStatus).length > 0 && (
                    <div className="bg-[#111827] border border-white/6 rounded-xl p-4">
                      <div className="text-xs font-bold text-zinc-300 mb-3">Payout Status Breakdown</div>
                      <div className="grid grid-cols-4 gap-3">
                        {['pending_verification','approved','paid','voided'].map(s => {
                          const data = refCommandCentre.byStatus[s];
                          return (
                            <div key={s} className="text-center">
                              <div className="text-base font-bold tabular-nums text-white">{fmtN(data?.count || 0)}</div>
                              <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold mt-0.5">{s.replace(/_/g,' ')}</div>
                              <div className="text-[10px] text-zinc-500 mt-0.5">{fmt(data?.totalGbp || 0)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Super-Referrer Leaderboard */}
                  {refCommandCentre.leaderboard?.length > 0 && (
                    <div className="bg-[#111827] border border-amber-500/15 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                        <Award size={13} className="text-amber-400" />
                        <span className="text-xs font-bold text-amber-300">Super-Referrer Leaderboard</span>
                        <span className="text-[10px] text-zinc-500 ml-1">— top earners · consider offering permanent Ambassador status</span>
                      </div>
                      <Table heads={['#', 'Name', 'Referrals', 'Earned', 'Ambassador', 'Action']}>
                        {refCommandCentre.leaderboard.map((l, i) => (
                          <Tr key={i}>
                            <Td muted mono>{i + 1}</Td>
                            <Td>
                              <div className="font-semibold text-zinc-200">{l.name || '—'}</div>
                              <div className="text-[10px] text-zinc-600 font-mono">{l.email}</div>
                            </Td>
                            <Td mono>{fmtN(l.referrals)}</Td>
                            <Td><span className="font-bold text-emerald-400">{fmt(l.earnedGbp)}</span></Td>
                            <Td>
                              {l.isAmbassador
                                ? <div className="flex items-center gap-1.5"><AmbassadorBadge size={16} glow={false} tooltip={false} /><span className="text-[10px] text-amber-300 font-bold">Ambassador</span></div>
                                : <span className="text-[9px] text-zinc-600">—</span>}
                            </Td>
                            <Td>
                              {!l.isAmbassador && l.userId && (
                                <Btn size="xs" variant="ghost" onClick={() => handleSetAmbassador(l.userId, true)}>
                                  <Award size={9} /> Make Ambassador
                                </Btn>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              REFERRAL FRAUD RADAR
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'referral-fraud' && (
            <div className="space-y-4">
              <SectionHead
                title="Referral Fraud Radar"
                sub="Automated flags: self-referral, circular loops, micro-stay gaming, junk networks"
                action={
                  <div className="flex items-center gap-3">
                    <select
                      value={refPayoutFilter}
                      onChange={e => setRefPayoutFilter(e.target.value)}
                      className="bg-white/6 border border-white/10 text-zinc-300 rounded text-[10px] px-2.5 py-1 focus:outline-none"
                    >
                      <option value="">All payouts</option>
                      <option value="pending_verification">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid</option>
                      <option value="voided">Voided</option>
                    </select>
                    <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                      <RefreshCw size={11} />
                    </Btn>
                  </div>
                }
              />

              {/* Fraud flags */}
              {loading['referral-fraud'] ? <Spin /> : (
                <>
                  {refFraudFlags.length === 0 ? (
                    <div className="bg-[#111827] border border-emerald-500/20 rounded-xl p-10 flex flex-col items-center gap-3">
                      <Shield size={32} className="text-emerald-400 opacity-60" />
                      <p className="text-emerald-400 text-sm font-semibold">No fraud flags detected</p>
                      <p className="text-zinc-600 text-xs">All referral relationships are within normal parameters</p>
                    </div>
                  ) : (
                    <div className="bg-[#111827] border border-red-500/15 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                        <AlertOctagon size={13} className="text-red-400" />
                        <span className="text-xs font-bold text-red-300">Automated Fraud Flags</span>
                        <span className="ml-auto text-[10px] text-zinc-600">{refFraudFlags.length} flags</span>
                      </div>
                      <div className="divide-y divide-white/4">
                        {refFraudFlags.map((flag, i) => (
                          <div key={i} className={`flex items-start gap-3 px-4 py-3
                            ${flag.severity === 'high' ? 'bg-red-500/5' : 'bg-amber-500/3'}`}>
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0
                              ${flag.severity === 'high' ? 'bg-red-400' : 'bg-amber-400'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded
                                  ${flag.severity === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                  {flag.type.replace(/_/g,' ')}
                                </span>
                                <span className="text-xs font-semibold text-zinc-300">{flag.userName}</span>
                                <span className="text-[10px] text-zinc-600 font-mono">{flag.userEmail}</span>
                              </div>
                              <p className="text-xs text-zinc-500">{flag.detail}</p>
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Btn size="xs" variant="danger" onClick={() => { setVoidTarget(flag.payoutIds?.[0] || flag.userId); setShowVoidModal(true); }}>
                                <XCircle size={9} /> Void
                              </Btn>
                              <Btn size="xs" variant="ghost" onClick={() => handleSuspend(flag.userId, false)}>
                                <Ban size={9} /> Suspend
                              </Btn>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All payouts table */}
                  <div className="bg-[#111827] border border-white/6 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                      <Tag size={13} className="text-blue-400" />
                      <span className="text-xs font-bold text-zinc-200">Referral Payout Ledger</span>
                      <span className="ml-auto text-[10px] text-zinc-600">{refAllPayouts.length} records</span>
                    </div>
                    {refAllPayouts.length === 0 ? (
                      <div className="p-6"><Empty icon={Tag} msg="No payout records found" /></div>
                    ) : (
                      <Table heads={['Referrer', 'New User', 'Type', 'Gross', 'Payout', 'Status', 'Fraud', 'Actions']}>
                        {refAllPayouts.slice(0,50).map(p => (
                          <Tr key={p._id}>
                            <Td>
                              <div className="font-semibold text-zinc-200 text-[11px]">{p.referrerName || '—'}</div>
                              <div className="text-[9px] text-zinc-600 font-mono">{p.referrerEmail}</div>
                            </Td>
                            <Td>
                              <div className="font-semibold text-zinc-200 text-[11px]">{p.newUserName || '—'}</div>
                              <div className="text-[9px] text-zinc-600 font-mono">{p.newUserEmail}</div>
                            </Td>
                            <Td muted>{p.triggerEventType || 'booking'}</Td>
                            <Td mono>{fmt(p.grossAmountGbp)}</Td>
                            <Td><span className="font-bold text-emerald-400">{fmt(p.referralPayoutGbp)}</span></Td>
                            <Td><Badge status={p.status} /></Td>
                            <Td>
                              {p.fraudScore > 0
                                ? <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded
                                    ${p.fraudScore >= 40 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                    {p.fraudScore}
                                  </span>
                                : <span className="text-[9px] text-zinc-700">—</span>
                              }
                            </Td>
                            <Td>
                              <div className="flex gap-1.5">
                                {['pending_verification','flagged'].includes(p.status) && (
                                  <Btn size="xs" variant="success" onClick={() => handleApprovePayout(p._id)}>
                                    <CheckCircle size={9} /> OK
                                  </Btn>
                                )}
                                {!['voided','paid'].includes(p.status) && (
                                  <Btn size="xs" variant="danger" onClick={() => { setVoidTarget(p); setShowVoidModal(true); }}>
                                    <XCircle size={9} /> Void
                                  </Btn>
                                )}
                              </div>
                            </Td>
                          </Tr>
                        ))}
                      </Table>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              SUSPENSION COMMAND CENTRE
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'suspension-command' && (
            <div className="space-y-5">
              <SectionHead
                title="Suspension Command Centre"
                sub="Entity kill-switch for Drivers and Restaurants. Toggle suspends the entity and sends an automated notification."
                action={
                  <div className="flex gap-2">
                    <Btn size="xs" variant="ghost" onClick={handleDownloadReport}>
                      <Download size={12} /> Compliance Report (CSV)
                    </Btn>
                    <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                      <RefreshCw size={12} /> Refresh
                    </Btn>
                  </div>
                }
              />

              {/* Loading / Error states */}
              {loading['suspension-command'] && (
                <div className="flex items-center justify-center py-16 gap-2 text-zinc-500 text-sm">
                  <Loader2 size={18} className="animate-spin" /> Loading suspension data…
                </div>
              )}
              {sectionErrors['suspension-command'] && !loading['suspension-command'] && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={15} />
                    <span>{sectionErrors['suspension-command']}</span>
                  </div>
                  <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                    <RefreshCw size={12} /> Retry
                  </Btn>
                </div>
              )}

              {/* Health Pill Summary */}
              {!loading['suspension-command'] && suspendedSummary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Suspended Drivers',     val: suspendedSummary.suspendedDrivers,     color: 'text-red-400' },
                    { label: 'Suspended Restaurants',  val: suspendedSummary.suspendedRestaurants, color: 'text-red-400' },
                    { label: 'Total Suspended Users',  val: suspendedSummary.suspendedUsersTotal,  color: 'text-amber-400' },
                    { label: 'Pending Lockdown',       val: suspendedSummary.pendingLockdown,      color: 'text-amber-400' },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="bg-[#111827] border border-white/8 rounded-lg p-4 text-center">
                      <div className={`text-2xl font-black tabular-nums ${color}`}>{val ?? '—'}</div>
                      <div className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold mt-1">{label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Drivers + Restaurants Tables */}
              {!loading['suspension-command'] && !sectionErrors['suspension-command'] && (
              <>
              <div className="bg-[#0D1323] border border-white/8 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-white/8 flex items-center gap-2">
                  <Truck size={14} className="text-zinc-500" />
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Drivers</span>
                </div>
                <Table heads={['Name','Email','City','Status','Action']}>
                  {suspendEntities.drivers.length === 0 ? (
                    <Tr><Td><Empty icon={Truck} msg="No drivers found" /></Td></Tr>
                  ) : suspendEntities.drivers.map(d => (
                    <Tr key={d._id || d.id}>
                      <Td><span className="font-semibold text-zinc-200 text-[11px]">{d.name || d.fullName || '—'}</span></Td>
                      <Td muted>{d.email || '—'}</Td>
                      <Td muted>{d.city || '—'}</Td>
                      <Td>
                        {d.suspended
                          ? <Badge status="suspended" />
                          : d.pendingLockdown
                          ? <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">Pending Lockdown</span>
                          : <Badge status="active" />
                        }
                      </Td>
                      <Td>
                        <Btn
                          size="xs"
                          variant={d.suspended ? 'success' : 'danger'}
                          onClick={() => { setSuspendTarget({ ...d, entityType: 'driver' }); setSuspendReason(''); setShowSuspendModal(true); }}
                        >
                          {d.suspended ? <><CheckCircle size={9} /> Reactivate</> : <><Ban size={9} /> Suspend</>}
                        </Btn>
                      </Td>
                    </Tr>
                  ))}
                </Table>
              </div>

              {/* Restaurants Table */}
              <div className="bg-[#0D1323] border border-white/8 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-white/8 flex items-center gap-2">
                  <Utensils size={14} className="text-zinc-500" />
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Restaurants</span>
                </div>
                <Table heads={['Name','Email','City','Status','Action']}>
                  {suspendEntities.restaurants.length === 0 ? (
                    <Tr><Td><Empty icon={Utensils} msg="No restaurants found" /></Td></Tr>
                  ) : suspendEntities.restaurants.map(r => (
                    <Tr key={r._id || r.id}>
                      <Td><span className="font-semibold text-zinc-200 text-[11px]">{r.restaurantName || r.name || '—'}</span></Td>
                      <Td muted>{r.email || '—'}</Td>
                      <Td muted>{r.city || '—'}</Td>
                      <Td>
                        {r.suspended ? <Badge status="suspended" /> : <Badge status="active" />}
                      </Td>
                      <Td>
                        <Btn
                          size="xs"
                          variant={r.suspended ? 'success' : 'danger'}
                          onClick={() => { setSuspendTarget({ ...r, entityType: 'restaurant' }); setSuspendReason(''); setShowSuspendModal(true); }}
                        >
                          {r.suspended ? <><CheckCircle size={9} /> Reactivate</> : <><Ban size={9} /> Suspend</>}
                        </Btn>
                      </Td>
                    </Tr>
                  ))}
                </Table>
              </div>
              </> )} {/* end !loading && !error gate for tables */}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              COMPLIANCE APPEALS PIPELINE
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'compliance-appeals' && (
            <div className="space-y-5">
              <SectionHead
                title="Appeals Verification Pipeline"
                sub={`${appealsTotal} open appeal${appealsTotal !== 1 ? 's' : ''} — suspended entities awaiting review`}
                action={
                  <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                    <RefreshCw size={12} /> Refresh
                  </Btn>
                }
              />

              {loading['compliance-appeals'] ? (
                <div className="flex items-center justify-center py-16 gap-2 text-zinc-500 text-sm">
                  <Loader2 size={18} className="animate-spin" /> Loading appeals…
                </div>
              ) : sectionErrors['compliance-appeals'] ? (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={15} />
                    <span>{sectionErrors['compliance-appeals']}</span>
                  </div>
                  <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                    <RefreshCw size={12} /> Retry
                  </Btn>
                </div>
              ) : complianceAppeals.length === 0 ? (
                <Empty icon={Gavel} msg="No open appeals — all clear ✅" />
              ) : (
                <div className="space-y-3">
                  {complianceAppeals.map(appeal => (
                    <div key={appeal._id} className="bg-[#0D1323] border border-white/8 rounded-xl p-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-zinc-200">{appeal.userName}</span>
                            {appeal.userRef && (
                              <span className="font-mono text-[9px] text-amber-400/80 bg-amber-500/8 border border-amber-500/15 px-1.5 py-0.5 rounded tracking-wider" title="User reference ID">
                                {appeal.userRef}
                              </span>
                            )}
                            <Badge status={appeal.status} />
                            {appeal.suspensionCount > 1 && (
                              <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full font-bold">
                                {appeal.suspensionCount}× suspended
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-zinc-500">{appeal.userEmail}</div>
                          <div className="text-[11px] text-zinc-600 mt-1">
                            Suspended for: <span className="text-zinc-400">{appeal.suspensionReason || '—'}</span>
                          </div>
                        </div>
                        <div className="text-[10px] text-zinc-600">{appeal.createdAt ? new Date(appeal.createdAt).toLocaleDateString() : '—'}</div>
                      </div>

                      {/* Appeal message */}
                      <div className="bg-white/3 rounded-lg p-3 mb-3">
                        <p className="text-xs text-zinc-300 leading-relaxed">{appeal.message}</p>
                      </div>

                      {/* Evidence files */}
                      {appeal.evidenceFiles?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {appeal.evidenceFiles.map((f, i) => (
                            <a key={i} href={f.url} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-semibold px-2.5 py-1 rounded-full hover:opacity-80 transition-opacity">
                              <ExternalLink size={10} /> {f.label || f.originalName}
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Decision buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <Btn size="xs" variant="success"
                          onClick={() => { setDecisionTarget(appeal); setDecisionType('approve'); setDecisionExtra(''); setShowDecisionModal(true); }}
                          disabled={!!decidingAppeal}
                        >
                          <CheckCircle size={10} /> Approve
                        </Btn>
                        <Btn size="xs" variant="ghost"
                          onClick={() => { setDecisionTarget(appeal); setDecisionType('request_info'); setDecisionExtra(''); setShowDecisionModal(true); }}
                          disabled={!!decidingAppeal}
                        >
                          <Send size={10} /> Request Info
                        </Btn>
                        <Btn size="xs" variant="danger"
                          onClick={() => { setDecisionTarget(appeal); setDecisionType('permanent_ban'); setDecisionExtra(''); setShowDecisionModal(true); }}
                          disabled={!!decidingAppeal}
                        >
                          <Ban size={10} /> Permanent Ban
                        </Btn>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              COMPLIANCE EXPIRING DOCS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'compliance-expiring' && (
            <div className="space-y-5">
              <SectionHead
                title="Expiring Documents"
                sub="Drivers and restaurants with documents expiring within 14 days. Auto-suspend fires at T-0."
                action={
                  <div className="flex gap-2">
                    <Btn size="xs" variant="ghost" onClick={handleNudgeAll} disabled={nudgingAll}>
                      {nudgingAll ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                      Nudge All
                    </Btn>
                    <Btn size="xs" variant="ghost" onClick={handleDownloadReport}>
                      <Download size={12} /> Compliance Report
                    </Btn>
                  </div>
                }
              />

              {loading['compliance-expiring'] ? (
                <div className="flex items-center justify-center py-16 gap-2 text-zinc-500 text-sm">
                  <Loader2 size={18} className="animate-spin" /> Loading expiring documents…
                </div>
              ) : sectionErrors['compliance-expiring'] ? (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={15} />
                    <span>{sectionErrors['compliance-expiring']}</span>
                  </div>
                  <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                    <RefreshCw size={12} /> Retry
                  </Btn>
                </div>
              ) : expiringDocs.length === 0 ? (
                <Empty icon={FileWarning} msg="No documents expiring within 14 days — all clear ✅" />
              ) : (
                <div className="bg-[#0D1323] border border-white/8 rounded-xl overflow-hidden">
                  <Table heads={['Entity','Type','Document','Expiry','Days Left','Status']}>
                    {expiringDocs.map((d, i) => {
                      const expiryDate    = d.expiryWarningDate ? new Date(d.expiryWarningDate) : null;
                      const daysLeft      = expiryDate
                        ? Math.ceil((expiryDate - new Date()) / 86400000)
                        : null;
                      const urgentColor   = daysLeft !== null && daysLeft <= 3
                        ? 'text-red-400'
                        : daysLeft !== null && daysLeft <= 7
                        ? 'text-amber-400'
                        : 'text-zinc-300';
                      return (
                        <Tr key={i}>
                          <Td>
                            <div className="font-semibold text-zinc-200 text-[11px]">{d.userName || '—'}</div>
                            <div className="text-[9px] text-zinc-600">{d.userEmail}</div>
                          </Td>
                          <Td>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full
                              ${d.entityType === 'driver' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}>
                              {d.entityType}
                            </span>
                          </Td>
                          <Td muted>{d.expiryWarningDoc || '—'}</Td>
                          <Td mono>{d.expiryWarningDate || '—'}</Td>
                          <Td>
                            <span className={`font-bold tabular-nums text-xs ${urgentColor}`}>
                              {daysLeft !== null ? (daysLeft <= 0 ? 'TODAY' : `${daysLeft}d`) : '—'}
                            </span>
                          </Td>
                          <Td>
                            <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                              {d.documentStatus?.replace(/_/g, ' ')}
                            </span>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Table>
                </div>
              )}

              {/* Merchant Agreement note */}
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
                <p className="text-[11px] text-blue-300 leading-relaxed">
                  <strong>Merchant Agreement clause:</strong> Partners acknowledge that maintaining valid legal documentation is their responsibility.
                  Ariv provides a 7-day grace notification (T-7) and a low-priority heads-up at T-14. Failure to provide valid renewals results in
                  automatic suspension at T-0. Ambassador status and progress bars remain active during the grace period.
                </p>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              OCR DOCUMENT VERIFICATION
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'ocr-verification' && (
            <div className="space-y-5">
              <SectionHead
                title="AI OCR Verification Queue"
                sub="Documents submitted by partners that have passed pre-flight and been processed by OCR. Review and verify or reject."
              />

              {/* ── Filters ── */}
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'All',          value: '' },
                  { label: '🟢 Green',     value: 'green' },
                  { label: '🟡 Amber',     value: 'amber' },
                  { label: '🔴 Red',       value: 'red' },
                ].map(f => (
                  <button key={f.value} onClick={() => setOcrScoreFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all
                      ${ocrScoreFilter === f.value
                        ? 'bg-[#1F3A68] border-[#1F3A68] text-white'
                        : 'bg-white/4 border-white/10 text-zinc-400 hover:border-white/20'}`}>
                    {f.label}
                  </button>
                ))}
                <span className="flex-1" />
                {[
                  { label: 'All Routes',    value: '' },
                  { label: 'Auto-Verify',   value: 'auto_verify' },
                  { label: 'Manual Review', value: 'manual_review' },
                ].map(f => (
                  <button key={f.value} onClick={() => setOcrRouteFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all
                      ${ocrRouteFilter === f.value
                        ? 'bg-[#1F3A68] border-[#1F3A68] text-white'
                        : 'bg-white/4 border-white/10 text-zinc-400 hover:border-white/20'}`}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* ── Reject Modal ── */}
              {ocrRejectId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                  <div className="bg-[#0D1323] border border-red-500/30 rounded-2xl p-6 w-full max-w-md space-y-4">
                    <h3 className="text-sm font-black text-white">Reject Document</h3>
                    <p className="text-xs text-zinc-400">The partner will be notified by email with this reason so they can re-upload.</p>
                    <textarea
                      rows={3}
                      value={ocrRejectReason}
                      onChange={e => setOcrRejectReason(e.target.value)}
                      placeholder="e.g. Document is expired / illegible / wrong document type…"
                      className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => { setOcrRejectId(null); setOcrRejectReason(''); }}
                        className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-white/6 border border-white/10 text-zinc-300 hover:bg-white/10 transition-all">
                        Cancel
                      </button>
                      <button onClick={handleOcrReject}
                        disabled={!ocrRejectReason.trim() || ocrVerifying === ocrRejectId}
                        className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-red-600 hover:bg-red-500 text-white transition-all disabled:opacity-50">
                        {ocrVerifying === ocrRejectId ? 'Rejecting…' : 'Confirm Reject'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Verification Cards ── */}
              {loading['ocr-verification'] ? (
                <div className="flex items-center justify-center py-16 gap-2 text-zinc-500 text-sm">
                  <Loader2 size={18} className="animate-spin" /> Loading verification queue…
                </div>
              ) : sectionErrors['ocr-verification'] ? (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={15} />
                    <span>{sectionErrors['ocr-verification']}</span>
                  </div>
                  <Btn size="xs" variant="ghost" onClick={() => setRefreshKey(k => k + 1)}>
                    <RefreshCw size={12} /> Retry
                  </Btn>
                </div>
              ) : ocrQueue.length === 0 ? (
                <Empty icon={ScanLine} msg="No documents pending verification ✅" />
              ) : (
                <div className="space-y-4">
                  {ocrQueue.map(log => {
                    const ext        = log.extractedData || {};
                    const confidence = ext.confidence ?? log.confidenceScore;
                    const score      = ext.preScore;
                    const scoreColour = score === 'green'
                      ? { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-400' }
                      : score === 'amber'
                      ? { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-400' }
                      : { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', dot: 'bg-red-400' };

                    const confPct = confidence != null ? Math.round(confidence * 100) : null;

                    return (
                      <div key={log._id} className="bg-[#0D1323] border border-white/8 rounded-2xl overflow-hidden">
                        {/* ── Card Header ── */}
                        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-white/5">
                          <div>
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-black text-white">{log.userName || log.userId}</span>
                              <span className="text-[10px] text-zinc-500">{log.userEmail}</span>
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                log.entityType === 'driver' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'
                              }`}>{log.entityType}</span>
                            </div>
                            <p className="text-xs text-zinc-500">{log.documentType} · Uploaded {new Date(log.createdAt).toLocaleDateString('en-GB')}</p>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Pre-score badge */}
                            {score && (
                              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${scoreColour.bg} border ${scoreColour.border}`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${scoreColour.dot}`} />
                                <span className={`text-[10px] font-bold capitalize ${scoreColour.text}`}>{score}</span>
                              </div>
                            )}
                            {/* Confidence badge */}
                            {confPct != null && (
                              <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                                confPct >= 70
                                  ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                  : 'bg-red-500/10 border-red-500/20 text-red-400'
                              }`}>
                                {confPct}% confidence
                              </div>
                            )}
                            {/* Route badge */}
                            <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                              ext.route === 'auto_verify'
                                ? 'bg-sky-500/10 border-sky-500/20 text-sky-400'
                                : 'bg-violet-500/10 border-violet-500/20 text-violet-400'
                            }`}>
                              {ext.route === 'auto_verify' ? '⚡ Auto-Verify' : '👁 Manual Review'}
                            </div>
                          </div>
                        </div>

                        {/* ── Side-by-side: Doc preview + Pre-filled form ── */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:divide-x md:divide-white/5">
                          {/* Left: Document preview link */}
                          <div className="p-5">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-3">Uploaded Document</p>
                            {log.fileUrl ? (
                              <a href={log.fileUrl} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-white/4 border border-white/10 rounded-xl p-4 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all group">
                                <div className="w-8 h-8 bg-blue-500/15 border border-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <ScanLine size={14} className="text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-zinc-200 group-hover:text-blue-300 transition-colors truncate">
                                    {log.documentType}
                                  </p>
                                  <p className="text-[10px] text-zinc-600">Click to open in new tab</p>
                                </div>
                              </a>
                            ) : (
                              <p className="text-xs text-zinc-600 italic">No file URL available</p>
                            )}

                            {/* Flags */}
                            {ext.flags?.length > 0 && (
                              <div className="mt-3 space-y-1.5">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">Flags</p>
                                {ext.flags.map((f, i) => (
                                  <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-400">
                                    <span className="mt-0.5">⚠</span> {f}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* OCR name match */}
                            {ext.registeredName && (
                              <div className="mt-3 flex items-center gap-2">
                                <span className="text-[10px] text-zinc-600">Name on doc:</span>
                                <span className="text-[10px] font-semibold text-zinc-300">{ext.registeredName}</span>
                                {ext.nameMatch === false && (
                                  <span className="text-[10px] text-red-400 font-bold">⚠ Mismatch</span>
                                )}
                                {ext.nameMatch === true && (
                                  <span className="text-[10px] text-emerald-400 font-bold">✓ Match</span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Right: Pre-filled verification form */}
                          <div className="p-5 space-y-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1">Verification Form</p>

                            {/* Cert number (read-only, OCR extracted) */}
                            {ext.certNumber && (
                              <div>
                                <label className="block text-[10px] text-zinc-600 mb-1">Certificate Number (OCR)</label>
                                <div className="bg-white/4 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-zinc-300">
                                  {ext.certNumber}
                                </div>
                              </div>
                            )}

                            {/* Expiry date — admin can edit the OCR-extracted value */}
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1">
                                Expiry Date <span className="text-red-400">*</span>
                                {ext.expiryDate && (
                                  <span className="ml-1 text-emerald-400 normal-case font-normal">(OCR: {ext.expiryDate})</span>
                                )}
                              </label>
                              <input
                                type="date"
                                value={ocrEditExpiry[log._id] ?? ext.expiryDate ?? ''}
                                onChange={e => setOcrEditExpiry(p => ({ ...p, [log._id]: e.target.value }))}
                                className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>

                            {/* Notes */}
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1">Admin Notes (optional)</label>
                              <input
                                type="text"
                                value={ocrEditNotes[log._id] || ''}
                                onChange={e => setOcrEditNotes(p => ({ ...p, [log._id]: e.target.value }))}
                                placeholder="Any additional notes…"
                                className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>

                            {/* Action buttons */}
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={() => handleOcrVerify(log)}
                                disabled={ocrVerifying === log._id}
                                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black transition-all disabled:opacity-50"
                              >
                                {ocrVerifying === log._id ? (
                                  <><span className="animate-spin">⏳</span> Verifying…</>
                                ) : (
                                  <><CheckCircle size={12} /> Verify &amp; Set Expiry</>
                                )}
                              </button>
                              <button
                                onClick={() => { setOcrRejectId(log._id); setOcrRejectReason(''); }}
                                disabled={ocrVerifying === log._id}
                                className="px-4 py-2.5 rounded-xl bg-red-500/15 border border-red-500/25 text-red-400 text-xs font-bold hover:bg-red-500/25 transition-all disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
                <p className="text-[11px] text-blue-300 leading-relaxed">
                  <strong>OCR Pipeline:</strong> Documents pass a pre-flight quality scan (blur, boundary, field presence) before reaching this queue.
                  The AI extracts the expiry date, certificate number, and registered name.
                  Confidence ≥ 70% routes to <strong>Auto-Verify</strong> (single-click approve). Below 70% or name mismatch routes to <strong>Manual Review</strong>.
                  Partner is notified by email on verification or rejection.
                </p>
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              LEDGER
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'ledger' && (
            <div className="space-y-5">
              <SectionHead
                title="Platform Ledger"
                sub="All-time gross volume, platform fees, and payout status across every transaction type"
                action={
                  <div className="flex items-center gap-2">
                    {/* Balance integrity pill */}
                    {balanceCheck && (
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-bold uppercase tracking-wider
                        ${balanceCheck.balanced
                          ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                          : 'bg-red-500/15 border-red-500/30 text-red-400'
                        }`}>
                        {balanceCheck.balanced
                          ? <><CheckCircle size={11} /> Ledger Balanced</>
                          : <><AlertTriangle size={11} /> Δ £{Math.abs(balanceCheck.delta).toFixed(2)} Leak</>
                        }
                      </div>
                    )}
                    {/* VAT / HMRC export */}
                    <Btn
                      size="xs"
                      variant="ghost"
                      onClick={() => adminAPI.exportVatCsv(getAuthToken(), period)}
                    >
                      <Download size={11} /> VAT Export
                    </Btn>
                  </div>
                }
              />

              {loading['ledger'] ? <Spin /> : (
                <>
                  {/* Summary KPIs */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Kpi label="Total Gross Volume"    value={fmt(ledgerAnalytics?.summary?.totalGrossVolume)}      icon={TrendingUp}  />
                    <Kpi label="Platform Fees Earned"  value={fmt(ledgerAnalytics?.summary?.totalPlatformFees)}     icon={DollarSign}  />
                    <Kpi label="Payouts Completed"     value={fmt(ledgerAnalytics?.summary?.totalPayoutsCompleted)} icon={CheckCircle} />
                    <Kpi label="Payouts Pending"       value={fmt(ledgerAnalytics?.summary?.totalPayoutsPending)}   icon={AlertTriangle} danger={ledgerAnalytics?.summary?.totalPayoutsPending > 0} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Kpi label="Total Transactions"    value={fmtN(ledgerAnalytics?.summary?.transactionCount)}    icon={Receipt}     />
                    <Kpi label="Payout Rate"
                      value={
                        ledgerAnalytics?.summary?.totalGrossVolume
                          ? fmtPct((ledgerAnalytics.summary.totalPayoutsCompleted / ledgerAnalytics.summary.totalGrossVolume) * 100)
                          : '—'
                      }
                      icon={BarChart3}
                    />
                  </div>

                  {/* Breakdown by entity type */}
                  {ledgerAnalytics?.byEntityType && Object.keys(ledgerAnalytics.byEntityType).length > 0 ? (
                    <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                        <Layers size={13} className="text-zinc-500" />
                        <span className="text-xs font-bold text-zinc-300">Revenue by Transaction Type</span>
                      </div>
                      <Table heads={['Type', 'Transactions', 'Gross Volume', 'Platform Fees', 'Net Payouts']}>
                        {Object.entries(ledgerAnalytics.byEntityType).map(([type, data], i) => (
                          <Tr key={i}>
                            <Td>
                              <span className="capitalize font-semibold text-zinc-200">{type.replace(/_/g, ' ')}</span>
                            </Td>
                            <Td muted>{fmtN(data.count)}</Td>
                            <Td><span className="font-bold tabular-nums text-blue-400">{fmt(data.grossVolume)}</span></Td>
                            <Td><span className="font-semibold tabular-nums text-emerald-400">{fmt(data.platformFees)}</span></Td>
                            <Td muted>{fmt(data.netPayouts)}</Td>
                          </Tr>
                        ))}
                      </Table>

                      {/* Totals row */}
                      <div className="px-4 py-3 border-t border-white/10 bg-white/3 flex items-center gap-4">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 flex-1">Totals</span>
                        <span className="text-[10px] font-bold tabular-nums text-blue-400 w-28 text-right">
                          {fmt(ledgerAnalytics.summary.totalGrossVolume)} GMV
                        </span>
                        <span className="text-[10px] font-bold tabular-nums text-emerald-400 w-28 text-right">
                          {fmt(ledgerAnalytics.summary.totalPlatformFees)} fees
                        </span>
                        <span className="text-[10px] text-zinc-600 w-24 text-right">
                          {fmtN(ledgerAnalytics.summary.transactionCount)} txns
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#111827] border border-white/6 rounded-lg p-8">
                      <Empty icon={BookOpen} msg="No ledger entries recorded yet" />
                    </div>
                  )}

                  {/* Fee structure reference */}
                  <div className="bg-[#111827] border border-white/6 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Database size={13} className="text-zinc-500" />
                      <span className="text-xs font-bold text-zinc-300">Platform Commission Structure</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { type: 'Short-term Booking', rate: '12% + VAT' },
                        { type: 'Food Order',          rate: '15% + VAT' },
                        { type: 'Ride / Transport',    rate: '20%'       },
                        { type: 'R2R Deal',            rate: '5%'        },
                      ].map((r, i) => (
                        <div key={i} className="bg-white/4 rounded p-3">
                          <div className="text-[10px] text-zinc-500 mb-1">{r.type}</div>
                          <div className="text-sm font-bold text-blue-400">{r.rate}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              SYSTEM CONTROLS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'system-controls' && (
            <div className="space-y-5">
              <SectionHead
                title="System Controls"
                sub="Kill-switches and platform-wide operational toggles — changes take effect immediately"
              />

              {/* Warning banner if anything is off */}
              {(activeKillSwitches.length > 0 || maintenanceActive) && (
                <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <AlertOctagon size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-400">
                      {maintenanceActive ? 'Platform is in Maintenance Mode' : `${activeKillSwitches.length} feature${activeKillSwitches.length !== 1 ? 's are' : ' is'} currently disabled`}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {maintenanceActive
                        ? 'All consumer-facing pages are unavailable. Re-enable below to restore access.'
                        : `Active kill-switches: ${activeKillSwitches.map(([k]) => k.replace(/([A-Z])/g, ' $1').trim()).join(', ')}`
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Operational kill-switches */}
              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <Zap size={13} className="text-amber-400" />
                  <span className="text-xs font-bold text-zinc-300">Operational Kill-Switches</span>
                  <span className="text-[10px] text-zinc-600 ml-auto">Toggle to immediately enable or disable platform features</span>
                </div>
                <div className="p-4 grid sm:grid-cols-2 gap-3">
                  <KillSwitch
                    label="Bookings"
                    sub="Short-term rental booking flow — guests can create and pay for bookings"
                    enabled={controls.bookingsEnabled}
                    onChange={v => handleToggleControl('bookingsEnabled', v)}
                  />
                  <KillSwitch
                    label="Food Orders"
                    sub="Restaurant ordering — customers can place food orders"
                    enabled={controls.foodOrdersEnabled}
                    onChange={v => handleToggleControl('foodOrdersEnabled', v)}
                  />
                  <KillSwitch
                    label="Delivery"
                    sub="Food-delivery rider dispatch — restaurants fulfil orders via riders"
                    enabled={controls.deliveryEnabled}
                    onChange={v => handleToggleControl('deliveryEnabled', v)}
                  />
                  <KillSwitch
                    label="Taxi / PHV Rides"
                    sub="Private-hire ride requests — disabled until TfL Operator Licence is active"
                    enabled={controls.ridesEnabled}
                    onChange={v => handleToggleControl('ridesEnabled', v)}
                  />
                  <KillSwitch
                    label="Deal Marketplace"
                    sub="R2R deal listings — hosts can browse and bid on landlord properties"
                    enabled={controls.marketplaceEnabled}
                    onChange={v => handleToggleControl('marketplaceEnabled', v)}
                  />
                  <div className="sm:col-span-2 space-y-2">
                    <KillSwitch
                      label="Guest Sign-ups"
                      sub="New guest accounts — disable during Supply-First phase to show Waitlist screen"
                      enabled={controls.guestSignupsEnabled}
                      onChange={async (v) => {
                        await handleToggleControl('guestSignupsEnabled', v);
                        // When flipping ON, prompt to send Launch Blast if there are waiting guests
                        if (v && (waitlistStats?.notYetNotified ?? 0) > 0) {
                          if (window.confirm(
                            `Send Launch Blast to ${(waitlistStats.notYetNotified).toLocaleString()} waitlisted guests?\n\n` +
                            `"We're Live! 🚀 Your London stay, ride, and food are now one tap away."`
                          )) {
                            try {
                              const result = await waitlistAPI.sendBlast(getAuthToken());
                              toast({ title: `🚀 Launch Blast sent!`, description: result.message });
                              waitlistAPI.getStats(getAuthToken()).then(d => setWaitlistStats(d)).catch(() => {});
                            } catch (e) {
                              toast({ title: 'Blast failed', description: e?.response?.data?.detail || e?.message, variant: 'destructive' });
                            }
                          }
                        }
                      }}
                    />
                    {/* Launch Blast shortcut */}
                    {!controls.guestSignupsEnabled && (waitlistStats?.notYetNotified ?? 0) > 0 && (
                      <div className="flex items-center gap-3 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-2">
                        <span className="text-[10px] text-amber-400 flex-1">
                          <span className="font-bold">{(waitlistStats?.notYetNotified).toLocaleString()}</span> guests waiting for launch notification
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(
                              `Send Launch Blast to ${(waitlistStats.notYetNotified).toLocaleString()} waitlisted guests?\n\n` +
                              `"We're Live! 🚀 Your London stay, ride, and food are now one tap away."`
                            )) return;
                            try {
                              const result = await waitlistAPI.sendBlast(getAuthToken());
                              toast({ title: `🚀 Blast sent!`, description: result.message });
                              waitlistAPI.getStats(getAuthToken()).then(d => setWaitlistStats(d)).catch(() => {});
                            } catch (e) {
                              toast({ title: 'Blast failed', description: e?.response?.data?.detail || e?.message, variant: 'destructive' });
                            }
                          }}
                          className="text-[10px] bg-amber-500 hover:bg-amber-400 text-black font-bold px-2.5 py-1 rounded-md flex items-center gap-1 flex-shrink-0 transition-colors"
                        >
                          🚀 Send Launch Blast
                        </button>
                      </div>
                    )}
                  </div>
                  <KillSwitch
                    label="Partner Sign-ups"
                    sub="Hosts, Drivers, Riders & Providers — keep ON during supply build-out"
                    enabled={controls.partnerSignupsEnabled}
                    onChange={v => handleToggleControl('partnerSignupsEnabled', v)}
                  />
                  <KillSwitch
                    label="Payouts"
                    sub="Weekly automated payouts to hosts, drivers, and restaurant partners"
                    enabled={controls.payoutsEnabled}
                    onChange={v => handleToggleControl('payoutsEnabled', v)}
                  />
                </div>
              </div>

              {/* ── Supply Liquidity Panel ─────────────────────────────────── */}
              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <Activity size={13} className="text-sky-400" />
                  <span className="text-xs font-bold text-zinc-300">Supply Liquidity</span>
                  <span className="text-[10px] text-zinc-600 ml-auto">Monitor when to open the Guest Gate</span>
                </div>
                <div className="p-4 space-y-4">
                  {/* Ratio cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                      {
                        label: 'Verified Hosts',
                        value: fmtN(platformStats?.users?.byRole?.hosts),
                        target: 50,
                        raw: platformStats?.users?.byRole?.hosts || 0,
                        color: 'text-emerald-400',
                        bg: 'bg-emerald-500/8',
                        border: 'border-emerald-500/20',
                        icon: Building2,
                      },
                      {
                        label: 'Delivery Riders',
                        value: fmtN(platformStats?.users?.byRole?.deliveryRiders),
                        target: 20,
                        raw: platformStats?.users?.byRole?.deliveryRiders || 0,
                        color: 'text-sky-400',
                        bg: 'bg-sky-500/8',
                        border: 'border-sky-500/20',
                        icon: Truck,
                      },
                      {
                        label: 'Taxi Drivers',
                        value: fmtN(platformStats?.users?.byRole?.drivers),
                        target: 20,
                        raw: platformStats?.users?.byRole?.drivers || 0,
                        color: 'text-indigo-400',
                        bg: 'bg-indigo-500/8',
                        border: 'border-indigo-500/20',
                        icon: Car,
                        note: !controls.ridesEnabled ? 'TfL licence pending' : null,
                      },
                      {
                        label: 'Restaurants',
                        value: fmtN(platformStats?.entities?.activeRestaurants),
                        target: 10,
                        raw: platformStats?.entities?.activeRestaurants || 0,
                        color: 'text-amber-400',
                        bg: 'bg-amber-500/8',
                        border: 'border-amber-500/20',
                        icon: Utensils,
                      },
                      {
                        label: 'Guest Accounts',
                        value: fmtN(platformStats?.users?.byRole?.guests),
                        target: null,
                        raw: platformStats?.users?.byRole?.guests || 0,
                        color: 'text-purple-400',
                        bg: 'bg-purple-500/8',
                        border: 'border-purple-500/20',
                        icon: Users,
                      },
                    ].map((item, i) => {
                      const pct = item.target ? Math.min(100, Math.round((item.raw / item.target) * 100)) : null;
                      return (
                        <div key={i} className={`p-3 rounded-lg border ${item.bg} ${item.border}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <item.icon size={11} className={item.color} />
                            <span className={`text-[9px] font-bold uppercase tracking-wider ${item.color}`}>{item.label}</span>
                          </div>
                          <div className={`text-xl font-bold ${item.color} tabular-nums`}>{item.value ?? '—'}</div>
                          {item.target && (
                            <>
                              <div className="text-[9px] text-zinc-600 mt-1">Target: {item.target}</div>
                              <div className="mt-1.5 w-full bg-white/5 rounded-full h-1">
                                <div
                                  className={`h-1 rounded-full transition-all ${pct >= 100 ? 'bg-emerald-400' : 'bg-amber-400'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <div className="text-[9px] text-zinc-500 mt-0.5">{pct}% of target</div>
                            </>
                          )}
                          {item.note && (
                            <div className="text-[9px] text-amber-400/80 mt-1 italic">{item.note}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Launch Ready signal */}
                  {(() => {
                    const hosts   = platformStats?.users?.byRole?.hosts || 0;
                    const riders  = platformStats?.users?.byRole?.deliveryRiders || 0;
                    // Launch threshold ignores taxi drivers — taxi launch is deferred until the TfL Operator Licence is live
                    const ready   = hosts >= 50 && riders >= 20;
                    const missingHosts  = Math.max(0, 50 - hosts);
                    const missingRiders = Math.max(0, 20 - riders);
                    const missingParts = [];
                    if (missingHosts)  missingParts.push(`${missingHosts} more hosts`);
                    if (missingRiders) missingParts.push(`${missingRiders} more delivery riders`);
                    return (
                      <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all
                        ${ready
                          ? 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-amber-500/8 border-amber-500/20'
                        }`}>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ready ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                        <div className="flex-1">
                          <p className={`text-xs font-bold ${ready ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {ready ? '🚀 Launch Ready — Open the Guest Gate!' : 'Building Supply…'}
                          </p>
                          <p className="text-[10px] text-zinc-500 mt-0.5">
                            {ready
                              ? 'Target density reached. Enable "Guest Sign-ups" above to go live.'
                              : `Need ${missingParts.join(' and ')} to hit launch threshold. Taxi is excluded until TfL Operator Licence is active.`
                            }
                          </p>
                        </div>
                        {!ready && !controls.guestSignupsEnabled && (
                          <span className="text-[9px] bg-amber-500/15 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-mono">
                            WAITLIST MODE
                          </span>
                        )}
                        {ready && !controls.guestSignupsEnabled && (
                          <span className="text-[9px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full font-mono animate-pulse">
                            OPEN GATE ↑
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Service-to-Guest ratio */}
                  {(() => {
                    const guests      = platformStats?.users?.byRole?.guests || 0;
                    const hosts       = platformStats?.users?.byRole?.hosts || 0;
                    const riders      = platformStats?.users?.byRole?.deliveryRiders || 0;
                    const taxiDrivers = platformStats?.users?.byRole?.drivers || 0;
                    // Count taxi drivers only if the taxi vertical is active
                    const supply = hosts + riders + (controls.ridesEnabled ? taxiDrivers : 0);
                    const ratio  = guests > 0 ? (supply / guests).toFixed(1) : '∞';
                    return (
                      <div className="flex items-center justify-between p-3 bg-white/3 rounded-lg border border-white/6">
                        <div>
                          <p className="text-[10px] text-zinc-500">Service-to-Guest Ratio</p>
                          <p className="text-lg font-bold text-zinc-200 tabular-nums">{ratio} : 1</p>
                          <p className="text-[9px] text-zinc-600">{supply} partners : {guests} guests — target ≥ 0.5 : 1 to open gates</p>
                        </div>
                        <div className={`text-2xl font-black ${parseFloat(ratio) >= 0.5 || ratio === '∞' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {parseFloat(ratio) >= 0.5 || ratio === '∞' ? '✓' : '↑'}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Waitlist Demand Gauge ─────────────────────────────── */}
                  {(() => {
                    const total = waitlistStats?.total ?? platformStats?.waitlist?.total ?? 0;
                    const today = waitlistStats?.today ?? platformStats?.waitlist?.todayGrowth ?? 0;
                    const threshold = 500;
                    const pct = Math.min(100, Math.round((total / threshold) * 100));
                    const signal = total >= threshold;
                    // SVG circular gauge
                    const r = 28; const circ = 2 * Math.PI * r;
                    const dash = circ * (pct / 100);
                    return (
                      <div className="bg-white/3 border border-white/6 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Users size={13} className={signal ? 'text-emerald-400' : 'text-amber-400'} />
                          <span className="text-xs font-bold text-zinc-300">Demand Signal — Guest Waitlist</span>
                          {today > 0 && (
                            <span className="ml-auto text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full">
                              +{today} today
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-5">
                          {/* Circular gauge */}
                          <div className="relative flex-shrink-0">
                            <svg width="72" height="72" viewBox="0 0 72 72">
                              <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
                              <circle
                                cx="36" cy="36" r={r} fill="none"
                                stroke={signal ? '#34d399' : '#fbbf24'}
                                strokeWidth="6"
                                strokeDasharray={`${dash} ${circ}`}
                                strokeLinecap="round"
                                transform="rotate(-90 36 36)"
                                className="transition-all duration-700"
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className={`text-sm font-black tabular-nums leading-none ${signal ? 'text-emerald-400' : 'text-amber-400'}`}>{pct}%</span>
                              <span className="text-[8px] text-zinc-600">filled</span>
                            </div>
                          </div>
                          {/* Stats */}
                          <div className="flex-1 space-y-1.5">
                            <div className="flex items-baseline justify-between">
                              <span className="text-2xl font-black text-zinc-200 tabular-nums">{total.toLocaleString()}</span>
                              <span className="text-[10px] text-zinc-600">/ {threshold.toLocaleString()} threshold</span>
                            </div>
                            <p className={`text-xs font-semibold ${signal ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {signal ? '🚀 DEMAND ALERT: 500 Guests Waiting' : `${Math.max(0, threshold - total)} more guests to trigger Launch Signal`}
                            </p>
                            {signal && (
                              <p className="text-[10px] text-zinc-500">
                                Evaluate Drivers & Hosts supply levels before flipping the Guest Kill-Switch.
                              </p>
                            )}
                            {waitlistStats?.notYetNotified > 0 && (
                              <p className="text-[10px] text-zinc-600">
                                {waitlistStats.notYetNotified.toLocaleString()} guests not yet notified — use Launch Blast above.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Waitlist Ambassador Leaderboard ──────────────────── */}
                  {waitlistStats?.topReferrers?.length > 0 && (
                    <div className="bg-white/3 border border-white/6 rounded-lg overflow-hidden">
                      <div className="px-4 py-2.5 border-b border-white/6 flex items-center gap-2">
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Waitlist Ambassadors</span>
                        <span className="text-[9px] text-zinc-600 ml-auto">Guests recruiting supply before launch</span>
                      </div>
                      <div className="divide-y divide-white/4">
                        {waitlistStats.topReferrers.slice(0, 5).map((r, i) => (
                          <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                            <span className={`text-[10px] font-black w-4 text-center ${i === 0 ? 'text-amber-400' : 'text-zinc-600'}`}>
                              {i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-zinc-300 font-mono truncate">{r.email}</p>
                              <p className="text-[9px] text-zinc-600 font-mono">{r.referralCode}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-black tabular-nums ${i === 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                                {r.referralCount}
                              </p>
                              <p className="text-[9px] text-zinc-600">referrals</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Maintenance mode — separate danger zone */}
              <div className="bg-[#111827] border border-red-500/20 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-red-500/15 flex items-center gap-2">
                  <AlertOctagon size={13} className="text-red-400" />
                  <span className="text-xs font-bold text-red-400">Danger Zone</span>
                </div>
                <div className="p-4">
                  <KillSwitch
                    label="Maintenance Mode"
                    sub="Displays a maintenance page to all non-admin users across the entire platform"
                    enabled={controls.maintenanceMode}
                    onChange={v => handleToggleControl('maintenanceMode', v)}
                    danger={!controls.maintenanceMode}
                  />
                </div>
              </div>

              {/* Platform health grid */}
              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <Activity size={13} className="text-emerald-400" />
                  <span className="text-xs font-bold text-zinc-300">Platform Health</span>
                  <span className="text-[10px] text-zinc-600 ml-auto">Indicative only — verify via monitoring dashboard</span>
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'API Server',        ok: true,                        detail: 'Response time normal' },
                    { label: 'MongoDB Atlas',      ok: true,                        detail: 'All clusters healthy' },
                    { label: 'Stripe Payments',    ok: controls.payoutsEnabled,     detail: controls.payoutsEnabled ? 'Webhooks active' : 'Payouts disabled' },
                    { label: 'File Storage',       ok: true,                        detail: 'CDN operational' },
                    { label: 'Booking Flow',      ok: controls.bookingsEnabled,       detail: controls.bookingsEnabled ? 'Accepting bookings' : 'Kill-switch active' },
                    { label: 'Food Orders',       ok: controls.foodOrdersEnabled,     detail: controls.foodOrdersEnabled ? 'Accepting orders' : 'Kill-switch active' },
                    { label: 'Ride Requests',     ok: controls.ridesEnabled,          detail: controls.ridesEnabled ? 'Dispatching drivers' : 'Kill-switch active' },
                    { label: 'Guest Sign-ups',    ok: controls.guestSignupsEnabled,   detail: controls.guestSignupsEnabled ? 'Guest gate open' : 'Waitlist mode' },
                    { label: 'Partner Sign-ups',  ok: controls.partnerSignupsEnabled, detail: controls.partnerSignupsEnabled ? 'Partners can register' : 'Kill-switch active' },
                  ].map((h, i) => (
                    <div key={i} className={`p-3 rounded-lg border
                      ${h.ok ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-red-500/10 border-red-500/25'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${h.ok ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                        <span className={`text-[10px] font-bold ${h.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                          {h.ok ? 'OK' : 'DOWN'}
                        </span>
                      </div>
                      <div className="text-[11px] font-semibold text-zinc-300">{h.label}</div>
                      <div className="text-[9px] text-zinc-600 mt-0.5">{h.detail}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              ADMIN ALERTS
              Fed by referral milestone crossings (250 / 500), and the
              Stripe webhook stubs (payout.failed, dispute, account.updated).
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'alerts' && (
            <div className="space-y-4">
              <SectionHead
                title="Alerts"
                sub={`${adminAlerts.unresolvedCount} unresolved · referral milestones, payout failures, disputes`}
              />

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <BellRing size={12} className="text-amber-400" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Needs attention</span>
                </div>
                {loading['alerts'] ? <Spin /> : (
                  <div className="divide-y divide-white/4 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {adminAlerts.alerts.length === 0 && (
                      <Empty icon={CheckCircle} msg="No unresolved alerts. You're all caught up." />
                    )}
                    {adminAlerts.alerts.map((a) => {
                      const sev = a.severity || 'info';
                      const sevColor = sev === 'warning' ? 'text-amber-400 bg-amber-500/15' :
                                       sev === 'error'   ? 'text-red-400 bg-red-500/15' :
                                                           'text-blue-400 bg-blue-500/15';
                      return (
                        <div key={a._id} className="px-4 py-3 hover:bg-white/3 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${sevColor}`}>
                              <BellRing size={11} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-zinc-200">{a.title || a.type}</p>
                              <p className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">{a.message}</p>
                              {a.data && Object.keys(a.data).length > 0 && (
                                <pre className="mt-2 text-[10px] text-zinc-500 font-mono whitespace-pre-wrap">
                                  {JSON.stringify(a.data, null, 2)}
                                </pre>
                              )}
                              <p className="text-[10px] text-zinc-600 mt-1">
                                {fmtDate(a.createdAt)} {fmtTime(a.createdAt)} · type: <span className="font-mono">{a.type}</span>
                              </p>
                            </div>
                            <button
                              onClick={async () => {
                                const token = getAuthToken();
                                try {
                                  await adminAPI.resolveAlert(a._id, token);
                                  const fresh = await adminAPI.getAlerts(token, false);
                                  setAdminAlerts(fresh || { alerts: [], unresolvedCount: 0 });
                                } catch (e) {
                                  console.error('resolve alert', e);
                                }
                              }}
                              className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 flex-shrink-0"
                            >
                              Mark resolved
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
              ACTIVITY LOG
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {section === 'activity' && (
            <div className="space-y-4">
              <SectionHead title="Activity Log" sub="Real-time platform-wide event feed — last 100 events" />

              <div className="bg-[#111827] border border-white/6 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Live Feed</span>
                </div>
                {loading['activity'] ? <Spin /> : (
                  <div className="divide-y divide-white/4 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {recentActivity.length === 0 && <Empty icon={Activity} msg="No activity recorded yet" />}
                    {recentActivity.map((ev, i) => (
                      <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-white/3 transition-colors">
                        <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5
                          ${ev.type === 'user_signup' ? 'bg-blue-500/15' :
                            ev.type === 'booking'     ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
                          {ev.type === 'user_signup'
                            ? <Users size={11} className="text-blue-400" />
                            : ev.type === 'booking'
                              ? <CalendarDays size={11} className="text-emerald-400" />
                              : <Handshake size={11} className="text-amber-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-zinc-300">{ev.title}</p>
                          <p className="text-[10px] text-zinc-600 mt-0.5">{ev.detail}</p>
                        </div>
                        <div className="text-[10px] text-zinc-700 flex-shrink-0 text-right">
                          <div>{fmtDate(ev.timestamp)}</div>
                          <div className="font-mono">{fmtTime(ev.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ══════════════════════════════════════════════════
          PAYOUT PRE-FLIGHT MODAL
      ══════════════════════════════════════════════════ */}
      {showPreflightModal && preflightData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0D1323] border border-white/10 rounded-xl w-full max-w-lg shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
              <div className="flex items-center gap-2.5">
                <FlaskConical size={16} className={preflightData.clear ? 'text-emerald-400' : 'text-amber-400'} />
                <span className="text-sm font-bold text-white">Payout Pre-Flight Check</span>
              </div>
              <button onClick={() => setShowPreflightModal(false)} className="text-zinc-600 hover:text-zinc-300">
                <X size={16} />
              </button>
            </div>

            {/* Summary */}
            <div className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#111827] rounded-lg p-3 text-center">
                  <div className="text-lg font-bold tabular-nums text-blue-400">{fmt(preflightData.totalScheduledAmount)}</div>
                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold mt-0.5">Total Scheduled</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${preflightData.totalHeldAmount > 0 ? 'bg-amber-500/10' : 'bg-[#111827]'}`}>
                  <div className={`text-lg font-bold tabular-nums ${preflightData.totalHeldAmount > 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                    {fmt(preflightData.totalHeldAmount)}
                  </div>
                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold mt-0.5">Flagged / Hold</div>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold tabular-nums text-emerald-400">{fmt(preflightData.safeToPayAmount)}</div>
                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-bold mt-0.5">Safe to Pay</div>
                </div>
              </div>

              {preflightData.clear ? (
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                  <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                  <span className="text-xs text-emerald-300 font-semibold">All clear — no flagged users. Safe to proceed with payout.</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-amber-300">
                        {preflightData.flaggedCount} user{preflightData.flaggedCount !== 1 ? 's' : ''} flagged for manual review
                      </p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">These users have open disputes, high risk scores, or suspended accounts.</p>
                    </div>
                  </div>

                  {/* Flagged user list */}
                  <div className="max-h-48 overflow-y-auto space-y-1.5">
                    {preflightData.flaggedUsers.map((u, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#111827] rounded-lg px-3 py-2">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          u.riskScore >= 60 ? 'bg-red-400' : u.openDisputes > 0 ? 'bg-amber-400' : 'bg-zinc-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-semibold text-zinc-200 truncate">{u.name}</div>
                          <div className="text-[10px] text-zinc-600 font-mono truncate">{u.email}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-[11px] font-bold tabular-nums text-amber-400">{fmt(u.pendingPayoutAmount)}</div>
                          <div className="text-[9px] text-zinc-700">
                            {u.reasons?.map(r => r.replace(/_/g, ' ')).join(' · ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-5 py-4 border-t border-white/8 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setShowPreflightModal(false)}>Cancel</Btn>
              {preflightData.clear ? (
                <Btn variant="success" onClick={handleConfirmPayout} disabled={triggeringPayout}>
                  {triggeringPayout ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                  Trigger Payout
                </Btn>
              ) : (
                <div className="flex gap-2">
                  <Btn variant="ghost" onClick={handleConfirmPayout} disabled={triggeringPayout}>
                    {triggeringPayout ? <Loader2 size={12} className="animate-spin" /> : null}
                    Pay Safe Users Only
                  </Btn>
                  <Btn variant="danger" onClick={handleConfirmPayout} disabled={triggeringPayout}>
                    {triggeringPayout ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                    Pay All Anyway
                  </Btn>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          GRANT INCENTIVE MODAL
      ══════════════════════════════════════════════════ */}
      {showIncentiveModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1323] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift size={16} className="text-emerald-400" />
                <span className="text-sm font-bold text-white">Grant Incentive</span>
              </div>
              <button onClick={() => setShowIncentiveModal(false)} className="text-zinc-600 hover:text-zinc-300">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {/* User Ref / ID lookup */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">
                  User Ref ID
                  <span className="ml-1 font-normal normal-case text-zinc-600">— ask the user for their BC-XXXXX ref, or paste their user ID</span>
                </label>
                <input
                  value={newIncentive.userId}
                  onChange={e => setNewIncentive(p => ({ ...p, userId: e.target.value }))}
                  placeholder="e.g. BC-A7X3K or paste user ID"
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                />
                <p className="text-[9px] text-zinc-600 mt-1">
                  Find a user's Ref ID in the Users tab — it's displayed as a gold tag next to their name.
                </p>
              </div>
              {/* Type */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Incentive Type</label>
                <select
                  value={newIncentive.incentiveType}
                  onChange={e => setNewIncentive(p => ({ ...p, incentiveType: e.target.value }))}
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                >
                  <option value="commission_waiver">Commission Waiver</option>
                  <option value="referral_bonus">Referral Bonus</option>
                  <option value="ambassador">Ambassador</option>
                </select>
              </div>
              {/* Role scope */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Role Scope</label>
                <select
                  value={newIncentive.roleScope}
                  onChange={e => setNewIncentive(p => ({ ...p, roleScope: e.target.value }))}
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                >
                  {['all', 'host', 'landlord', 'rider', 'driver', 'provider'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              {/* Discount % and max uses */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Discount %</label>
                  <input
                    type="number" min="0" max="100"
                    value={newIncentive.discountPct}
                    onChange={e => setNewIncentive(p => ({ ...p, discountPct: e.target.value }))}
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Max Uses (blank = ∞)</label>
                  <input
                    type="number" min="1"
                    value={newIncentive.maxUses}
                    onChange={e => setNewIncentive(p => ({ ...p, maxUses: e.target.value }))}
                    placeholder="e.g. 10"
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                  />
                </div>
              </div>
              {/* Notes */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Notes</label>
                <textarea
                  value={newIncentive.notes}
                  onChange={e => setNewIncentive(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  placeholder="e.g. Beta launch — first 3 months commission free"
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68] resize-none"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/8 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setShowIncentiveModal(false)}>Cancel</Btn>
              <Btn variant="success" onClick={handleCreateIncentive} disabled={savingIncentive || !newIncentive.userId}>
                {savingIncentive ? <Loader2 size={12} className="animate-spin" /> : <Gift size={12} />}
                Grant Incentive
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          MINT REFERRAL CODE MODAL
      ══════════════════════════════════════════════════ */}
      {showMintModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1323] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-blue-400" />
                <span className="text-sm font-bold text-white">Mint Referral Code</span>
              </div>
              <button onClick={() => setShowMintModal(false)} className="text-zinc-600 hover:text-zinc-300">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Code (blank = auto-generate)</label>
                <input
                  value={newCode.code}
                  onChange={e => setNewCode(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. LAUNCH50"
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Referrer Reward (£)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={newCode.referrerRewardGbp}
                    onChange={e => setNewCode(p => ({ ...p, referrerRewardGbp: e.target.value }))}
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">New User Reward (£)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={newCode.newUserRewardGbp}
                    onChange={e => setNewCode(p => ({ ...p, newUserRewardGbp: e.target.value }))}
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Max Uses (blank = ∞)</label>
                  <input
                    type="number" min="1"
                    value={newCode.maxUses}
                    onChange={e => setNewCode(p => ({ ...p, maxUses: e.target.value }))}
                    placeholder="e.g. 100"
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Global Cap £ (blank = no cap)</label>
                  <input
                    type="number" min="0" step="1"
                    value={newCode.globalCapGbp}
                    onChange={e => setNewCode(p => ({ ...p, globalCapGbp: e.target.value }))}
                    placeholder="e.g. 1000"
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Notes</label>
                <input
                  value={newCode.notes}
                  onChange={e => setNewCode(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Launch campaign — Host recruitment"
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#1F3A68]"
                />
              </div>
              {(newCode.referrerRewardGbp > 0 || newCode.newUserRewardGbp > 0) && (
                <div className="bg-blue-500/8 border border-blue-500/20 rounded-lg p-3">
                  <div className="text-[10px] text-zinc-500 mb-1 font-bold uppercase tracking-widest">Reward Summary</div>
                  <div className="text-xs text-zinc-300">
                    Each use pays <span className="text-emerald-400 font-bold">£{parseFloat(newCode.referrerRewardGbp || 0).toFixed(2)}</span> to the referrer
                    and <span className="text-blue-400 font-bold">£{parseFloat(newCode.newUserRewardGbp || 0).toFixed(2)}</span> to the new user.
                    {newCode.globalCapGbp && <span className="text-amber-400"> Total spend capped at £{parseFloat(newCode.globalCapGbp).toFixed(0)}.</span>}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-white/8 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setShowMintModal(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={handleMintCode} disabled={mintingCode}>
                {mintingCode ? <Loader2 size={12} className="animate-spin" /> : <Tag size={12} />}
                Mint Code
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          VOID PAYOUT MODAL
      ══════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════
          SUSPEND ENTITY MODAL
      ══════════════════════════════════════════════════ */}
      {showSuspendModal && suspendTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1323] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ban size={16} className={suspendTarget.suspended ? 'text-emerald-400' : 'text-red-400'} />
                <span className="text-sm font-bold text-white">
                  {suspendTarget.suspended ? 'Reactivate Entity' : 'Suspend Entity'}
                </span>
              </div>
              <button onClick={() => { setShowSuspendModal(false); setSuspendTarget(null); setSuspendReason(''); setSuspendNote(''); }} className="text-zinc-600 hover:text-zinc-300">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-white/5 border border-white/8 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Entity</span>
                  <span className="text-zinc-300 font-semibold">{suspendTarget.name || suspendTarget.restaurantName || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Type</span>
                  <span className="text-zinc-300 capitalize">{suspendTarget.entityType}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Email</span>
                  <span className="text-zinc-300">{suspendTarget.email || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Current Status</span>
                  <Badge status={suspendTarget.suspended ? 'suspended' : 'active'} />
                </div>
              </div>

              {!suspendTarget.suspended && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Reason *</label>
                  <select
                    value={suspendReason}
                    onChange={e => setSuspendReason(e.target.value)}
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-red-500"
                  >
                    <option value="">— Select reason —</option>
                    <option value="safety_violation">Safety Violation</option>
                    <option value="expired_license">Expired Licence / Document</option>
                    <option value="fraudulent_activity">Fraudulent Activity</option>
                    <option value="fsa_failure">FSA / Food Hygiene Failure</option>
                    <option value="policy_violation">Policy Violation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Notes (optional)</label>
                <textarea
                  rows={2}
                  value={suspendNote}
                  onChange={e => setSuspendNote(e.target.value)}
                  placeholder="Internal context for audit log…"
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </div>

              {!suspendTarget.suspended && (
                <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-[11px] text-amber-400">
                    An automated suspension email will be sent and an internal note added. Active trips/orders will be allowed to complete before full lockout.
                  </p>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-white/8 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => { setShowSuspendModal(false); setSuspendTarget(null); setSuspendReason(''); setSuspendNote(''); }}>Cancel</Btn>
              <Btn
                variant={suspendTarget.suspended ? 'success' : 'danger'}
                onClick={handleSuspendEntity}
                disabled={executingSuspend || (!suspendTarget.suspended && !suspendReason)}
              >
                {executingSuspend ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                {suspendTarget.suspended ? 'Reactivate' : 'Suspend'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          APPEAL DECISION MODAL
      ══════════════════════════════════════════════════ */}
      {showDecisionModal && decisionTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1323] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gavel size={16} className="text-amber-400" />
                <span className="text-sm font-bold text-white">
                  {decisionType === 'approve' && 'Approve Appeal'}
                  {decisionType === 'request_info' && 'Request More Information'}
                  {decisionType === 'permanent_ban' && 'Permanently Ban'}
                </span>
              </div>
              <button onClick={() => { setShowDecisionModal(false); setDecisionTarget(null); setDecisionType(''); setDecisionExtra(''); }} className="text-zinc-600 hover:text-zinc-300">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-white/5 border border-white/8 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Appellant</span>
                  <span className="text-zinc-300 font-semibold">{decisionTarget.userName}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Suspended for</span>
                  <span className="text-zinc-400">{decisionTarget.suspensionReason || '—'}</span>
                </div>
                {decisionTarget.suspensionCount > 1 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">History</span>
                    <span className="text-red-400 font-bold">{decisionTarget.suspensionCount}× previously suspended</span>
                  </div>
                )}
              </div>

              {decisionType === 'approve' && (
                <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-lg p-3">
                  <p className="text-[11px] text-emerald-400">
                    Account will be reactivated immediately. A "Welcome Back" email will be sent and the appeal ticket closed.
                    This action is logged in the audit trail.
                  </p>
                </div>
              )}

              {decisionType === 'request_info' && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">What information is required? *</label>
                  <textarea
                    rows={3}
                    value={decisionExtra}
                    onChange={e => setDecisionExtra(e.target.value)}
                    placeholder="e.g. Your insurance certificate image was too blurry. Please re-upload a clear scan."
                    className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-amber-500"
                  />
                </div>
              )}

              {decisionType === 'permanent_ban' && (
                <>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Ban Reason *</label>
                    <textarea
                      rows={2}
                      value={decisionExtra}
                      onChange={e => setDecisionExtra(e.target.value)}
                      placeholder="e.g. Repeated FSA violations — 3rd offence within 6 months."
                      className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                  <div className="bg-red-500/8 border border-red-500/20 rounded-lg p-3">
                    <p className="text-[11px] text-red-400">
                      ⚠ This is irreversible. The user will be permanently banned. Future signup attempts with the same email,
                      VAT number, or licence ID will be flagged. This action is logged in the compliance audit trail.
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-4 border-t border-white/8 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => { setShowDecisionModal(false); setDecisionTarget(null); setDecisionType(''); setDecisionExtra(''); }}>Cancel</Btn>
              <Btn
                variant={decisionType === 'approve' ? 'success' : decisionType === 'permanent_ban' ? 'danger' : 'primary'}
                onClick={handleDecideAppeal}
                disabled={!!decidingAppeal || (decisionType !== 'approve' && !decisionExtra.trim())}
              >
                {decidingAppeal ? <Loader2 size={12} className="animate-spin" /> : <Gavel size={12} />}
                {decisionType === 'approve' && 'Approve & Reactivate'}
                {decisionType === 'request_info' && 'Send Request'}
                {decisionType === 'permanent_ban' && 'Confirm Permanent Ban'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {showVoidModal && voidTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1323] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-red-400" />
                <span className="text-sm font-bold text-white">Void Referral Payout</span>
              </div>
              <button onClick={() => { setShowVoidModal(false); setVoidTarget(null); setVoidReason(''); setVoidNotes(''); }} className="text-zinc-600 hover:text-zinc-300">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {/* Payout summary */}
              <div className="bg-red-500/8 border border-red-500/20 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Payout ID</span>
                  <span className="text-zinc-300 font-mono">{voidTarget._id?.slice(0, 12)}…</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Referrer</span>
                  <span className="text-zinc-300">{voidTarget.referrerName || voidTarget.referrerId?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">New User</span>
                  <span className="text-zinc-300">{voidTarget.newUserName || voidTarget.newUserId?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Amount</span>
                  <span className="text-red-400 font-bold">£{((voidTarget.payoutAmountPence || 0) / 100).toFixed(2)}</span>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Void Reason *</label>
                <select
                  value={voidReason}
                  onChange={e => setVoidReason(e.target.value)}
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-red-500"
                >
                  <option value="">— Select reason —</option>
                  <option value="self_referral">Self-Referral Detected</option>
                  <option value="circular_referral">Circular Referral (A→B→A)</option>
                  <option value="ip_match">IP / Device Match</option>
                  <option value="invalid_transaction">Invalid Transaction</option>
                  <option value="micro_stay">Micro-Stay Gaming</option>
                  <option value="junk_network">Junk Account Network</option>
                  <option value="policy_violation">General Policy Violation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Internal Notes (optional)</label>
                <textarea
                  rows={3}
                  value={voidNotes}
                  onChange={e => setVoidNotes(e.target.value)}
                  placeholder="Add context for the audit trail…"
                  className="w-full bg-white/6 border border-white/10 text-zinc-200 rounded-lg px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </div>

              {/* Warning */}
              <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg p-3">
                <p className="text-[11px] text-amber-400">
                  ⚠ This will <strong>silently</strong> void the payout, add an Internal Note to the new user's profile, and increment their risk score by +10. No email will be sent to either party.
                </p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/8 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => { setShowVoidModal(false); setVoidTarget(null); setVoidReason(''); setVoidNotes(''); }}>
                Cancel
              </Btn>
              <Btn
                variant="danger"
                onClick={() => handleVoidPayout(voidTarget._id, voidReason, voidNotes)}
                disabled={voidingPayout || !voidReason}
              >
                {voidingPayout ? <Loader2 size={12} className="animate-spin" /> : <AlertCircle size={12} />}
                Confirm Void
              </Btn>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
